import React, { useState, useEffect, useRef } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import ReactMarkdown from "react-markdown"
import {
  Shield, FileText, Layers, Info, Image, Settings,
  CheckCircle2, AlertTriangle, XCircle, RotateCw,
  Sparkles, Plus, X, Send, Check, ChevronLeft, ChevronRight, ChevronDown, Trash2, CornerDownRight,
  Eye, Bold, Italic, Link2, List, Heading2, Copy, Download,
} from "lucide-react"
import SEO from "../../components/Common/SEO"
import IarcCertificateTab, { IarcAnswersSummary } from "./IarcCertificateTab"
import iarcSchema from "./data/iarcQuestionnaire.json"
import PlatformIcon from "../../components/Common/PlatformIcon"
import { storeValidatorApi, STORE_VALIDATOR_FIELD_ENDPOINTS } from "../../services/storeValidatorApi"
import { useAnalytics, ANALYTICS_EVENTS } from "../../analytics"

// Inverse of buildIarcAnswersPayload (in the component below) — the backend stores a single
// response_id string for a "radio" question and a list for "checkbox", but our own UI state
// always wraps the current selection in an array regardless of type. Shared by both
// buildFormPatchFromRecord (restoring a saved record) and applyGeneratedFields (applying a
// fresh /generate/complete/ response), so a raw object from either source converts the same way.
const parseIarcAnswers = raw => {
  if (!raw || typeof raw !== "object") return null
  const restored = {}
  Object.entries(raw).forEach(([qid, val]) => {
    if (!iarcSchema.questions[qid]) return
    restored[qid] = Array.isArray(val) ? val : [val]
  })
  return Object.keys(restored).length ? restored : null
}

// Store Validator only supports Meta Quest / Meta Horizon Store today — no platform selector in the UI.
const PLATFORM = "meta"

// Meta's own org-level developer dashboard — not a per-app deep link (there's no confirmed URL
// pattern for one), just the entry point to it. Requires the user to already be logged in with
// access to the right organization; otherwise it bounces them to Meta's own login first.
const META_DASHBOARD_URL = "https://developers.meta.com/vr/manage/"

// Everything in this file is inline-styled, so real breakpoint behavior (stacking the
// two-column layouts, letting the app list scroll instead of clipping, etc.) needs actual
// CSS media queries — injected once per render branch via this shared block of classes.
export const RESPONSIVE_CSS = `
  @media (max-width: 960px) {
    .sv-main-columns { flex-direction: column; }
    .sv-main-columns .sv-form-col { flex: none !important; width: 100% !important; border-right: none !important; border-bottom: 1px solid #2E3244; }
    .sv-main-columns .sv-panel-col { flex: none !important; width: 100% !important; }
    .sv-preview-columns { flex-direction: column; }
    .sv-preview-columns .sv-preview-main { flex: none !important; width: 100% !important; }
    .sv-preview-columns .sv-preview-sidebar { flex: none !important; width: 100% !important; min-width: 0 !important; position: static !important; top: auto !important; }
    .sv-detail-row { flex-direction: column !important; gap: 4px !important; padding-bottom: 10px !important; }
    .sv-detail-row .sv-detail-label { width: auto !important; font-weight: 600; }
  }
  @media (max-width: 640px) {
    .sv-app-list-grid { min-width: 640px; }
    .sv-top-bar { flex-wrap: wrap; row-gap: 8px; }
  }
  .sv-markdown-preview p { margin: 0 0 10px; }
  .sv-markdown-preview p:last-child { margin-bottom: 0; }
  .sv-markdown-preview strong { color: #fff; }
  .sv-markdown-preview a { color: #F6255A; }
  .sv-markdown-preview img { max-width: 100%; border-radius: 6px; margin: 6px 0; }
  .sv-markdown-preview ul, .sv-markdown-preview ol { margin: 0 0 10px; padding-left: 20px; }
  .sv-markdown-preview h1, .sv-markdown-preview h2, .sv-markdown-preview h3 { color: #fff; margin: 0 0 8px; }
  @keyframes sv-skeleton-pulse { 0%, 100% { opacity: 0.55; } 50% { opacity: 1; } }
  .sv-skeleton { background: #2E3244; border-radius: 6px; animation: sv-skeleton-pulse 1.4s ease-in-out infinite; }
  @keyframes sv-progress-indeterminate { 0% { transform: translateX(-60%); } 100% { transform: translateX(160%); } }
`

// A plain shimmering block standing in for not-yet-loaded content — same shape/sizing props as
// a normal element, just rendered as a pulsing placeholder instead. Shared by the app list, the
// editor (while openApp's history fetch is in flight), and the standalone Preview page.
export const Skeleton = ({ width = "100%", height = "14px", radius = "6px", style = {} }) => (
  <div className="sv-skeleton" style={{ width, height, borderRadius: radius, ...style }} />
)

// Shared custom-dropdown styling — module-level (not component-scoped) since it's pure/
// stateless, and needed by the "landing" step's early-return branch which runs before
// reaching any consts defined later in the component body.
const dropdownPanelStyle = {
  position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, zIndex: 31,
  background: "#21222D", border: "1px solid #2E3244", borderRadius: "10px", padding: "6px",
  boxShadow: "0 16px 40px rgba(0,0,0,0.5)",
}
const dropdownOptionStyle = on => ({
  display: "flex", alignItems: "center", justifyContent: "space-between", gap: "9px", width: "100%",
  textAlign: "left", background: on ? "#F6255A14" : "transparent", border: "none", borderRadius: "7px",
  padding: "9px 10px", fontSize: "13px", color: on ? "#fff" : "#d1d5db", cursor: "pointer",
})
const hoverIn  = (e, on) => { if (!on) e.currentTarget.style.background = "#ffffff08" }
const hoverOut = (e, on) => { e.currentTarget.style.background = on ? "#F6255A14" : "transparent" }

const timeAgo = iso => {
  if (!iso) return "—"
  // The backend's timestamps have no explicit UTC offset (e.g. "2026-10-12T10:00:00" or
  // "2026-10-12 10:00:00") — most JS engines then parse a bare date-time string as *local*
  // time instead of UTC, which skews every "ago" by exactly the viewer's own UTC offset. That's
  // why a brand-new app (created via createNewApp -> saveDraft -> applyRecordInsights ->
  // applyDraftToRow, all within the same second) could show e.g. "5h ago" immediately — IST is
  // UTC+5:30, so a UTC timestamp misread as local time looks 5.5 hours further in the past than
  // it really is. Normalize to a real UTC instant before parsing if no offset is already present.
  const normalized = typeof iso === "string" && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(iso)
    ? iso.replace(" ", "T") + "Z"
    : iso
  const diffMs = Math.max(0, Date.now() - new Date(normalized).getTime())
  if (Number.isNaN(diffMs)) return "—"
  const mins = Math.floor(diffMs / 60000)
  if (mins < 1) return "just now"
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return `${Math.floor(days / 7)}w ago`
}

// Confirmed shape (GET /apps/, 2026-08-20): { app_id, platform, last_activity, run_count }.
// No title/category/score/status in this response today — ask backend to add those to
// avoid an extra per-row history fetch just to show a name. Falls back gracefully until then.
const mapApiApp = a => ({
  id: a.app_id || a.id,
  name: a.title || a.name || "Untitled",
  category: a.category || "",
  // Confirmed shape (GET /apps/, 2026-09-08): `rating` is 0–10 like every other score in
  // this API, so it needs the same ×10 rescale the rest of the app already uses. `overall_score`
  // (confirmed live 2026-09-17, e.g. 10.0) is the same 0–10 scale too.
  score: a.rating != null ? Math.round(a.rating * 10)
    : a.overall_score != null ? Math.round(a.overall_score * 10)
    : (a.score ?? null),
  status: mapApiStatus(a.status),
  updatedAt: (a.last_updated || a.last_activity || a.updated_at || a.updatedAt)
    ? timeAgo(a.last_updated || a.last_activity || a.updated_at || a.updatedAt) : "—",
  platform: a.platform || PLATFORM,
})

// Confirmed live (2026-09-17): GET /apps/ reports this state back as "ready_to_publish" —
// submitForReview now sends that same value too (2026-10: the backend started rejecting
// "in_review"), but "in_review" is kept mapped for any older saved record that still has it.
const API_STATUS_TO_UI = { draft: "Draft", in_review: "Ready To Publish", ready_to_publish: "Ready To Publish", submitted: "Submitted", published: "Submitted" }
const mapApiStatus = s => (s ? API_STATUS_TO_UI[s] || (s.charAt(0).toUpperCase() + s.slice(1)) : "Draft")

// Confirmed shape (POST /draft/, 2026-08-20): echoes back the full saved record —
// { app_id, user_id, created_at, platform, status, title, updated_at, ...whatever else was sent }.
// Use it to keep a list row in sync with what the backend actually stored.
const applyDraftToRow = (row, draft) => ({
  ...row,
  name: draft?.title || row.name,
  category: draft?.category || row.category,
  status: mapApiStatus(draft?.status) || row.status,
  updatedAt: draft?.updated_at ? timeAgo(draft.updated_at) : "just now",
})

// Confirmed shape (validate/title/ + correct/title/, 2026-08-20):
// { section, score /* 0–10 */, verdict: "low"|"medium"|"high", reason, suggestions: [] }
// UI scores/thresholds are all 0–100, so the raw 0–10 score is rescaled ×10 here —
// the rest of the app never needs to know about the 0–10 scale.
const VERDICT_TO_STATUS = { low: "fail", medium: "warning", moderate: "warning", high: "pass" }

// GET /platforms/ returns raw ids (e.g. "meta_horizon") — shown with proper names/casing
// for known platforms, and a generic snake_case-to-Title-Case fallback for anything else.
const PLATFORM_LABELS = {
  meta: "Meta",
  steam: "Steam",
  playstation_vr: "PlayStation VR",
  pico: "Pico",
  app_store: "App Store",
  google_play: "Google Play",
}
const platformLabel = id => PLATFORM_LABELS[id] || String(id).split(/[_-]/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")

// The group-validate structural checks (player_modes, developer, etc.) return terse,
// templated reasons that quote the raw snake_case field name — redundant since the UI
// already shows that field's own label right above it, and it reads as unpolished/robotic.
// Rewrite the known templates into plain language; anything else passes through untouched.
const humanizeReason = reason => {
  if (typeof reason !== "string") return reason
  if (/^all selected options for '.+' are valid\.?$/i.test(reason)) return "All selected options are valid."
  if (/^'.+' provided\.?$/i.test(reason)) return "This field is filled in correctly."
  if (/^'.+' is required for this platform but wasn't provided\.?$/i.test(reason)) return "This field is required but hasn't been filled in yet."
  return reason
}

// Turns one asset "check" (from the assets validate response's per-asset `details[]`) into
// a plain-language message for that specific field, instead of the raw "asset: check_name -
// detail" format the backend uses internally.
const humanizeAssetCheck = (check, fieldLabel) => {
  if (check?.name === "present") return `${fieldLabel} hasn't been uploaded yet.`
  if (check?.name === "count") {
    const m = check.detail?.match(/(\d+)\s*provided\s*\(need\s*(\d+)-(\d+)\)/i)
    if (m) return `You've added ${m[1]} — at least ${m[2]} are required (up to ${m[3]}).`
  }
  return check?.detail || "This asset needs attention."
}

const normalizeValidateResponse = data => {
  const rawScore = data?.score ?? data?.rating
  if (rawScore == null) return null
  const score = Math.round(rawScore * 10)
  const status = VERDICT_TO_STATUS[data?.verdict] || (score >= 70 ? "pass" : score >= 45 ? "warning" : "fail")
  let feedback = humanizeReason(data?.reason) || data?.feedback || data?.message || ""
  if (Array.isArray(data?.suggestions) && data.suggestions.length) {
    feedback += "\n\n" + data.suggestions.map(s => `• ${s}`).join("\n")
  }
  return { score, status, feedback }
}

// predicted_rating / audience_fit / content_audience_fit (2026-10 feature, nested alongside
// score/verdict/etc in any real sections.iarc_questionnaire object) — pulled out separately
// from normalizeValidateResponse above since they don't fit its shared {score, status, feedback}
// shape, used by the dedicated validate endpoint, generate/complete's flattened
// groups.content_rating.sections, and a saved record's own sections (/history/) alike. Returns
// null (not an object of nulls) when none of the three are present, so callers can tell "this
// response just doesn't have the new fields yet" from "insights exist but are all empty".
export const extractIarcInsights = section => {
  if (!section || typeof section !== "object") return null
  const { predicted_rating, audience_fit, content_audience_fit } = section
  if (!predicted_rating && !audience_fit && !content_audience_fit) return null
  return { predictedRating: predicted_rating || null, audienceFit: audience_fit || null, contentAudienceFit: content_audience_fit || null }
}

const normalizeCorrectResponse = (data, field) => {
  let raw = data?.corrected_text ?? data?.corrected ?? data?.text ?? data?.result ?? data?.suggestion
  if (raw === undefined || raw === null) return null
  if (typeof raw === "string") raw = raw.trim()
  if (field.type === "tags" || field.type === "checkboxes") return String(raw).split(",").map(s => s.trim()).filter(Boolean)
  return raw
}

// Small presentational pieces for the store preview page — defined at module scope (not
// inside the component body) so they keep a stable identity across renders.
const appStatusColor = status =>
  status === "Submitted" ? "#22c55e" : status === "Ready To Publish" ? "#f59e0b" : "#6b7280"

const getScoreColor = s => s >= 80 ? "#22c55e" : s >= 60 ? "#f59e0b" : s >= 40 ? "#f97316" : "#ef4444"
const getScoreLabel = s => s >= 80 ? "Excellent" : s >= 60 ? "Good" : s >= 40 ? "Needs Work" : "Critical"

// Shared across Primary/Additional genre 1/2 — same list everywhere, each slot must pick
// a distinct value (enforced in renderCustomSelect below).
const GENRE_OPTIONS = ["Action", "Adventure", "Education", "Hangout", "Narrative", "Party game", "Puzzle", "Rhythm", "Role playing", "Sandbox", "Simulation", "Sports", "Strategy", "Survival", "Tabletop", "World creation"]
const GENRE_FIELD_IDS = ["primaryGenre", "additionalGenre1", "additionalGenre2"]

const SECTIONS_CONFIG = [
  {
    id: "name", label: "Name", icon: FileText,
    description: "Your app name and descriptions are the first thing users see. Make them clear, specific, and compelling.",
    fields: [
      { id: "appName",          label: "App name",          type: "text",     maxLength: 40,   required: true,  hint: "The primary name users see across Meta Quest." },
      { id: "shortDescription", label: "Short description", type: "textarea", maxLength: 500,  rows: 3, required: true,  hint: "Shown throughout the platform, primarily in VR." },
      { id: "longDescription",  label: "Long description",  type: "markdown", maxLength: 1500, rows: 7, required: true,  hint: "Full listing description shown on the store page. Supports Markdown — bold/italic text, links, images, and lists." },
      { id: "keywords",         label: "Search keywords",   type: "tags",     max: 5,          optional: true,  placeholder: "Add keyword…", hint: "Up to 5 keywords. Affects search discoverability." },
    ],
  },
  {
    id: "categorization", label: "Categorization", icon: Layers,
    description: "Selecting a relevant category and genres is essential to your app being relevantly discovered in the Meta Store.",
    fields: [
      { id: "_cat_header",       type: "heading", title: "Category", description: "Top level categorization that helps users navigate between content types. Each category has its own Features, Top Charts, and themed shelves." },
      { id: "category",          label: "Category",            type: "select",     required: true, options: ["Apps", "Games"] },
      { id: "artStyle",          label: "Art style",           type: "checkboxes", optional: true, hint: "Visual aesthetic and design elements used in a game.", options: ["Abstract", "Anime", "Cartoon", "Cel-shading", "Comic", "Gothic", "Isometric", "Low-poly", "Minimalist", "Noir", "Pixel", "Psychedelic", "Realism", "Retro-futurism", "Stylized", "Surreal", "Voxel"] },
      { id: "gameTheme",         label: "Game theme",          type: "checkboxes", optional: true, hint: "Overarching setting, motif, or narrative context of a game.", options: ["Aliens", "Animals", "Fantasy", "Historical", "Horror", "Magic", "Medieval", "Monsters", "Mystery", "Mythology", "Post-apocalyptic", "Robots", "Sci-fi", "Superheros", "Thriller", "War", "Western", "Zombies"] },
      { id: "gameplayChallenge", label: "Gameplay challenge",  type: "select",     optional: true, hint: "Degree of difficulty and intensity required to play.", options: ["Casual", "Hardcore"] },
      { id: "gameplayMechanics", label: "Gameplay mechanics",  type: "checkboxes", optional: true, hint: "Rules, actions, and systems that define how players interact.", options: ["Resource gathering", "Resource management", "Rhythm", "Rotating", "Shooting", "Social deduction", "Stealth", "Superpower", "Swimming", "Tag", "Time management", "Tower defense", "Travel", "Trivia", "Turn-based"] },
      { id: "_genre_header",     type: "heading", title: "Genres", description: "Allows users to narrow their search by interests. Select a primary genre first, then optionally up to 2 additional genres." },
      { id: "primaryGenre",      label: "Primary genre",       type: "select", required: true, hint: "The main genre used for categorization and chart placement.", options: GENRE_OPTIONS },
      { id: "additionalGenre1",  label: "Additional genre 1",  type: "select", optional: true, hint: "Adds broader context for discovery and search filters.", options: GENRE_OPTIONS },
      { id: "additionalGenre2",  label: "Additional genre 2",  type: "select", optional: true, hint: "Adds broader context for discovery and search filters.", options: GENRE_OPTIONS },
      // Nested sub-type pickers — shown only when the matching genre is picked in any of the
      // 3 slots above. Only Action and Sports have known sub-types so far; other genres will
      // need the same treatment once their taxonomy is confirmed.
      { id: "actionType", label: "Action type", type: "checkboxes", optional: true, max: 3,
        hint: "Emphasizes physical challenges, skillful interactions, quick reflexes. This genre often requires precision timing and fast-paced decision-making. Choose up to 3 options.",
        options: ["Arcade", "Fighting", "Platformer", "Shooter"],
        nestedUnder: fd => GENRE_FIELD_IDS.find(gid => fd[gid] === "Action") || null },
      { id: "sportsType", label: "Sports type", type: "checkboxes", optional: true,
        hint: "Top level categorization allows users to quickly navigate between these content types. Each category has its own Features, Top Charts, New Releases, and themed shelves.",
        options: ["Combat sports", "Competitive team sports", "Extreme sports", "Multi-sports", "Precision sports", "Racing", "Racket sports", "Recreational sports", "Track & field", "Water sports"],
        nestedUnder: fd => GENRE_FIELD_IDS.find(gid => fd[gid] === "Sports") || null },
      { id: "precisionSportsType", label: "Precision sports type", type: "checkboxes", optional: true, max: 3,
        hint: "Require accuracy and precision, such as darts, golf, and pool & billiards. Choose up to 3 options.",
        options: ["Air hockey", "Bocce", "Bowling", "Cornhole", "Curling", "Darts", "Disc golf", "Golf", "Juggling", "Mini golf", "Mölkky", "Pool & billiards", "Ring toss", "Table soccer"],
        nestedUnder: fd => fd.sportsType?.includes("Precision sports") ? "sportsType" : null },
    ],
  },
  {
    id: "specs", label: "Specs", icon: Settings,
    description: "Technical specifications help users understand how and where your app can be played, and affect discoverability in filtered searches.",
    fields: [
      { id: "earlyAccess",       label: "Early Access",             type: "radio",      required: true, hint: "Label your app as Early Access on your PDP to let users know it is still being built. This label can only be added during the initial submission, but it can be removed at any time.", options: ["Enable", "Disable"] },
      { id: "playerModes",       label: "Player modes",             type: "checkboxes", required: true, hint: "Player interaction styles supported by the app.", options: ["Asynchronous multiplayer", "Local multiplayer","Single player", "Player versus environment (PVE)", "Player versus player (PVP)", "Co-op", "Multiplayer","Massively multiplayer"] },
      { id: "supportedInput",    label: "Supported input",          type: "checkboxes", required: true, hint: "Method or device that enables users to interact with virtual environments.", options: ["Touch controller", "Hands", "Gamepad", "Gaze", "Keyboard", "Mouse", "Stylus", "Voice"] },
      { id: "playArea",          label: "Play area",                type: "select",     required: true, hint: "The safe physical space where the VR experience takes place.", options: ["Movement", "No movement"] },
      { id: "physicalModes",     label: "Supported physical modes", type: "checkboxes", required: true, hint: "Physical postures supported during gameplay.", options: ["Sitting", "Standing", "Roomscale"] },
      { id: "comfortLevel",      label: "Comfort level",            type: "select",     required: true, hint: "Indicates the app's motion and sensory intensity. Mislabeled ratings are a top cause of negative reviews.", options: ["Comfortable", "Moderate", "Intense", "Not rated"] },
      { id: "internetConnection",label: "Internet connection",      type: "radio",      required: true, hint: "Indicates if the app requires an active internet connection.", options: ["Required", "Not required"] },
      { id: "subscription",      label: "Subscription",             type: "radio",      required: true, hint: "Specify whether the app requires a paid subscription to play.", options: ["Required", "Not required"] },
      { id: "socialFeatures",    label: "Social features",          type: "radio",      required: true, hint: "Enables your users to make informed decisions about interacting with others.", options: ["Yes — Uses social features such as messaging, audio, video, or photo sharing", "No — Does not use social features"] },
      { id: "supportedLanguages",label: "Supported languages",      type: "tags",       max: 30, required: true, placeholder: "Add language…", hint: "Languages available within the app's user interface and content." },
    ],
  },
  {
    id: "details", label: "Details", icon: Info,
    description: "Developer and publisher information, external links, and version notes displayed on your store listing.",
    fields: [
      { id: "developer",     label: "Developer",             type: "text",     maxLength: 64,  required: true,  hint: "Your developer or studio name as it appears on the store." },
      { id: "publisher",     label: "Publisher",             type: "text",     maxLength: 64,  optional: true,  hint: "Publisher name, if different from developer." },
      { id: "_links_note", type: "note",
        text: "If these were AI-generated, they're placeholder examples (the domain likely doesn't exist) — double-check and replace with your own real URLs before submitting." },
      { id: "websiteUrl",    label: "Website",               type: "url",      optional: true,  placeholder: "https://yourwebsite.com" },
      { id: "privacyPolicy", label: "Privacy Policy",        type: "url",      required: true,  placeholder: "https://yourwebsite.com/privacy", hint: "A link to your privacy policy. Required to pass VRC.Privacy checks." },
      { id: "supportUrl",    label: "External support link", type: "url",      optional: true,  placeholder: "https://yourwebsite.com/support" },
      { id: "termsOfService",label: "Terms of Service",      type: "url",      optional: true,  placeholder: "https://yourwebsite.com/terms" },
      { id: "whatsNew",      label: "What's new",            type: "textarea", maxLength: 500, rows: 3, optional: true, hint: "Describe changes in the latest version." },
    ],
  },
  {
    id: "assets", label: "Assets", icon: Image,
    description: "Visual and media assets displayed on your store page. High-quality assets directly impact conversion and featuring eligibility.",
    fields: [
      { id: "_cover_header",  type: "heading", title: "Cover art", description: "Cover art appears throughout the Meta Store in various contexts. All images should be at least 1024px on the shortest side." },
      { id: "landscapeCover", label: "Landscape cover (16:9)", type: "file", required: true, accept: "image/*", hint: "2560 × 1440px recommended. Primary browsing thumbnail." },
      { id: "squareCover",    label: "Square cover (1:1)",    type: "file", optional: true, accept: "image/*", hint: "Used in compact list views and notifications." },
      { id: "heroCover",      label: "Hero cover (10:3)",     type: "file", required: true, accept: "image/*", hint: "3000 × 900px. Used if your app is editorially featured." },
      { id: "headerCapsule",  label: "Header capsule",        type: "file", optional: true, accept: "image/*", hint: "Wide banner image used in select header placements." },
      { id: "_icon_header",   type: "heading", title: "Icon & logo", description: "The app icon is shown everywhere your app appears. Logo is optional and used in specific branded placements." },
      { id: "appIcon",        label: "Icon (1:1)",            type: "file", required: true, accept: "image/*", hint: "512 × 512px, solid-filled background, squared corners." },
      { id: "appLogo",        label: "Logo",                  type: "file", optional: true, accept: "image/*", hint: "Lockup logo used in select featured placements." },
      { id: "_media_header",  type: "heading", title: "Screenshots & video", description: "Screenshots show users what your experience looks like. A trailer significantly boosts engagement and conversions." },
      { id: "screenshots",    label: "Screenshots",           type: "files", min: 5, max: 10, required: true, accept: "image/*", hint: "At least 5 required. 2560 × 1440px, 16:9 ratio." },
      { id: "trailerUrl",     label: "Trailer",               type: "file", optional: true, accept: "video/*", hint: "30s–2min. 16:9, 1080p–2K, MP4/H.264/AAC." },
    ],
  },
  {
    id: "content_rating", label: "Content rating", icon: Shield,
    description: "Content classification information required by Meta and used by regional ratings authorities like IARC.",
    fields: [
      { id: "countryOfOrigin",       label: "Country of Origin",       type: "select",     required: true, options: ["United States", "United Kingdom", "Canada", "Australia", "Germany", "France", "Spain", "Italy", "Japan", "South Korea", "Brazil", "India", "China", "Sweden", "Netherlands", "Poland", "Other"] },
      { id: "developerContactEmail", label: "Developer contact email", type: "text",       required: true, placeholder: "info@yourstudio.com", hint: "Shared with IARC for content rating purposes. Monitor this inbox." },
      // No text input anymore — just the "Add Certificate" button (renderFieldCard skips
      // renderField for this one id). Age rating lives inside the IARC questionnaire itself
      // (it's one of its own questions) — we'll surface it back here from that flow later,
      // same for content descriptors, so both are dropped from this section for now.
      { id: "iarcCertId",            label: "IARC certificate",        type: "text",       optional: true, hint: "Complete the IARC questionnaire to generate your certificate." },
    ],
  },
]

export const INITIAL_FORM = {
  appId: "",
  platform: PLATFORM,
  // Answers for the standalone IARC questionnaire tab — { questionID: [selected responseIDs] }.
  // Not part of SECTIONS_CONFIG (its own tab, own custom UI) and not sent to any API yet — that
  // integration comes later, this is local-only for now.
  iarcResponses: {},
  ...Object.fromEntries(
    SECTIONS_CONFIG.flatMap(s => s.fields.filter(f => f.type !== "heading" && f.type !== "note").map(f => [
      f.id,
      f.type === "tags" || f.type === "checkboxes" || f.type === "files" ? [] : f.type === "file" ? null : "",
    ]))
  ),
}

// Confirmed shape (POST /validate/, 2026-08-20): { platform, overall: { score, verdict,
// ready_to_publish, sections: { <api_field_key>: { score, verdict, reason, suggestions } } } }.
// This one call covers every field on every tab, so the big "Validate" button runs it
// instead of per-field calls — "Re-validate" on an individual field still hits validate/{field}/.
const OVERALL_SECTION_FIELD_MAP = {
  title: { section: "name", field: "appName" },
  short_description: { section: "name", field: "shortDescription" },
  long_description: { section: "name", field: "longDescription" },
  keywords: { section: "name", field: "keywords" },
  category: { section: "categorization", field: "category" },
  art_style: { section: "categorization", field: "artStyle" },
  game_theme: { section: "categorization", field: "gameTheme" },
  gameplay_challenge: { section: "categorization", field: "gameplayChallenge" },
  gameplay_mechanics: { section: "categorization", field: "gameplayMechanics" },
  genre: { section: "categorization", field: "primaryGenre" },
  player_modes: { section: "specs", field: "playerModes" },
  supported_input: { section: "specs", field: "supportedInput" },
  play_area: { section: "specs", field: "playArea" },
  supported_physical_modes: { section: "specs", field: "physicalModes" },
  comfort_level: { section: "specs", field: "comfortLevel" },
  internet_connection: { section: "specs", field: "internetConnection" },
  subscription: { section: "specs", field: "subscription" },
  social_features: { section: "specs", field: "socialFeatures" },
  supported_languages: { section: "specs", field: "supportedLanguages" },
  early_access: { section: "specs", field: "earlyAccess" }, // unconfirmed API key — see FULL_TEXT_FIELD_MAP note
  developer: { section: "details", field: "developer" },
  publisher: { section: "details", field: "publisher" },
  website_url: { section: "details", field: "websiteUrl" },
  privacy_policy_url: { section: "details", field: "privacyPolicy" },
  external_support_link: { section: "details", field: "supportUrl" },
  terms_of_service_url: { section: "details", field: "termsOfService" },
  whats_new: { section: "details", field: "whatsNew" },
  country_of_origin: { section: "content_rating", field: "countryOfOrigin" },
  developer_contact_email: { section: "content_rating", field: "developerContactEmail" },
  iarc_certificate_id: { section: "content_rating", field: "iarcCertId" },
  // Confirmed (2026-10, real /generate/complete/ response): the questionnaire scores as its own
  // sibling section alongside iarc_certificate_id, not folded into it — "iarcQuestionnaire" has
  // no literal form input (the questionnaire lives in IarcCertificateTab instead), it just gives
  // this score a home so applyOverallSections()/markFieldsScored() pick it up like every other
  // field, from any of the calls that can return it (group generate/validate, whole-app generate).
  iarc_questionnaire: { section: "content_rating", field: "iarcQuestionnaire" },
}

// The "assets" section bundles every asset into one score with a per-asset `details`
// breakdown — map each detail's asset name back to the UI field it belongs to.
const ASSET_DETAIL_FIELD_MAP = {
  landscape_cover: "landscapeCover",
  square_cover: "squareCover",
  hero_image: "heroCover",
  header_capsule: "headerCapsule",
  icon: "appIcon",
  logo: "appLogo",
  screenshots_count: "screenshots",
  trailer: "trailerUrl",
}

// Forward direction of the above (UI field id -> the API's own asset key name) — used to
// build outgoing single-asset-key requests (validate/generate) and the multipart upload map.
const ASSET_FILE_FIELD_MAP = {
  landscapeCover: "landscape_cover", squareCover: "square_cover", heroCover: "hero_image",
  headerCapsule: "header_capsule", appIcon: "icon", appLogo: "logo", trailerUrl: "trailer",
  screenshots: "screenshots",
}

// Shared UI-field-id <-> API-key maps for every plain text/select field and every
// list (comma-joined) field — used both to build outgoing payloads (Complete App
// Validate, Save draft) and to read incoming ones back (Generate App).
const FULL_TEXT_FIELD_MAP = {
  appName: "title", shortDescription: "short_description", longDescription: "long_description",
  category: "category", gameplayChallenge: "gameplay_challenge",
  playArea: "play_area", comfortLevel: "comfort_level", internetConnection: "internet_connection",
  subscription: "subscription", socialFeatures: "social_features",
  // Unconfirmed API key — guessed following this API's own naming convention (snake_case of
  // the field), same as every other specs field above. Not yet verified against a real
  // request/response since this field isn't in any doc we've seen; check on first real test.
  earlyAccess: "early_access", developer: "developer",
  publisher: "publisher", websiteUrl: "website_url", privacyPolicy: "privacy_policy_url",
  supportUrl: "external_support_link", termsOfService: "terms_of_service_url", whatsNew: "whats_new",
  countryOfOrigin: "country_of_origin", developerContactEmail: "developer_contact_email",
  iarcCertId: "iarc_certificate_id",
}

const FULL_LIST_FIELD_MAP = {
  keywords: "keywords", artStyle: "art_style", gameTheme: "game_theme", gameplayMechanics: "gameplay_mechanics",
  playerModes: "player_modes", supportedInput: "supported_input",
  physicalModes: "supported_physical_modes", supportedLanguages: "supported_languages",
}

// Every non-asset field's UI id -> its own single-field generate/validate API key (the genre
// trio all collapse onto the one "genre" key, same as everywhere else this app joins them).
const FIELD_TO_API_KEY = {
  ...FULL_TEXT_FIELD_MAP, ...FULL_LIST_FIELD_MAP,
  primaryGenre: "genre", additionalGenre1: "genre", additionalGenre2: "genre",
}

// The 10 text/choice fields' generate response carries the new value under `text`; every
// other (format) field's carries it under `value` instead — confirmed doc shape difference.
const TEXT_CHOICE_API_KEYS = new Set([
  "title", "short_description", "long_description", "keywords", "category",
  "art_style", "game_theme", "gameplay_challenge", "gameplay_mechanics", "genre",
])

// An asset field's value is either a real local File (user-picked) or a hosted URL string
// (AI-generated, already persisted server-side) — this is the one place that turns either
// into an <img>-able src, since createObjectURL() throws if handed a plain string.
export const assetSrc = v => (typeof v === "string" ? v : URL.createObjectURL(v))

// Turns one of generate/complete's raw base64 PNG strings (no data: URI prefix) into a real
// File object so it can drop straight into the same file-upload fields a user would've
// picked manually, and later ride along in the multipart Complete App Validate call.
const base64ToFile = (base64, filename, mime = "image/png") => {
  const bytes = atob(base64)
  const arr = new Uint8Array(bytes.length)
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i)
  return new File([arr], filename, { type: mime })
}

// A single-asset generate only ever produces a real score for the ONE field it just touched —
// averaging just that (see generateAssetField) would make the whole assets section look fully
// scored, even 100/100, the moment any one of its ~8 fields gets a result, while the rest are
// still completely empty. Folds in every other REQUIRED asset field with no value as a 0 (an
// empty required asset is a real gap, not an unknown), and leaves an optional empty field or an
// uploaded-but-not-yet-scored one out of the average entirely — all without writing a fake
// per-field entry (and badge) for a field nobody has touched.
const computeAssetsSectionScore = (fields, data) => {
  const assetFields = SECTIONS_CONFIG.find(s => s.id === "assets").fields.filter(f => f.type !== "heading")
  const scores = assetFields.map(f => {
    if (fields[f.id]) return fields[f.id].score
    const hasValue = Array.isArray(data[f.id]) ? data[f.id].length > 0 : !!data[f.id]
    if (hasValue) return null
    return f.required ? 0 : null
  }).filter(s => s !== null)
  return scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null
}

// Confirmed shape (2026-09-11 real generate/complete result): the assets group hands back
// hosted S3 URLs (asset_urls), not base64 — and those images are already validated
// server-side and persisted against this app_id (a later validate/submit call reuses that
// stored result when no files are re-attached, per the API doc), so there's no need to fetch
// the URL back into a File just to re-upload it. Store the URL string directly; the
// file-upload fields below know how to preview a URL the same way they preview a picked File.
const applyGeneratedAssets = assetsGroup => {
  const patch = {}
  const urls = assetsGroup?.asset_urls || {}
  Object.entries(urls).forEach(([apiKey, val]) => {
    if (!val) return
    if (apiKey === "screenshots") {
      if (Array.isArray(val) && val.length) patch.screenshots = val
      return
    }
    const uiId = ASSET_DETAIL_FIELD_MAP[apiKey]
    if (uiId) patch[uiId] = val
  })
  // Defensive fallback for an alternate/older response shape that returns raw base64 instead
  // of hosted URLs (seen in an earlier sample doc) — only fills gaps asset_urls didn't cover.
  const base64Map = assetsGroup?.assets_base64 || {}
  Object.entries(base64Map).forEach(([apiKey, val]) => {
    if (!val) return
    if (apiKey === "screenshots") {
      if (Array.isArray(val) && val.length && !patch.screenshots) patch.screenshots = val.map((b64, i) => base64ToFile(b64, `screenshot_${i + 1}.png`))
      return
    }
    const uiId = ASSET_DETAIL_FIELD_MAP[apiKey]
    if (uiId && !patch[uiId]) patch[uiId] = base64ToFile(val, `${apiKey}.png`)
  })
  return patch
}

// GET /history/{app_id}/ and GET /shared/{app_id}/ return the same shape (2026-08-21): the
// app's full current record — top-level keys (title, short_description, category, ...)
// holding the latest known value per field, `sections` holding the latest Complete App
// Validate breakdown, and (history only) `overall` holding the aggregate score/readiness.
// (2026-09 doc) The 21 deterministic format fields (specs/details/content_rating) may
// instead live under a separate top-level `format_fields` bucket — checked as a third
// fallback so nothing goes missing if a field only ever lands there. Pure (only depends on
// module-level maps above), so it's hoisted out of the component and exported for reuse by
// the standalone Preview page (StoreValidatorPreview.jsx), which has no access to this
// component's own state.
export const buildFormPatchFromRecord = record => {
  const patch = {}
  if (record?.platform) patch.platform = record.platform
  Object.entries(FULL_TEXT_FIELD_MAP).forEach(([uiId, apiKey]) => {
    const val = record?.[apiKey] || record?.sections?.[apiKey]?.data || record?.format_fields?.[apiKey]
    if (!val) return
    // The maxLength cap is purely a frontend UI guardrail (see SECTIONS_CONFIG) — a saved
    // record (e.g. AI-generated before this cap existed, or written by another client) can
    // still carry more, so enforce it here too rather than only on the input's own keystrokes.
    const maxLength = SECTIONS_CONFIG.flatMap(s => s.fields).find(f => f.id === uiId)?.maxLength
    patch[uiId] = maxLength ? String(val).slice(0, maxLength) : val
  })
  Object.entries(FULL_LIST_FIELD_MAP).forEach(([uiId, apiKey]) => {
    const val = record?.[apiKey] || record?.sections?.[apiKey]?.data || record?.format_fields?.[apiKey]
    if (!val) return
    const list = Array.isArray(val) ? val : String(val).split(",").map(s => s.trim()).filter(Boolean)
    // A saved app_id can carry more than the UI's own cap (e.g. a real history record with
    // 10 keywords when the form only allows 5) — this is the only list-field restore path
    // that wasn't already enforcing each field's `max`, unlike the AI-generate path.
    const max = SECTIONS_CONFIG.flatMap(s => s.fields).find(f => f.id === uiId)?.max
    patch[uiId] = max ? list.slice(0, max) : list
  })
  const genreVal = record?.genre || record?.sections?.genre?.data
  if (genreVal) {
    const parts = Array.isArray(genreVal) ? genreVal : String(genreVal).split(",").map(s => s.trim()).filter(Boolean)
    if (parts[0]) patch.primaryGenre = parts[0]
    if (parts[1]) patch.additionalGenre1 = parts[1]
    if (parts[2]) patch.additionalGenre2 = parts[2]
  }

  // Confirmed live (2026-09-14): the top-level `assets` object only ever holds bare
  // generated filenames ("icon_generated.png"), never a usable URL — there's no way to
  // reconstruct a real hosted path from just a filename on the client. The real S3 URLs
  // live one level deeper, under intake.result.groups.assets — the exact same shape
  // generate/complete itself returns (this is that same result, just stored) — so reuse
  // applyGeneratedAssets on that instead of the useless top-level `assets` filenames.
  if (record?.intake?.result?.groups?.assets) {
    Object.assign(patch, applyGeneratedAssets(record.intake.result.groups.assets))
  }
  // Confirmed live (2026-10-15, real history record): a STANDALONE single-asset generate (the
  // per-field "Generate with AI" button, not the whole-app flow above) persists its own result
  // under a separate top-level `asset_generation.result` key — same {asset_urls, ...} shape the
  // live generate response itself already returns, so applyGeneratedAssets reads it the same
  // way. This is why a standalone-generated image (e.g. a single Logo regenerate after the
  // whole-app run already finished) was disappearing on reload: intake.result.groups.assets
  // above only ever reflects the whole-app generate's own assets and was never being updated by
  // a later standalone one. Only ever covers whichever key(s) were last generated standalone, so
  // it's additive here, not a replacement — applied second so a more recently standalone-
  // regenerated asset correctly overrides an older whole-app-generated value for the same key.
  if (record?.asset_generation?.result) {
    Object.assign(patch, applyGeneratedAssets(record.asset_generation.result))
  }
  if (!record?.intake?.result?.groups?.assets && !record?.asset_generation?.result && record?.assets && Object.keys(record.assets).length) {
    // A manually-uploaded (not AI-generated) asset has no intake.result or asset_generation at
    // all, so there's currently no way to restore its preview after a reload — flagging so this
    // residual gap is visible rather than silently swallowed.
    console.warn("Store Validator: app history has assets but no intake.result.groups.assets/asset_generation.result to restore real URLs from — got bare filenames:", record.assets)
  }

  // Confirmed key (2026-10, real /generate/complete/ response): "iarc_questionnaire", living
  // under `listing` at the top level for that response — not "iarc_responses" (an earlier,
  // wrong guess).
  const restoredIarc = parseIarcAnswers(
    record?.iarc_questionnaire || record?.listing?.iarc_questionnaire
    || record?.sections?.iarc_questionnaire?.data || record?.format_fields?.iarc_questionnaire
  )
  if (restoredIarc) patch.iarcResponses = restoredIarc

  return patch
}

// ─── main component ───────────────────────────────────────────────────────────

const StoreValidator = () => {
  const navigate = useNavigate()
  const { trackEvent } = useAnalytics()
  const [searchParams] = useSearchParams()
  const hasHandledUrlRef = useRef(false)
  const [step,             setStep]             = useState("landing")
  const [apps,             setApps]             = useState([])
  // Shown in the picker so people can see what's coming, but only PLATFORM (meta_horizon)
  // can actually be created — every option list in this tool (category, genre, art style,
  // ...) is built specifically around Meta Horizon's taxonomy, so anything else would
  // validate against the wrong rules until that's built out.
  const [platforms,        setPlatforms]        = useState([PLATFORM])
  const [selectedPlatform, setSelectedPlatform] = useState(PLATFORM)
  const [appsLoading,      setAppsLoading]      = useState(true)
  const [openingApp,       setOpeningApp]       = useState(false) // true while openApp's history fetch is in flight
  const [creatingApp,      setCreatingApp]      = useState(false)
  const [savingApp,        setSavingApp]        = useState(false)
  const [exportingApp,     setExportingApp]     = useState(false)
  const [downloadingCertificate, setDownloadingCertificate] = useState(false)
  const [certificateError,       setCertificateError]       = useState(null)
  const [submitting,       setSubmitting]       = useState(false)
  const [openDropdownId,   setOpenDropdownId]   = useState(null)
  const [briefByApp,       setBriefByApp]       = useState({})
  const [generating,       setGenerating]       = useState(false)
  // Per-app so switching apps doesn't leak one app's in-progress Q&A into another (same
  // reason briefByApp is keyed this way) — { pending: [{id,text}], history: [{question,answer}],
  // meta: {any session/conversation id the API echoed back}, answersDraft: {id: text} }.
  const [conversationByApp, setConversationByApp] = useState({})
  // Per-asset-key AI image generation (POST /generate/<asset_key>/ and /generate/group/assets/)
  // — shares the same one-at-a-time generation lock as /generate/complete/ (per the API doc),
  // so generatingAssetKey doubles as the busy flag that disables every other Generate trigger
  // while set. "__group__" means the whole-assets-group generate is running.
  const [generatingAssetKey, setGeneratingAssetKey] = useState(null)
  // True while assets keep generating in the background after the rest of a whole-app
  // generate already landed (status: "assets_processing") — independent of `generating`,
  // which clears as soon as the text/spec groups are ready so the form stays usable.
  const [assetsGenerating, setAssetsGenerating] = useState(false)
  const [assetPromptOpenFor, setAssetPromptOpenFor] = useState(null)
  const [assetPromptDraft,   setAssetPromptDraft]   = useState("")
  // Markdown editor for the long description (single set of flags — only one field in the
  // whole form uses this type). Off by default — a plain textarea until the user opts in, so
  // most people just see a normal text box, not a toolbar they didn't ask for.
  const [markdownEnabled,   setMarkdownEnabled]   = useState(false)
  const [markdownPreview,   setMarkdownPreview]   = useState(false)
  const [imagePromptOpen,   setImagePromptOpen]   = useState(false)
  const markdownTextareaRef = useRef(null)
  // Lightbox tracks which asset field is open by a stable key, not the image src — a locally
  // picked File's src is a blob URL from URL.createObjectURL(), which returns a brand new
  // string every time it's called even for the same File, so src alone can't be matched back
  // to "which field is this" when rebuilding the gallery for prev/next navigation.
  const [previewImageKey, setPreviewImageKey] = useState(null) // e.g. "appIcon" or "screenshots:2", or null when closed
  const [selectedAppIds,   setSelectedAppIds]   = useState(new Set())
  const [deleteConfirmation, setDeleteConfirmation] = useState(null)
  const [deletingApps,      setDeletingApps]      = useState(false)
  const [activeSection,    setActiveSection]    = useState("name")
  // Swaps the Content Rating tab's own content area for the IARC questionnaire — not a
  // separate tab, just an in-place view within Content Rating, with its own "Back" control.
  const [showIarcQuestionnaire, setShowIarcQuestionnaire] = useState(false)
  const [formData,         setFormData]         = useState(INITIAL_FORM)
  // Generate/regenerate keep running in the background even after the user navigates away —
  // this component never unmounts on "Back" (it's the same instance, just a different `step`),
  // and even a real reload leaves the backend job running. A late result must only land in
  // formData/validationResults if the user is still looking at the app it was generated for —
  // otherwise it would silently splice one app's AI content into whatever app is on screen when
  // the response arrives. This ref always mirrors the currently-displayed app id so async
  // completions can check that before touching state (see applyGenerateCompleteResult et al).
  const activeAppIdRef = useRef(formData.appId)
  useEffect(() => { activeAppIdRef.current = formData.appId }, [formData.appId])
  // Keyed by app_id (not a flat string) so each app remembers its own brief text across
  // switches/regenerates, instead of it leaking between apps or getting wiped on navigation.
  const aiBrief = briefByApp[formData.appId] || ""
  const setAiBrief = text => setBriefByApp(p => ({ ...p, [formData.appId]: text }))

  const EMPTY_CONVERSATION = { pending: [], answersDraft: {} }
  const conversation = conversationByApp[formData.appId] || EMPTY_CONVERSATION
  const setConversation = patch => setConversationByApp(p => ({
    ...p,
    [formData.appId]: { ...(p[formData.appId] || EMPTY_CONVERSATION), ...patch },
  }))
  const [tagInputs,        setTagInputs]        = useState({})
  const [validationResults,setValidationResults]= useState({})
  const [overallApiResult, setOverallApiResult] = useState(null)
  const [fieldSuggestions,  setFieldSuggestions]  = useState({})
  const [revealedSuggestions, setRevealedSuggestions] = useState(new Set())
  const [validating,        setValidating]        = useState(false)
  const [validateError,     setValidateError]     = useState(null)
  const [validatingFields,  setValidatingFields]  = useState(new Set())
  // The whole-app AI Generate is a one-time action per app — once it's produced content,
  // further changes go through each section's own "Regenerate this section" button instead, so
  // this only flips back to false for a brand-new app (createNewApp) or one that was never
  // AI-generated to begin with (no record.intake on reopen).
  const [hasGeneratedOnce, setHasGeneratedOnce]   = useState(false)
  const [regeneratingSection, setRegeneratingSection] = useState(null) // group id mid-regenerate, or null
  const [regeneratingField,   setRegeneratingField]   = useState(null) // UI field id mid-regenerate, or null
  const [generatingIarc,      setGeneratingIarc]      = useState(false)
  const [validatingIarc,      setValidatingIarc]      = useState(false)
  // predicted_rating / audience_fit / content_audience_fit (2026-10 feature) — don't fit
  // normalizeValidateResponse()'s shared {score, status, feedback} shape (used by every other
  // field/section in the app), so they're tracked separately rather than widening that function.
  const [iarcInsights,        setIarcInsights]        = useState(null)
  // Fields the user has actually typed/picked/removed something in since their last real
  // score (a validate, a generate, or loading a saved app) — the per-field "Regenerate" button
  // only shows for these, not for every field all the time.
  const [dirtyFields,         setDirtyFields]         = useState(new Set())
  // Fields that have had at least one REAL score (from Validate, Regenerate, or Generate) at
  // some point — unlike a field's current score (cleared the moment it's edited, see setField),
  // this never gets cleared by an edit, only reset for a different/new app. It's what gates the
  // "Regenerate this field" prompt: there's nothing to react against ("didn't like the result")
  // until the field has actually produced one, however it got there.
  const [everScoredFields,    setEverScoredFields]    = useState(new Set())
  const [copiedFieldId,       setCopiedFieldId]       = useState(null) // field id whose value was just copied, briefly
  const [downloadingFieldId,  setDownloadingFieldId]  = useState(null) // asset field id mid-download, or null
  // The app's real backend status ("draft"/"in_review"/...) — unlike `submitted` (only true
  // right after this session's own Submit click), this reflects reality even after reopening
  // an app that was already submitted in a previous session.
  const [appStatus,         setAppStatus]         = useState("draft")

  // Only meta_horizon apps can exist right now, but filtering explicitly (rather than
  // assuming) stays correct if the backend ever has apps for other platforms already.
  const visibleApps = apps.filter(a => (a.platform || PLATFORM) === selectedPlatform)

  const loadApps = async () => {
    setAppsLoading(true)
    try {
      const res = await storeValidatorApi.getUserApps()
      const list = Array.isArray(res) ? res : res?.apps || res?.results || []
      setApps(list.map(mapApiApp))
    } catch (e) {
      console.error("Failed to load Store Validator apps:", e)
      setApps([])
    } finally {
      setAppsLoading(false)
    }
  }

  useEffect(() => { loadApps() }, [])

  useEffect(() => {
    storeValidatorApi.getPlatforms()
      .then(res => {
        const list = Array.isArray(res) ? res : res?.platforms || res?.results || []
        const ids = list.map(p => (typeof p === "string" ? p : p?.id || p?.value || p?.platform)).filter(Boolean)
        if (ids.length) setPlatforms(ids)
      })
      .catch(e => console.error("Failed to load platforms:", e))
  }, [])

  // Restores the open app + active tab from the URL on a fresh load/refresh, once the apps
  // list has loaded. Runs only once — subsequent URL changes come from our own navigate() calls.
  useEffect(() => {
    if (hasHandledUrlRef.current || appsLoading) return
    hasHandledUrlRef.current = true
    const appIdFromUrl = searchParams.get("app")
    if (!appIdFromUrl) return
    const tabFromUrl = searchParams.get("tab")
    const row = apps.find(a => a.id === appIdFromUrl) || { id: appIdFromUrl, name: "App", category: "" }
    openApp(row).then(() => {
      if (tabFromUrl && SECTIONS_CONFIG.some(s => s.id === tabFromUrl)) setActiveSection(tabFromUrl)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [appsLoading])

  // Clears a nested genre sub-type field's selections once its trigger no longer applies
  // (e.g. switching a genre slot away from "Action", or deselecting "Precision sports"),
  // so stale hidden choices don't linger and get submitted.
  useEffect(() => {
    const genreVals = [formData.primaryGenre, formData.additionalGenre1, formData.additionalGenre2]
    if (!genreVals.includes("Action") && formData.actionType?.length) setField("actionType", [])
    if (!genreVals.includes("Sports") && formData.sportsType?.length) setField("sportsType", [])
    if (!formData.sportsType?.includes("Precision sports") && formData.precisionSportsType?.length) setField("precisionSportsType", [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.primaryGenre, formData.additionalGenre1, formData.additionalGenre2, formData.sportsType])

  const SHOW_COMING_SOON = false
  if (SHOW_COMING_SOON) {
    return (
      <React.Fragment>
        <SEO title="Store Validator" description="Validate and optimize your app's store listing before publishing." />
        <div className="page-content">
          <div className="container-fluid" style={{ fontFamily: "Rubik, sans-serif" }}>
            <div className="mb-4 mt-2">
              <h2 className="mb-0 text-white" style={{ fontWeight: 700, fontSize: "28px" }}>
                Store Validator
              </h2>
              <p style={{ fontSize: "15px", maxWidth: "900px", color: "#9ca3af", fontWeight: 400, marginTop: "8px", marginBottom: "20px" }}>
                Validate and optimize your app's store listing before publishing.
              </p>
            </div>

            <div className="text-center py-5" style={{
              background: "#21222d",
              borderRadius: "24px",
              border: "1px dashed rgba(255,255,255,0.1)",
            }}>
              <div style={{ maxWidth: "500px", margin: "0 auto" }}>
                <div style={{
                  width: "80px", height: "80px",
                  background: "rgba(246,37,90,0.05)",
                  borderRadius: "50%",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  margin: "0 auto 24px",
                  border: "1px solid rgba(246,37,90,0.2)",
                }}>
                  <Shield size={32} color="#F6255A" />
                </div>
                <h4 className="text-white mb-3">Coming Soon</h4>
                <p style={{ lineHeight: "1.6", color: "#e8e8e8" }} className="mb-0">
                  We're building real store-listing validation and optimization tools. Check back soon!
                </p>
              </div>
            </div>
          </div>
        </div>
      </React.Fragment>
    )
  }

  const activeCfg    = SECTIONS_CONFIG.find(s => s.id === activeSection)
  const activeIdx    = SECTIONS_CONFIG.findIndex(s => s.id === activeSection)
  const sectionResult= validationResults[activeSection]
  // The backend shares ONE generation lock per app_id across every async generate call
  // (whole-app, per-section group, per-asset-key, or the whole assets group) — a second
  // attempt while any of them is already running gets a 409. This is the single guard every
  // generate trigger below checks, including while assets are still finishing in the
  // background after the rest of a whole-app generate already landed.
  const anyGenerationInFlight = generating || assetsGenerating || !!generatingAssetKey || !!regeneratingSection || !!regeneratingField || generatingIarc || validatingIarc

  const isSectionComplete = id => {
    const cfg = SECTIONS_CONFIG.find(s => s.id === id)
    return cfg.fields.filter(f => f.required && f.type !== "heading").every(f => {
      const v = formData[f.id]
      // Respects a field's own minimum count (e.g. Screenshots' 5) when it declares one,
      // instead of just checking "not empty" for every array field.
      if (Array.isArray(v)) return v.length >= (f.min || 1)
      if (typeof v === "string") return v.trim().length > 0
      return !!v // File objects (type "file"), or anything else — just check truthiness
    })
  }

  const overallScore = () => {
    if (overallApiResult?.score != null) return overallApiResult.score
    // Same reasoning as dropping a whole section on edit: averaging only the sections that
    // still happen to have a score would barely move (or not move at all) once just one
    // section goes stale, silently hiding that something needs re-validation. Only show a
    // real overall number once every section actually has one.
    if (Object.keys(validationResults).length !== SECTIONS_CONFIG.length) return null
    const scores = Object.values(validationResults).map(r => r.score)
    return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)
  }

  // Once a full Complete App Validate has run, trust its own ready_to_publish verdict
  // instead of the arbitrary "score >= 60" guess.
  const canSubmit = overallApiResult
    ? overallApiResult.readyToPublish
    : Object.keys(validationResults).length === SECTIONS_CONFIG.length && overallScore() >= 60

  // ── app list handlers (landing step) ───────────────────────────────────────

  const toggleAppSelected = id =>
    setSelectedAppIds(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n })

  const toggleSelectAllApps = () =>
    setSelectedAppIds(p => p.size === visibleApps.length ? new Set() : new Set(visibleApps.map(a => a.id)))

  const requestDeleteApps = (ids, label) => setDeleteConfirmation({ ids, label })

  const deleteConfirmedApps = async () => {
    if (!deleteConfirmation || deletingApps) return
    const { ids } = deleteConfirmation
    setDeletingApps(true)
    try {
      setApps(p => p.filter(a => !ids.includes(a.id)))
      setSelectedAppIds(p => {
        const remaining = new Set(p)
        ids.forEach(id => remaining.delete(id))
        return remaining
      })
      await Promise.all(ids.map(id => storeValidatorApi.deleteApp(id)))
      trackEvent(ANALYTICS_EVENTS.FEATURE.STORE_VALIDATOR_APP_DELETED, { count: ids.length })
    } catch (e) {
      console.error("Failed to delete app(s):", e)
      loadApps() // resync with server if a delete failed
    } finally {
      setDeletingApps(false)
      setDeleteConfirmation(null)
    }
  }


  // Updates validation scores, overall readiness, and the list row from a record —
  // deliberately never touches form fields, so calling this after a save can't clobber
  // what the user just typed with a possibly-stale value from `sections.<field>.data`.
  // A saved score for an asset field is only honest if that asset is actually restorable
  // right now — a standalone-generated asset (see buildFormPatchFromRecord's note above) has
  // no way to reconstruct its real URL after a reload, so its field ends up blank even though
  // the backend's own score for it is a real 100. Showing that score anyway would look like a
  // bug (and be one) — a blank field can't possibly be a perfect asset. Drop that field's
  // score (and recompute the section average) until it's re-validated/re-generated against
  // whatever's actually on screen.
  const pruneStaleAssetScores = (results, effectiveFormData) => {
    const assetsResult = results?.assets
    if (!assetsResult?.fields || !effectiveFormData) return results
    const fields = {}
    Object.entries(assetsResult.fields).forEach(([fieldId, fieldRes]) => {
      const val = effectiveFormData[fieldId]
      const hasValue = Array.isArray(val) ? val.length > 0 : !!val
      if (hasValue) fields[fieldId] = fieldRes
    })
    if (Object.keys(fields).length === Object.keys(assetsResult.fields).length) return results
    if (!Object.keys(fields).length) {
      const { assets: _drop, ...rest } = results
      return rest
    }
    const scores = Object.values(fields).map(f => f.score)
    const avg = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)
    return { ...results, assets: { score: avg, fields } }
  }

  const applyRecordInsights = (record, effectiveFormData) => {
    if (!record) return
    if (record.status) setAppStatus(record.status)
    if (record.sections) {
      const freshResults = pruneStaleAssetScores(applyOverallSections(record.sections), effectiveFormData)
      setValidationResults(freshResults)
      markFieldsScored(freshResults)
      // Unlike the other two call sites (which only ever update on a fresh, real result), this
      // one fully replaces — loading a different app's saved record should reflect exactly what
      // it has, including clearing to null if it has no insights at all, not keep showing
      // whatever the previously-open app happened to have.
      setIarcInsights(extractIarcInsights(record.sections.iarc_questionnaire))
    }
    if (record.overall) {
      setOverallApiResult({
        score: record.overall.score != null ? Math.round(record.overall.score * 10) : null,
        readyToPublish: !!record.overall.ready_to_publish,
      })
    }
    if (record.app_id) setApps(p => p.map(a => a.id === record.app_id ? applyDraftToRow(a, record) : a))
  }

  // Keeps the URL in sync with the open app + active tab (query params, no route config
  // changes needed) so a refresh lands back on the same app/tab instead of the list.
  const syncUrl = (appId, tab) => {
    if (appId) navigate(`/store-validator?app=${appId}&tab=${tab}`, { replace: true })
    else navigate("/store-validator", { replace: true })
  }

  const switchSection = sectionId => {
    setActiveSection(sectionId)
    setShowIarcQuestionnaire(false)
    setValidateError(null)
    syncUrl(formData.appId, sectionId)
    trackEvent(ANALYTICS_EVENTS.FEATURE.STORE_VALIDATOR_SECTION_SWITCHED, { section: sectionId })
  }

  const openApp = async app => {
    trackEvent(ANALYTICS_EVENTS.FEATURE.STORE_VALIDATOR_APP_OPENED, { app_id: app.id, app_name: app.name })
    setFormData(p => ({ ...INITIAL_FORM, appId: app.id, appName: app.name, category: app.category || "" }))
    setValidationResults({}); setOverallApiResult(null); setIarcInsights(null)
    setHasGeneratedOnce(false) // corrected below once the real record loads, if it was AI-generated
    setDirtyFields(new Set())
    setEverScoredFields(new Set()) // corrected below once the real record loads, from its own saved sections
    setActiveSection("name")
    setShowIarcQuestionnaire(false)
    setStep("form")
    setAppStatus("draft") // corrected below once the real record loads, if it's not actually a draft
    syncUrl(app.id, "name")
    setOpeningApp(true)

    try {
      const record = await storeValidatorApi.getAppHistory(app.id)
      console.log("Store Validator app history record:", record)
      const patch = buildFormPatchFromRecord(record)
      if (Object.keys(patch).length) setFormData(p => ({ ...p, ...patch }))
      const effectiveFormData = { ...INITIAL_FORM, appId: app.id, appName: app.name, category: app.category || "", ...patch }
      applyRecordInsights(record, effectiveFormData)
      // Confirmed live (2026-09-14): the brief used to generate this app lives under
      // intake.original_brief/enriched_brief — restore it so it doesn't vanish on reload.
      // enriched_brief is preferred since it's the AI-clarified version actually used for
      // generation once any follow-up questions were answered.
      const savedBrief = record?.intake?.enriched_brief || record?.intake?.original_brief
      if (savedBrief) setBriefByApp(p => ({ ...p, [app.id]: savedBrief }))
      // The whole-app Generate is a one-time action per app — `intake` only exists once
      // generate/complete/ has actually run for this app_id, so its presence is the durable
      // (survives-reload) signal that this app already used its one shot.
      setHasGeneratedOnce(!!record?.intake)
      return record
    } catch (e) {
      console.error("Failed to load app history:", e)
      return null
    } finally {
      setOpeningApp(false)
    }
  }

  const createNewApp = async () => {
    if (creatingApp) return
    setCreatingApp(true)
    try {
      const res = await storeValidatorApi.createAppId(PLATFORM)
      const appId = res?.app_id || res?.id
      trackEvent(ANALYTICS_EVENTS.FEATURE.STORE_VALIDATOR_APP_CREATED, { app_id: appId, platform: selectedPlatform })
      setFormData({ ...INITIAL_FORM, appId, appName: "My App", platform: PLATFORM })
      setValidationResults({}); setOverallApiResult(null); setIarcInsights(null)
      setHasGeneratedOnce(false)
      setDirtyFields(new Set())
      setEverScoredFields(new Set())
      setActiveSection("name")
      setShowIarcQuestionnaire(false)
      setStep("form")
      setAppStatus("draft")
      syncUrl(appId, "name")
      setApps(p => [{ id: appId, name: "My App", category: "", score: null, status: "Draft", updatedAt: "just now", platform: PLATFORM }, ...p])

      // Create App ID only reserves an id — /draft/ is the lightweight save endpoint,
      // so persist the default title right away or the app won't exist server-side
      // (and will vanish from the list on refresh).
      try {
        const draft = await storeValidatorApi.saveDraft(appId, PLATFORM, { title: "My App" })
        applyRecordInsights(draft)
      } catch (e) {
        console.error("Failed to persist new app:", e)
      }
    } catch (e) {
      console.error("Failed to create app id:", e)
    } finally {
      setCreatingApp(false)
    }
  }

  // Only sends the fields we've confirmed the API accepts today (Name + Categorization
  // minus genre) — Specs/Details/Assets/Content Rating fields aren't sent yet since their
  // values haven't been confirmed against the backend's expected format.
  const buildSafeSavePayload = () => {
    const payload = { title: formData.appName || "My App" }
    const safeTextKeys = ["shortDescription", "longDescription", "category", "gameplayChallenge"]
    const safeListKeys = ["keywords", "artStyle", "gameTheme", "gameplayMechanics"]
    safeTextKeys.forEach(uiId => {
      const apiKey = FULL_TEXT_FIELD_MAP[uiId]
      if (apiKey && formData[uiId]) payload[apiKey] = formData[uiId]
    })
    safeListKeys.forEach(uiId => {
      const apiKey = FULL_LIST_FIELD_MAP[uiId]
      if (apiKey && formData[uiId]?.length) payload[apiKey] = formData[uiId].join(", ")
    })
    // Confirmed key and shape (2026-10, from the real /generate/complete/ response's own
    // `listing.iarc_questionnaire`) — unlike the rest of content_rating/specs/details, this
    // one field's format is now verified, so it's safe to persist here even though its
    // neighbors still aren't. Otherwise a filled-in questionnaire only ever lives in local
    // React state and is lost on reload, since this /draft/ payload is the only thing that
    // survives one. (Was briefly sent as "iarc_responses" — wrong key, fixed once the real
    // generate response confirmed "iarc_questionnaire" instead.)
    const iarcAnswers = buildIarcAnswersPayload(formData.iarcResponses)
    if (Object.keys(iarcAnswers).length) payload.iarc_questionnaire = iarcAnswers
    return payload
  }

  const saveApp = async () => {
    if (!formData.appId || savingApp) return
    setSavingApp(true)
    try {
      const draft = await storeValidatorApi.saveDraft(formData.appId, formData.platform, buildSafeSavePayload())
      applyRecordInsights(draft)
    } catch (e) {
      console.error("Failed to save app:", e)
    } finally {
      setSavingApp(false)
    }
  }

  const exportApp = async () => {
    if (!formData.appId || exportingApp) return
    setExportingApp(true)
    try {
      const blob = await storeValidatorApi.exportApp(formData.appId)
      const blobUrl = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = blobUrl
      a.download = `${(formData.appName || formData.appId).replace(/[^a-z0-9-_]+/gi, "_")}.zip`
      document.body.appendChild(a); a.click(); a.remove()
      URL.revokeObjectURL(blobUrl)
    } catch (e) {
      console.error("Failed to export app:", e)
    } finally {
      setExportingApp(false)
    }
  }

  // Predicted IARC rating certificate PDF (2026-10 feature) — same blob-download pattern as
  // exportApp above. The documented 400 ("no saved IARC questionnaire answers yet") comes back
  // as raw JSON text in err.message (blobGet's generic error path, shared with every other
  // binary-download endpoint) — parsed here so the user sees the real reason, not a JSON blob.
  const downloadIarcCertificate = async () => {
    if (!formData.appId || downloadingCertificate) return
    setDownloadingCertificate(true)
    setCertificateError(null)
    try {
      const blob = await storeValidatorApi.getIarcCertificate(formData.appId)
      const blobUrl = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = blobUrl
      a.download = `${(formData.appName || formData.appId).replace(/[^a-z0-9-_]+/gi, "_")}-iarc-certificate.pdf`
      document.body.appendChild(a); a.click(); a.remove()
      URL.revokeObjectURL(blobUrl)
    } catch (e) {
      let message = "Couldn't download the certificate — try again."
      try {
        const parsed = JSON.parse(e.message)
        if (parsed?.error) message = parsed.error
      } catch (_) { /* not JSON — keep the generic message */ }
      setCertificateError(message)
      console.error("Failed to download IARC certificate:", e)
    } finally {
      setDownloadingCertificate(false)
    }
  }

  // Preview is its own standalone page/route (StoreValidatorPreview.jsx, mounted at
  // /store-validator/preview) so it renders with no site header/sidebar — opened in a new
  // tab rather than as a step within this component.
  const previewUrl = appId => `${window.location.origin}/store-validator/preview?app=${appId}`

  const submitAppForReview = async () => {
    if (!formData.appId || submitting) return
    setSubmitting(true)
    try {
      await saveApp()
      await storeValidatorApi.submitForReview(formData.appId)
      trackEvent(ANALYTICS_EVENTS.FEATURE.STORE_VALIDATOR_SUBMITTED, {
        app_id: formData.appId,
        app_name: formData.appName,
        platform: formData.platform,
      })
      setAppStatus("ready_to_publish")
      setApps(p => p.map(a => a.id === formData.appId ? { ...a, status: "Ready To Publish" } : a))
    } catch (e) {
      console.error("Failed to submit app for review:", e)
    } finally {
      setSubmitting(false)
    }
  }

  // ── form handlers ──────────────────────────────────────────────────────────

  // A field's last score belongs to the value it had when validated — once the user changes
  // it (text edit, tag/checkbox toggle, new file, generated content tweaked, anything), that
  // score no longer describes what's actually in the field, so leaving it displayed would be
  // misleading (e.g. still showing 100/100 after the content was edited or removed). Clear
  // just that field's result — recomputing the section's average from whatever's left, or
  // dropping the section entirely once nothing in it is validated, the same invariant
  // applyOverallSections already uses — and clear the whole-app overall score too, since
  // Complete App Validate's readiness verdict speaks for the whole form, not one field.
  // Bulk patches (loading a saved app, applying AI-generated fields) go through setFormData
  // directly instead of this, since those arrive with their own freshly computed scores.
  const setField = (id, val) => {
    // Belt-and-braces alongside the input/textarea's own maxLength attribute (which only
    // blocks new keystrokes, not a value set programmatically) — cap here too so nothing can
    // land longer than the limit shown under the field, from any source.
    const maxLength = findFieldConfig(id)?.maxLength
    const nextVal = maxLength && typeof val === "string" ? val.slice(0, maxLength) : val
    setFormData(p => ({ ...p, [id]: nextVal }))
    setDirtyFields(prev => (prev.has(id) ? prev : new Set(prev).add(id)))
    // Only drop THIS field's own score, not the whole section's — every other field keeps its
    // last known result, so a per-field Re-validate (gated on dirtyFields, see the field-card
    // panel below) is enough to catch this one back up instead of forcing a full re-check of
    // everything in the section. The genre trio all share one score stored under "primaryGenre"
    // (same convention used everywhere else genre is scored), so any of the 3 dropdowns clears
    // that same shared entry.
    const isGenreSlot = id === "primaryGenre" || id === "additionalGenre1" || id === "additionalGenre2"
    const resultFieldId = isGenreSlot ? "primaryGenre" : id
    setValidationResults(p => {
      const sectionId = SECTIONS_CONFIG.find(s => s.fields.some(f => f.id === resultFieldId))?.id
      if (!sectionId || !p[sectionId]?.fields?.[resultFieldId]) return p
      const { [resultFieldId]: _removedField, ...restFields } = p[sectionId].fields
      const scores = Object.values(restFields).map(f => f.score)
      const avg = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null
      return { ...p, [sectionId]: { score: avg, fields: restFields } }
    })
    setOverallApiResult(null)
  }

  // Mirrors setField's own "drop this field's stale score" behavior for the IARC questionnaire —
  // except two separate field ids under content_rating.fields are tied to these answers:
  // "iarcQuestionnaire" (the questionnaire's own score, from /validate/iarc_questionnaire/, shown
  // in the full-screen/inline editor) and "iarcCertId" (the plain "IARC certificate" field's own
  // score, from the generic per-field validate, shown in the AI Validation panel's field list —
  // its "Re-validate" button was staying disabled after an edit because only iarcQuestionnaire
  // was being dropped/marked dirty here). Both — plus the insights, computed from the same
  // answers — are only valid for the exact answers they were generated against, so ANY change (a
  // manual edit, or a fresh set of AI-generated answers landing) invalidates all of them until
  // re-validated, same as editing any other field here does.
  const setIarcResponses = next => {
    setFormData(p => ({ ...p, iarcResponses: next }))
    setDirtyFields(prev => {
      if (prev.has("iarcQuestionnaire") && prev.has("iarcCertId")) return prev
      const nextSet = new Set(prev)
      nextSet.add("iarcQuestionnaire")
      nextSet.add("iarcCertId")
      return nextSet
    })
    setIarcInsights(null)
    setValidationResults(p => {
      if (!p.content_rating?.fields?.iarcQuestionnaire && !p.content_rating?.fields?.iarcCertId) return p
      const { iarcQuestionnaire: _removedQuestionnaire, iarcCertId: _removedCertId, ...restFields } = p.content_rating.fields
      const scores = Object.values(restFields).map(f => f.score)
      const avg = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null
      return { ...p, content_rating: { score: avg, fields: restFields } }
    })
    setOverallApiResult(null)
  }

  // Marks every field that appears in a freshly-computed applyOverallSections() result as
  // "has a real score at least once" — called anywhere a Validate/Regenerate/Generate response
  // actually produces field-level results, regardless of which of those it came from.
  const markFieldsScored = freshResults => {
    setEverScoredFields(prev => {
      const next = new Set(prev)
      Object.values(freshResults || {}).forEach(sec => Object.keys(sec?.fields || {}).forEach(fid => next.add(fid)))
      return next
    })
  }

  // Turns any field's current value into plain text worth copying — lists join with ", ",
  // a local (not-yet-uploaded) File has nothing meaningful to copy, an AI-generated asset
  // that's already a hosted URL string does.
  const fieldValueAsText = id => {
    const val = formData[id]
    if (Array.isArray(val)) return val.join(", ")
    if (typeof val === "string") return val
    if (val instanceof File) return ""
    return val != null ? String(val) : ""
  }

  const copyFieldValue = async id => {
    const text = fieldValueAsText(id)
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
      setCopiedFieldId(id)
      setTimeout(() => setCopiedFieldId(p => (p === id ? null : p)), 1500)
    } catch (e) {
      console.error("Copy failed:", e)
    }
  }

  // A local (not-yet-uploaded) File downloads straight from its own blob URL. An AI-generated
  // asset is a hosted URL string — fetch it into a blob first so the browser saves it with a
  // real filename instead of just navigating to it (a plain `download` attribute is ignored on
  // a cross-origin link). Falls back to opening it in a new tab (save manually) if the fetch
  // fails — e.g. the bucket doesn't allow cross-origin reads.
  const downloadOneAsset = async (val, filenameBase, index) => {
    const suffix = index != null ? `_${index + 1}` : ""
    if (val instanceof File) {
      const a = document.createElement("a")
      a.href = URL.createObjectURL(val)
      a.download = val.name || `${filenameBase}${suffix}.png`
      document.body.appendChild(a); a.click(); a.remove()
      return
    }
    if (typeof val !== "string") return
    try {
      const res = await fetch(val)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const blob = await res.blob()
      const blobUrl = URL.createObjectURL(blob)
      const ext = (blob.type.split("/")[1] || "png").split("+")[0]
      const a = document.createElement("a")
      a.href = blobUrl
      a.download = `${filenameBase}${suffix}.${ext}`
      document.body.appendChild(a); a.click(); a.remove()
      URL.revokeObjectURL(blobUrl)
    } catch (e) {
      // The image host doesn't allow a cross-origin fetch (no CORS headers, confirmed live) —
      // fall back to a plain anchor with `download` set, which still forces a real save for a
      // cross-origin URL in Chrome/Firefox/Edge without needing CORS at all (opening a new tab,
      // as it was doing before, is NOT a real download — only Safari has no frontend-only fix,
      // since it ignores `download` on cross-origin links and just navigates instead).
      console.warn(`Couldn't fetch ${val} for a blob download (likely no CORS on the host) — falling back to a direct download link:`, e)
      const ext = (val.split("?")[0].split(".").pop() || "png").slice(0, 4)
      const a = document.createElement("a")
      a.href = val
      a.download = `${filenameBase}${suffix}.${ext}`
      document.body.appendChild(a); a.click(); a.remove()
    }
  }

  const downloadFieldAsset = async id => {
    const val = formData[id]
    if (!val) return
    const filenameBase = ASSET_FILE_FIELD_MAP[id] || id
    setDownloadingFieldId(id)
    try {
      if (Array.isArray(val)) await Promise.all(val.map((v, i) => downloadOneAsset(v, filenameBase, i)))
      else await downloadOneAsset(val, filenameBase, null)
    } catch (e) {
      console.error(`Download failed for ${id}:`, e)
    } finally {
      setDownloadingFieldId(null)
    }
  }

  const addTag = (fieldId, max) => {
    const v = (tagInputs[fieldId] || "").trim()
    if (!v) return
    const cur = formData[fieldId] || []
    if (cur.includes(v) || cur.length >= max) return
    setField(fieldId, [...cur, v])
    setTagInputs(p => ({ ...p, [fieldId]: "" }))
  }

  const removeTag = (fieldId, tag) => setField(fieldId, formData[fieldId].filter(t => t !== tag))

  const toggleCheck = (fieldId, opt, max) => {
    const cur = formData[fieldId] || []
    if (!cur.includes(opt) && max && cur.length >= max) return
    setField(fieldId, cur.includes(opt) ? cur.filter(o => o !== opt) : [...cur, opt])
  }

  // ── field suggestions ─────────────────────────────────────────────────────

  const setSuggestion = (key, val) => setFieldSuggestions(p => ({ ...p, [key]: val }))

  const applyFieldSuggestion = (field, key) => {
    const suggestion = fieldSuggestions[key]
    if (suggestion === undefined || suggestion === null) return
    if (field.type === "tags") {
      const existing = formData[field.id] || []
      const toAdd = Array.isArray(suggestion) ? suggestion : []
      const merged = [...new Set([...existing, ...toAdd])]
      setField(field.id, field.max ? merged.slice(0, field.max) : merged)
    } else {
      setField(field.id, suggestion)
    }
  }

  // ── shared validation logic ────────────────────────────────────────────────

  // Runs the real validate+correct API for fields wired to Store Validator's endpoints.

  const runFieldCheck = async (field, val) => {
    // Genre is one API field fed by 3 UI dropdowns — re-validating any of them re-checks
    // the combined value and shows the same result on all three.
    if (GENRE_FIELD_IDS.includes(field.id)) {
      const genreText = [formData.primaryGenre, formData.additionalGenre1, formData.additionalGenre2].filter(Boolean).join(", ")
      if (!genreText) return { result: null, suggestion: null }
      const validateData = await storeValidatorApi.validateField("genre", formData.platform, genreText, formData.appId)
      const result = normalizeValidateResponse(validateData)
      let suggestion = null
      try {
        const correctData = await storeValidatorApi.correctField("genre", formData.platform, genreText, formData.appId)
        suggestion = normalizeCorrectResponse(correctData, field)
      } catch (e) {
        console.error("Correct failed for genre:", e)
      }
      return { result, suggestion }
    }

    const apiKey = STORE_VALIDATOR_FIELD_ENDPOINTS[field.id]
    if (apiKey) {
      const text = Array.isArray(val) ? val.join(", ") : (val || "")
      const validateData = await storeValidatorApi.validateField(apiKey, formData.platform, text, formData.appId)
      const result = normalizeValidateResponse(validateData)
      let suggestion = null
      try {
        const correctData = await storeValidatorApi.correctField(apiKey, formData.platform, text, formData.appId)
        suggestion = normalizeCorrectResponse(correctData, field)
      } catch (e) {
        console.error(`Correct failed for ${field.id}:`, e)
      }
      return { result, suggestion }
    }

    const ownerSection = SECTIONS_CONFIG.find(s => s.fields.includes(field))?.id

    // (2026-09) Specs/Details/Content Rating format fields and asset keys each got their own
    // lightweight single-field validate endpoint too (POST /validate/<field_or_asset_key>/) —
    // try that before falling back to the heavier section-scoped group endpoint below. Correct
    // still isn't offered for these — the API 400s correct/ for anything but the 10 text/choice
    // fields already handled above.
    if (ownerSection === "assets") {
      const assetKey = ASSET_FILE_FIELD_MAP[field.id]
      // Only meaningful for a real locally-picked File — an AI-generated asset is a URL
      // string already validated + persisted server-side, nothing to re-upload for it.
      if (assetKey && val instanceof File) {
        try {
          const res = await storeValidatorApi.validateAssetKey(assetKey, formData.platform, formData.appId, val)
          const result = normalizeValidateResponse(res)
          if (result) return { result, suggestion: null }
        } catch (e) {
          console.error(`Asset key validate failed for ${field.id}:`, e)
        }
      }
    } else if (ownerSection) {
      const formatApiKey = FULL_TEXT_FIELD_MAP[field.id] || FULL_LIST_FIELD_MAP[field.id]
      if (formatApiKey) {
        const isListField = (ownerSection === "specs" || ownerSection === "content_rating") && FULL_LIST_FIELD_MAP[field.id]
        const value = isListField ? val : (Array.isArray(val) ? val.join(", ") : val)
        const hasValue = value !== undefined && value !== null && value !== "" && !(Array.isArray(value) && !value.length)
        if (hasValue) {
          try {
            const res = await storeValidatorApi.validateFormatField(formatApiKey, formData.platform, value, formData.appId)
            // Optional + blank returns {skipped: true} rather than a real result — fall
            // through to the group endpoint below instead of reporting a false negative.
            if (!res?.skipped) {
              const result = normalizeValidateResponse(res)
              if (result) return { result, suggestion: null }
            }
          } catch (e) {
            console.error(`Format field validate failed for ${field.id}:`, e)
          }
        }
      }
    }

    // Fallback — no dedicated single-field result above (asset field with no local File yet,
    // an optional format field the API skipped, or the call errored): re-run the whole
    // section-scoped /validate/group/{section}/ (2026-08-26) instead of the full Complete App
    // Validate, so re-checking one field doesn't re-validate the whole app. Only that
    // section's slice of validationResults is touched; overallApiResult is left alone since a
    // group response only speaks for its own section, not the whole app's readiness.
    try {
      const res = ownerSection === "assets"
        ? await storeValidatorApi.validateAssetsGroup(formData.appId, formData.platform, buildFullValidateFiles())
        : await storeValidatorApi.validateGroup(ownerSection, formData.appId, formData.platform, buildGroupPayload(ownerSection))
      const sections = res?.overall?.sections || res?.sections || res || {}
      const freshResults = applyOverallSections(sections)
      if (ownerSection) { setValidationResults(p => ({ ...p, [ownerSection]: freshResults[ownerSection] })); markFieldsScored(freshResults) }
      const freshFieldResult = ownerSection && freshResults[ownerSection]?.fields?.[field.id]
      if (freshFieldResult) return { result: freshFieldResult, suggestion: null }
    } catch (e) {
      console.error(`Group validate failed for ${field.id}:`, e)
    }
    return { result: null, suggestion: null }
  }

  // UI field ids belonging to each group-validate section without dedicated single-field
  // endpoints — used to build the scoped payload for the Re-validate fallback above.
  const GROUP_FIELD_IDS = {
    specs: ["playerModes", "supportedInput", "playArea", "physicalModes", "comfortLevel", "internetConnection", "subscription", "socialFeatures", "supportedLanguages", "earlyAccess"],
    details: ["developer", "publisher", "websiteUrl", "privacyPolicy", "supportUrl", "termsOfService", "whatsNew"],
    content_rating: ["countryOfOrigin", "developerContactEmail", "iarcCertId"],
  }

  // Converts our internal { questionId: [responseId, ...] } map into the exact shape the real
  // IARC validate API expects (per its own answer_rules): a single response_id string for a
  // "radio" question, a list of response_ids for a "checkbox" question. Every key already in
  // iarcResponses is a currently-visible (unlocked) question — IarcCertificateTab prunes any
  // answer whose question gets hidden on every change — so "send only shown questions" already
  // holds without filtering here.
  const buildIarcAnswersPayload = iarcResponses => {
    const payload = {}
    Object.entries(iarcResponses || {}).forEach(([qid, selected]) => {
      const q = iarcSchema.questions[qid]
      if (!q || !Array.isArray(selected) || !selected.length) return
      payload[qid] = q.inputType === "checkbox" ? selected : selected[0]
    })
    return payload
  }

  const buildGroupPayload = group => {
    const payload = {}
    ;(GROUP_FIELD_IDS[group] || []).forEach(uiId => {
      const apiKey = FULL_TEXT_FIELD_MAP[uiId] || FULL_LIST_FIELD_MAP[uiId]
      if (!apiKey) return
      const val = formData[uiId]
      if (val === undefined || val === null || val === "" || (Array.isArray(val) && !val.length)) return
      // Specs and Content Rating Group Validate's documented samples use real JSON arrays
      // for their list fields (player_modes, content_descriptors, etc); every other
      // endpoint in this API wants comma-joined strings.
      if ((group === "specs" || group === "content_rating") && FULL_LIST_FIELD_MAP[uiId]) payload[apiKey] = val
      else payload[apiKey] = Array.isArray(val) ? val.join(", ") : val
    })
    if (group === "content_rating") {
      const iarcAnswers = buildIarcAnswersPayload(formData.iarcResponses)
      if (Object.keys(iarcAnswers).length) payload.iarc_questionnaire = iarcAnswers
    }
    return payload
  }

  // Unlike buildGroupPayload above (which OMITS an empty field so it falls back to whatever's
  // already saved, per the doc), this sends every field in the section explicitly — an empty
  // one as `null` — so the section-scoped "Validate" button always scores exactly what's
  // currently on screen, including a field the user just cleared out.
  const buildSectionValidatePayload = groupId => {
    const cfg = SECTIONS_CONFIG.find(s => s.id === groupId)
    if (!cfg) return {}
    const payload = {}
    const seenApiKeys = new Set()
    cfg.fields.forEach(field => {
      if (field.type === "heading" || field.type === "note") return
      if (ASSET_FILE_FIELD_MAP[field.id]) return // assets validate via their own multipart group call
      const apiKey = FIELD_TO_API_KEY[field.id]
      // The genre trio all collapse onto the one "genre" key — only write it once.
      if (!apiKey || seenApiKeys.has(apiKey)) return
      seenApiKeys.add(apiKey)
      if (apiKey === "genre") {
        const parts = [formData.primaryGenre, formData.additionalGenre1, formData.additionalGenre2].filter(Boolean)
        payload.genre = parts.length ? parts.join(", ") : null
        return
      }
      const raw = formData[field.id]
      const isEmpty = raw === undefined || raw === null || raw === "" || (Array.isArray(raw) && !raw.length)
      if (isEmpty) { payload[apiKey] = null; return }
      const isJsonListField = (groupId === "specs" || groupId === "content_rating") && Array.isArray(raw)
      payload[apiKey] = isJsonListField ? raw : (Array.isArray(raw) ? raw.join(", ") : raw)
    })
    if (groupId === "content_rating") {
      const iarcAnswers = buildIarcAnswersPayload(formData.iarcResponses)
      payload.iarc_questionnaire = Object.keys(iarcAnswers).length ? iarcAnswers : null
    }
    return payload
  }

  const findFieldConfig = id => SECTIONS_CONFIG.flatMap(s => s.fields).find(f => f.id === id)

  // Generic reverse mapping for any Generate response (whole-app or section-scoped) — applies
  // every field it recognizes, validating against each field's real options where it has a
  // fixed list (select/checkboxes), so a stray out-of-list suggestion is silently skipped
  // instead of corrupting the form.
  const applyGeneratedFields = res => {
    const source = res?.listing || res?.app || res?.data || res?.fields || res || {}
    const patch = {}

    Object.entries(FULL_TEXT_FIELD_MAP).forEach(([uiId, apiKey]) => {
      if (!source[apiKey]) return
      const cfg = findFieldConfig(uiId)
      if (cfg?.options && !cfg.options.includes(source[apiKey])) return
      // AI generation isn't bound by the input's own maxLength attribute — enforce the same
      // cap here so a generated field can't land longer than what the form will even show.
      patch[uiId] = cfg?.maxLength ? String(source[apiKey]).slice(0, cfg.maxLength) : source[apiKey]
    })

    Object.entries(FULL_LIST_FIELD_MAP).forEach(([uiId, apiKey]) => {
      if (!source[apiKey]) return
      const cfg = findFieldConfig(uiId)
      const parts = Array.isArray(source[apiKey]) ? source[apiKey] : String(source[apiKey]).split(",").map(s => s.trim()).filter(Boolean)
      const valid = cfg?.options ? parts.filter(p => cfg.options.includes(p)) : parts
      if (valid.length) patch[uiId] = cfg?.max ? valid.slice(0, cfg.max) : valid
    })

    if (source.genre) {
      const genreOptions = findFieldConfig("primaryGenre")?.options
      const parts = Array.isArray(source.genre) ? source.genre : String(source.genre).split(",").map(s => s.trim()).filter(Boolean)
      const valid = parts.filter(p => genreOptions?.includes(p))
      if (valid[0]) patch.primaryGenre = valid[0]
      if (valid[1]) patch.additionalGenre1 = valid[1]
      if (valid[2]) patch.additionalGenre2 = valid[2]
    }

    // Only the content_rating group's own `g` ever has this — every other group's `source`
    // simply won't have the key, so this is a no-op for them. Was missing entirely before,
    // so a whole-app /generate/complete/ run would score the questionnaire (via
    // applyGenerateCompleteScores below) without ever actually filling in its answers.
    const restoredIarc = parseIarcAnswers(source.iarc_questionnaire)
    if (restoredIarc) patch.iarcResponses = restoredIarc

    return patch
  }

  // /generate/complete/ (2026-09) replaces the old per-group parallel generate — one
  // conversational endpoint that may ask clarifying follow-up questions first, then hands
  // back every text section AND real generated asset images in one go once it has enough
  // to work with. A response carrying both `groups` and `overall` is the finished result;
  // anything else is treated as a round of follow-up questions to surface to the user.
  const GENERATE_GROUPS = ["name", "categorization", "specs", "details", "content_rating"]

  // "Regenerate this section" (bottom of each text-based tab — Assets already has its own
  // per-image "Generate with AI" links, so it's not included here) — POST
  // /generate/group/<group>/ redoes just this one section instead of the whole app. Reuses
  // whatever's in the top brief box if there is one; omitted otherwise so the API falls back
  // to this app_id's already-saved context. The response already scores what it generated, so
  // that's applied straight into this section's score grid — no separate Validate needed for
  // it — but the whole-app overall readiness is cleared since it no longer reflects this
  // section's new content.
  const regenerateSection = async groupId => {
    if (anyGenerationInFlight || !formData.appId) return
    const targetAppId = formData.appId
    setRegeneratingSection(groupId)
    trackEvent(ANALYTICS_EVENTS.FEATURE.STORE_VALIDATOR_SECTION_REGENERATED, { section: groupId })
    try {
      if (groupId === "assets") {
        // /generate/group/assets/ is async (real billed image calls) — same start-then-poll
        // shape as the per-image "Generate with AI" flow above, just for the whole group.
        const startRes = await storeValidatorApi.generateAssetsGroup(formData.platform, targetAppId, aiBrief.trim() || undefined)
        console.log("Store Validator regenerate (assets) start response:", startRes)
        const rawResult = startRes?.status === "processing"
          ? await waitForAssetGenerateComplete(() => storeValidatorApi.generateAssetsGroup(formData.platform, targetAppId, undefined, true))
          : startRes
        // Confirmed live (2026-09-17): the final response is wrapped as {app_id, platform,
        // status, keys, result: {...asset_urls, validation}} — waitForAssetGenerateComplete
        // already unwraps `.result` for the polled path, but a same-request synchronous
        // completion (no "processing" step) wouldn't be, so unwrap defensively either way.
        const result = rawResult?.result || rawResult
        console.log("Store Validator regenerate (assets) result:", result)
        // Bail before touching state if the user has since navigated to a different app.
        if (activeAppIdRef.current !== targetAppId) return
        const patch = applyGeneratedAssets(result)
        if (Object.keys(patch).length) setFormData(p => ({ ...p, ...patch }))
        else console.warn("Regenerate (assets) completed but no usable asset_urls — check the response logged above.")
        // Confirmed live (2026-09-17): this response comes back fully validate-scored, same
        // {section, score, verdict, reason, details} shape validate/group/assets/ already uses
        // — apply it via the same path instead of guessing/clearing the score.
        const assetsValidation = result?.validation || result?.assets_group
        if (assetsValidation) {
          const freshResults = applyOverallSections({ assets: assetsValidation })
          if (freshResults.assets) { setValidationResults(p => ({ ...p, assets: freshResults.assets })); markFieldsScored(freshResults) }
        } else {
          console.warn("Regenerate (assets) completed but no usable validation data — clearing the section's score instead.")
          setValidationResults(p => {
            const { assets: _removed, ...rest } = p
            return rest
          })
        }
      } else {
        const startRes = await storeValidatorApi.generateGroup(groupId, formData.platform, targetAppId, aiBrief.trim() || undefined)
        console.log(`Store Validator regenerate (${groupId}) start response:`, startRes)
        // content_rating's generate now also drives the IARC questionnaire's AI fill — a
        // heavier task than this group's other fields — so defensively poll the same way
        // assets does if the backend signals it's still working, instead of assuming every
        // group always answers synchronously.
        const rawResult = startRes?.status === "processing"
          ? await waitForAssetGenerateComplete(() => storeValidatorApi.generateGroup(groupId, formData.platform, targetAppId, undefined, true))
          : startRes
        const res = rawResult?.result || rawResult
        console.log(`Store Validator regenerate (${groupId}) result:`, res)
        if (activeAppIdRef.current !== targetAppId) return
        const patch = applyGeneratedFields(res)
        if (Object.keys(patch).length) setFormData(p => ({ ...p, ...patch }))
        else console.warn(`Regenerate (${groupId}) returned no recognizable fields — check the response shape logged above.`)
        if (res?.sections) {
          const freshResults = applyOverallSections(res.sections)
          if (freshResults[groupId]) { setValidationResults(p => ({ ...p, [groupId]: freshResults[groupId] })); markFieldsScored(freshResults) }
        }
      }
      // Every field in this section just got a fresh AI value — none of them are "changed
      // since last score" anymore, so their individual Regenerate buttons hide again.
      const groupCfg = SECTIONS_CONFIG.find(s => s.id === groupId)
      if (groupCfg) setDirtyFields(prev => { const next = new Set(prev); groupCfg.fields.forEach(f => next.delete(f.id)); return next })
      setOverallApiResult(null)
    } catch (e) {
      console.error(`Regenerate (${groupId}) failed:`, e)
    } finally {
      setRegeneratingSection(null)
    }
  }

  // AI-fills the IARC questionnaire (POST /generate/iarc_questionnaire/) — network call + the
  // shared generation lock live here like every other generate flow; the actual response
  // shape isn't confirmed yet (this endpoint still 404s server-side as of 2026-10), so this
  // just returns the raw parsed body and logs it, leaving IarcCertificateTab (which already
  // owns the schema) to interpret and merge it defensively. Polls the same way content_rating's
  // own group generate now does, in case answering all 229 questions takes long enough that
  // the backend responds async once this endpoint is actually working.
  const generateIarcQuestionnaire = async () => {
    if (anyGenerationInFlight || !formData.appId) return null
    const targetAppId = formData.appId
    setGeneratingIarc(true)
    try {
      const startRes = await storeValidatorApi.generateIarcQuestionnaire(formData.platform, targetAppId)
      console.log("Store Validator generate (iarc_questionnaire) start response:", startRes)
      const rawResult = startRes?.status === "processing"
        ? await waitForAssetGenerateComplete(() => storeValidatorApi.generateIarcQuestionnaire(formData.platform, targetAppId, true))
        : startRes
      const res = rawResult?.result || rawResult
      console.log("Store Validator generate (iarc_questionnaire) response:", res)
      if (activeAppIdRef.current !== targetAppId) return null
      return res
    } catch (e) {
      console.error("Generate (iarc_questionnaire) failed:", e)
      return null
    } finally {
      setGeneratingIarc(false)
    }
  }

  // Scores the IARC questionnaire's current answers (POST /validate/iarc_questionnaire/,
  // confirmed request/response shape 2026-10) — same shared generation lock as every other
  // validate/generate flow. The response is one field-style score/verdict/reason, not a
  // group's set of per-field results, so it's normalized through the same
  // normalizeValidateResponse() used for every other single-field check and stored under
  // "iarcQuestionnaire" — its own section (confirmed via the real /generate/complete/ response,
  // which scores it separately from iarc_certificate_id, not folded into it).
  const validateIarcQuestionnaire = async () => {
    // Deliberately NOT gated on the full anyGenerationInFlight — unlike every generate call,
    // this hits a validate endpoint, which the backend's shared per-app_id generation lock
    // doesn't cover (the main per-section Validate button isn't gated on it either). Background
    // asset generation specifically (assetsGenerating) can run for minutes after a whole-app
    // Generate finishes with no visible indicator — gating on the full flag was locking this
    // button out the whole time, contradicting pollAssetsInBackground's own "form stays usable"
    // intent. Still guards against racing the IARC questionnaire's own Generate with AI.
    if (generatingIarc || validatingIarc || !formData.appId) return
    const targetAppId = formData.appId
    setValidatingIarc(true)
    try {
      const answers = buildIarcAnswersPayload(formData.iarcResponses)
      const res = await storeValidatorApi.validateIarcQuestionnaire(formData.platform, targetAppId, answers)
      console.log("Store Validator validate (iarc_questionnaire) response:", res)
      if (activeAppIdRef.current !== targetAppId) return
      const result = normalizeValidateResponse(res)
      if (!result) return
      setValidationResults(p => ({
        ...p,
        content_rating: { ...p.content_rating, fields: { ...p.content_rating?.fields, iarcQuestionnaire: result } },
      }))
      setEverScoredFields(prev => (prev.has("iarcQuestionnaire") ? prev : new Set(prev).add("iarcQuestionnaire")))
      setIarcInsights(extractIarcInsights(res))
    } catch (e) {
      console.error("Validate (iarc_questionnaire) failed:", e)
    } finally {
      setValidatingIarc(false)
    }
  }

  // Persists the IARC questionnaire's answers right away, without waiting for the user to
  // press "Save answers" — takes the just-computed answers directly (rather than reading
  // formData.iarcResponses) since this runs immediately after an AI-generate merge, before
  // that state update has actually landed. A lightweight sibling of saveApp()/buildSafeSavePayload
  // scoped to only this one field, since it's the only content_rating value confirmed safe to
  // send to /draft/ so far.
  const saveIarcAnswers = async answers => {
    if (!formData.appId) return
    const iarcAnswers = buildIarcAnswersPayload(answers)
    if (!Object.keys(iarcAnswers).length) return
    try {
      const draft = await storeValidatorApi.saveDraft(formData.appId, formData.platform, {
        title: formData.appName || "My App",
        iarc_questionnaire: iarcAnswers,
      })
      applyRecordInsights(draft)
    } catch (e) {
      console.error("Auto-save (iarc_questionnaire) failed:", e)
    }
  }

  // Per-field regenerate (confirmed doc) — POST /generate/<api_key>/ covers every text/choice
  // AND format field individually. Lighter than regenerating the whole section when only one
  // field needs a redo. Not async (no processing/poll), unlike whole-app or asset generation.
  const regenerateField = async uiFieldId => {
    const apiKey = FIELD_TO_API_KEY[uiFieldId]
    if (!apiKey || anyGenerationInFlight || !formData.appId) return
    const targetAppId = formData.appId
    setRegeneratingField(uiFieldId)
    try {
      const res = await storeValidatorApi.generateField(apiKey, formData.platform, targetAppId, aiBrief.trim() || undefined)
      console.log(`Store Validator regenerate field (${apiKey}) response:`, res)
      if (activeAppIdRef.current !== targetAppId) return

      const isTextChoice = TEXT_CHOICE_API_KEYS.has(apiKey)
      const rawValue = isTextChoice ? res?.text : res?.value
      if (rawValue == null) {
        console.warn(`Regenerate field (${apiKey}) returned no usable text/value — check the response logged above.`)
        return
      }

      const isGenreSlot = uiFieldId === "primaryGenre" || uiFieldId === "additionalGenre1" || uiFieldId === "additionalGenre2"
      // The "genre" API key always drives all 3 genre dropdowns together, regardless of which
      // one's own Regenerate button was clicked — same as everywhere else this app treats genre.
      const resultFieldId = isGenreSlot ? "primaryGenre" : uiFieldId

      if (isGenreSlot) {
        const genreOptions = findFieldConfig("primaryGenre")?.options
        const parts = String(rawValue).split(",").map(s => s.trim()).filter(Boolean).filter(p => genreOptions?.includes(p))
        if (!parts.length) {
          console.warn(`Regenerate field (genre) returned no options matching the fixed genre list — check the response logged above.`)
          return
        }
        setFormData(p => ({
          ...p,
          primaryGenre: parts[0] || p.primaryGenre,
          additionalGenre1: parts[1] || p.additionalGenre1,
          additionalGenre2: parts[2] || p.additionalGenre2,
        }))
      } else {
        const cfg = findFieldConfig(uiFieldId)
        let finalVal = rawValue
        if (cfg?.type === "tags" || cfg?.type === "checkboxes") {
          const parts = Array.isArray(rawValue) ? rawValue : String(rawValue).split(",").map(s => s.trim()).filter(Boolean)
          const valid = cfg.options ? parts.filter(p => cfg.options.includes(p)) : parts
          if (!valid.length) {
            console.warn(`Regenerate field (${apiKey}) returned no options matching this field's allowed list — check the response logged above.`)
            return
          }
          finalVal = cfg.max ? valid.slice(0, cfg.max) : valid
        } else if (cfg?.options && !cfg.options.includes(rawValue)) {
          console.warn(`Regenerate field (${apiKey}) returned "${rawValue}", not one of this field's allowed options — skipped.`)
          return
        } else if (cfg?.maxLength && typeof finalVal === "string") {
          finalVal = finalVal.slice(0, cfg.maxLength)
        }
        setFormData(p => ({ ...p, [uiFieldId]: finalVal }))
      }

      const sectionId = SECTIONS_CONFIG.find(s => s.fields.some(f => f.id === resultFieldId))?.id
      const normalized = normalizeValidateResponse(res)
      if (sectionId && normalized) {
        // is_example means the value is an AI-invented placeholder (fake developer name/email/
        // URL, say) that must be replaced before submitting, not a real usable pick.
        if (res?.is_example) normalized.feedback = `AI-invented placeholder — replace before submitting. ${normalized.feedback}`.trim()
        setValidationResults(p => {
          const fields = { ...(p[sectionId]?.fields || {}), [resultFieldId]: normalized }
          const scores = Object.values(fields).map(f => f.score)
          const avg = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null
          return { ...p, [sectionId]: { score: avg, fields } }
        })
        setEverScoredFields(prev => (prev.has(resultFieldId) ? prev : new Set(prev).add(resultFieldId)))
      }
      // This field just got a fresh AI value — no longer "changed since last score", so its
      // own Regenerate button hides again (all 3 genre dropdowns move together).
      setDirtyFields(prev => {
        const next = new Set(prev)
        if (isGenreSlot) { next.delete("primaryGenre"); next.delete("additionalGenre1"); next.delete("additionalGenre2") }
        else next.delete(uiFieldId)
        return next
      })
      setOverallApiResult(null)
    } catch (e) {
      console.error(`Regenerate field (${apiKey}) failed:`, e)
    } finally {
      setRegeneratingField(null)
    }
  }

  // Confirmed shape (2026-09-09): { question, options: [...], recommended } — these are
  // curated multiple-choice questions, not open free text (options may still be empty for a
  // future open-ended question, so free text stays as a fallback in the UI below).
  const extractFollowUpQuestions = res => {
    const raw = res?.questions || res?.pending_questions || res?.follow_up_questions || res?.clarifying_questions || res?.next_questions || []
    return raw.map((q, i) => (
      typeof q === "string"
        ? { id: `q${i}`, text: q, options: [], recommended: null }
        : {
            id: q.id ?? q.key ?? `q${i}`,
            text: q.question ?? q.text ?? q.prompt ?? String(q),
            options: Array.isArray(q.options) ? q.options : [],
            recommended: q.recommended ?? null,
          }
    ))
  }

  const applyGenerateCompleteResult = (res, targetAppId) => {
    if (activeAppIdRef.current !== targetAppId) return
    let patch = {}
    GENERATE_GROUPS.forEach(group => {
      const g = res.groups?.[group]
      if (g) patch = { ...patch, ...applyGeneratedFields(g) }
    })
    if (res.groups?.assets) patch = { ...patch, ...applyGeneratedAssets(res.groups.assets) }
    if (Object.keys(patch).length) setFormData(p => ({ ...p, ...patch }))
  }

  // generate/complete already hands back a full score/verdict/reason per field, nested per
  // group (groups.<group>.sections.<field>) instead of flat like Complete App Validate's
  // overall.sections — flatten every group's sections into one object so applyOverallSections
  // (defined below) can turn it into the same validationResults shape either flow uses, so the
  // score grid lights up immediately instead of needing a separate "Validate" click after
  // every generate. overallApiResult is deliberately left untouched: generate's `overall` has
  // no `ready_to_publish` (unlike Complete App Validate's), so setting it here would wrongly
  // gate Submit to false — canSubmit's own score>=60 fallback covers this instead, and it now
  // has real per-section scores to work with.
  const applyGenerateCompleteScores = (res, targetAppId) => {
    if (activeAppIdRef.current !== targetAppId) return
    let flatSections = {}
    GENERATE_GROUPS.forEach(group => {
      const g = res.groups?.[group]
      if (g?.sections) flatSections = { ...flatSections, ...g.sections }
    })
    if (res.groups?.assets?.validation) flatSections.assets = res.groups.assets.validation
    if (Object.keys(flatSections).length) {
      const freshResults = applyOverallSections(flatSections)
      setValidationResults(p => ({ ...p, ...freshResults }))
      markFieldsScored(freshResults)
      const insights = extractIarcInsights(flatSections.iarc_questionnaire)
      if (insights) setIarcInsights(insights)
    }
  }

  // Generation runs in a background thread — either call below may respond with just
  // {status: "processing"} (a full run takes ~2-3 minutes, dominated by assets' billed image
  // calls). Per the backend engineer (2026-09-11), poll by re-POSTing /generate/complete/
  // with just platform + app_id until status is "complete" or "failed", so the UI just shows
  // one continuous "Generating…" spinner across the wait.
  const GENERATE_POLL_INTERVAL_MS = 3000
  const GENERATE_POLL_MAX_ATTEMPTS = 100 // ~5 minutes — headroom over the documented ~2-3min

  const waitForGenerateComplete = async res => {
    let attempts = 0
    while (res?.status === "processing" && attempts < GENERATE_POLL_MAX_ATTEMPTS) {
      await new Promise(r => setTimeout(r, GENERATE_POLL_INTERVAL_MS))
      res = await storeValidatorApi.getGenerateCompleteStatus(formData.platform, formData.appId)
      console.log("Store Validator generate/complete poll response:", res)
      if (res?.status === "failed") throw new Error(res?.error || "Generation failed.")
      attempts += 1
    }
    if (res?.status === "processing") throw new Error("Timed out waiting for generation to finish.")
    return res
  }

  // Confirmed live (2026-09-16): status moves processing -> assets_processing -> complete.
  // At assets_processing, every text/spec group is already done and scored — only the billed
  // image calls are still running — so apply that data immediately instead of blocking the
  // whole form on the (much slower) asset generation, and keep polling for assets separately
  // in the background via its own assetsGenerating flag.
  const pollAssetsInBackground = async targetAppId => {
    setAssetsGenerating(true)
    try {
      let res = await storeValidatorApi.getGenerateCompleteStatus(formData.platform, formData.appId)
      let attempts = 0
      while (res?.status === "processing" || res?.status === "assets_processing") {
        if (res?.status === "failed") throw new Error(res?.error || "Generation failed.")
        await new Promise(r => setTimeout(r, GENERATE_POLL_INTERVAL_MS))
        res = await storeValidatorApi.getGenerateCompleteStatus(formData.platform, formData.appId)
        console.log("Store Validator generate/complete (assets) poll response:", res)
        attempts += 1
        if (attempts >= GENERATE_POLL_MAX_ATTEMPTS) throw new Error("Timed out waiting for assets to finish.")
      }
      if (res?.status === "failed") throw new Error(res?.error || "Generation failed.")
      if (res?.groups) {
        applyGenerateCompleteResult(res, targetAppId)
        applyGenerateCompleteScores(res, targetAppId)
      } else {
        console.warn("Background asset generation completed with no groups — check the response logged above.")
      }
    } catch (e) {
      console.error("Background asset generation failed:", e)
    } finally {
      setAssetsGenerating(false)
    }
  }

  // Shared by the initial brief call and every answered-round call — either one lands here
  // with one of four shapes: fully done (`groups` + `overall`), text ready but assets still
  // cooking (`status: "assets_processing"`), another clarification round (`questions`), or
  // still running (`status: "processing"`, handled by polling above).
  const handleGenerateCompleteResponse = async (res, targetAppId) => {
    console.log("Store Validator generate/complete response:", res)
    res = await waitForGenerateComplete(res)
    if (res?.groups && res?.overall) {
      applyGenerateCompleteResult(res, targetAppId)
      applyGenerateCompleteScores(res, targetAppId)
      setConversation({ pending: [], answersDraft: {} })
      // Only if they're still looking at this app — if they've navigated elsewhere, this app's
      // own state will be correctly re-derived (or re-fetched) when reopened.
      if (activeAppIdRef.current === targetAppId) { setHasGeneratedOnce(true); setDirtyFields(new Set()) }
      return
    }
    if (res?.status === "assets_processing" && res?.groups) {
      applyGenerateCompleteResult(res, targetAppId)
      applyGenerateCompleteScores(res, targetAppId)
      setConversation({ pending: [], answersDraft: {} })
      if (activeAppIdRef.current === targetAppId) { setHasGeneratedOnce(true); setDirtyFields(new Set()) }
      pollAssetsInBackground(targetAppId) // not awaited — runs independently so the form stays usable
      return
    }
    const questions = extractFollowUpQuestions(res)
    if (!questions.length) {
      console.warn("generate/complete returned neither `groups` nor recognizable follow-up questions — check the response shape logged above.")
      return
    }
    setConversation({
      pending: questions,
      // Pre-fill each answer with the API's recommended pick so "Continue" works immediately
      // without forcing the user to touch every question.
      answersDraft: Object.fromEntries(questions.map(q => [q.id, q.recommended || ""])),
    })
  }

  const generateFromBrief = async () => {
    if (!aiBrief.trim() || !formData.appId || anyGenerationInFlight || hasGeneratedOnce) return
    const targetAppId = formData.appId
    setGenerating(true)
    try {
      const res = await storeValidatorApi.generateComplete(formData.platform, targetAppId, aiBrief.trim())
      await handleGenerateCompleteResponse(res, targetAppId)
    } catch (e) {
      console.error("Generate (complete) failed:", e)
    } finally {
      setGenerating(false)
    }
  }

  // Confirmed shape (2026-09-09): the continue call takes `answers` only — a flat array of
  // answer strings positioned to match the `questions` array from the round being answered
  // (no `brief`, the API rejects a request carrying both).
  const answerFollowUps = async () => {
    if (anyGenerationInFlight) return
    const targetAppId = formData.appId
    const answers = conversation.pending.map(q => (conversation.answersDraft?.[q.id] ?? q.recommended ?? "").trim())
    setGenerating(true)
    try {
      const res = await storeValidatorApi.continueGenerateComplete(formData.platform, targetAppId, answers)
      await handleGenerateCompleteResponse(res, targetAppId)
    } catch (e) {
      console.error("Generate (complete) follow-up failed:", e)
      // Confirmed (2026-09-09): the API 400s with this exact message once the pending
      // clarification round is gone (already answered, expired, or never started for this
      // app_id) — reset back to the plain brief box instead of leaving a dead "Continue"
      // button pointed at questions the backend no longer recognizes.
      if (/no pending clarifying questions/i.test(e.message || "")) {
        setConversation({ pending: [], answersDraft: {} })
      }
    } finally {
      setGenerating(false)
    }
  }

  // ── AI asset image generation (2026-09 doc) — POST /generate/<asset_key>/ and
  //    /generate/group/assets/ are async when app_id is given: both return 202
  //    {"status": "processing"} immediately. Confirmed live (2026-09-16): there is no separate
  //    status endpoint in use — same self-poll pattern as /generate/complete/, just re-POST the
  //    very same generate call (asset key or the whole group) with no brief — e.g. re-posting
  //    /generate/square_cover/ returns {"platform":"meta","section":"square_cover","status":
  //    "processing"} — until status stops being "processing". `brief` is optional on the
  //    starting call — omitted, the API falls back to this app_id's saved listing details, or
  //    invents a direction from nothing. ─────────
  const ASSET_GENERATE_POLL_INTERVAL_MS = 30000
  const ASSET_GENERATE_POLL_MAX_ATTEMPTS = 6 // ~3 minutes

  // Named for assets (its original use), but generic over any pollFn — also reused by
  // content_rating's own group regenerate and the dedicated IARC generate, both of which can
  // now drive the same kind of heavier AI work assets does.
  const waitForAssetGenerateComplete = async pollFn => {
    let attempts = 0
    let res = { status: "processing" }
    // Always wait before the *first* poll too, not just between retries — re-POSTing the same
    // lock-guarded generate endpoint immediately after its own kickoff call races the backend's
    // job/lock bookkeeping and comes back as "already in progress" instead of a status. Giving
    // it one full interval first (same as the /generate/complete/ poller) avoids that.
    while (res?.status === "processing" && attempts < ASSET_GENERATE_POLL_MAX_ATTEMPTS) {
      await new Promise(r => setTimeout(r, ASSET_GENERATE_POLL_INTERVAL_MS))
      try {
        res = await pollFn()
      } catch (e) {
        // A poll landing exactly as the backend's own lock bookkeeping is still settling can
        // still spuriously 409 with this message even after the delay above — treat it as
        // "still processing" and just try again next interval instead of failing the wait.
        if (!/already in progress/i.test(e.message || "")) throw e
        res = { status: "processing" }
      }
      console.log("Store Validator generate assets poll response:", res)
      attempts += 1
    }
    if (res?.status === "processing") throw new Error("Timed out waiting for asset generation to finish.")
    if (res?.status === "failed") throw new Error(res?.error || "Asset generation failed.")
    return res?.result || res
  }

  const generateAssetField = async field => {
    const assetKey = ASSET_FILE_FIELD_MAP[field.id]
    if (!assetKey || anyGenerationInFlight) return
    const targetAppId = formData.appId
    const brief = assetPromptOpenFor === field.id ? assetPromptDraft.trim() : ""
    setGeneratingAssetKey(field.id)
    try {
      const startRes = await storeValidatorApi.generateAssetKey(assetKey, formData.platform, targetAppId, brief || undefined)
      console.log(`Store Validator generate asset (${assetKey}) start response:`, startRes)
      const rawResult = startRes?.status === "processing"
        ? await waitForAssetGenerateComplete(() => storeValidatorApi.generateAssetKey(assetKey, formData.platform, targetAppId, undefined, true))
        : startRes
      // Confirmed live (2026-09-17): the final response is wrapped as {app_id, platform,
      // status, keys, result: {...asset_urls, validation}} — waitForAssetGenerateComplete
      // already unwraps `.result` for the polled path, but a same-request synchronous
      // completion (no "processing" step) wouldn't be, so unwrap defensively either way.
      const result = rawResult?.result || rawResult
      console.log(`Store Validator generate asset (${assetKey}) result:`, result)
      // Bail before touching state if the user has since navigated to a different app — this
      // result belongs to targetAppId, not whatever's on screen now.
      if (activeAppIdRef.current !== targetAppId) return
      const urls = result?.asset_urls || {}
      if (assetKey === "screenshots") {
        if (Array.isArray(urls.screenshots) && urls.screenshots.length) setFormData(p => ({ ...p, screenshots: urls.screenshots }))
        else console.warn("Generate asset (screenshots) completed but no usable asset_urls.screenshots — check the response logged above.")
      } else if (urls[assetKey]) {
        setFormData(p => ({ ...p, [field.id]: urls[assetKey] }))
      } else {
        console.warn(`Generate asset (${assetKey}) completed but no usable asset_urls.${assetKey} — check the response logged above.`)
      }
      // Confirmed live (2026-09-16): this response also carries a fresh assets validation, but
      // it's shaped like a FULL assets-group validate (scores every asset key, not just this
      // one). Running it through applyOverallSections() the way regenerateSection's assets
      // branch does would fabricate a fail/0 score for every OTHER asset field that has no
      // value yet, even though generating just this one field never touched them — so only
      // this field's own entry gets pulled out and merged in, the same "drop/update just this
      // field's score, leave every other one alone" principle setField already uses.
      const assetsValidation = result?.validation || result?.assets_group
      if (assetsValidation) {
        const detail = Array.isArray(assetsValidation.details)
          ? assetsValidation.details.find(d => ASSET_DETAIL_FIELD_MAP[d.asset] === field.id)
          : null
        let fieldResult = null
        if (detail) {
          const passed = detail.passed ?? 0
          const total = detail.total || 1
          const score = Math.round((passed / total) * 100)
          const status = passed >= total ? "pass" : passed === 0 ? "fail" : "warning"
          const failedChecks = (detail.checks || []).filter(c => !c.ok).map(c => humanizeAssetCheck(c, field.label))
          fieldResult = { score, status, feedback: failedChecks.join(" ") || "Looks good." }
        } else {
          const umbrella = normalizeValidateResponse(assetsValidation)
          if (umbrella) fieldResult = umbrella
        }
        if (fieldResult) {
          setValidationResults(p => {
            const nextFields = { ...(p.assets?.fields || {}), [field.id]: fieldResult }
            const avg = computeAssetsSectionScore(nextFields, formData)
            return { ...p, assets: { score: avg, fields: nextFields } }
          })
          setEverScoredFields(prev => (prev.has(field.id) ? prev : new Set(prev).add(field.id)))
        }
      }
      setOverallApiResult(null)
      setAssetPromptOpenFor(null)
      setAssetPromptDraft("")
    } catch (e) {
      console.error(`Generate asset (${assetKey}) failed:`, e)
    } finally {
      setGeneratingAssetKey(null)
    }
  }


  // Confirmed doc (2026-10, validate/group/assets/): every key here is a real file upload —
  // there's no field shape for passing an already-hosted URL instead. "Skips saving entirely
  // (no upsert) when no files are attached -- a check-only call never overwrites a previously-
  // good assets score with one computed against nothing" is the one documented behavior for an
  // omitted/empty payload, so an AI-generated asset (already a URL, not a File) is simply left
  // out rather than invented into a fake upload — per that same doc, not resent as a re-fetched
  // file.
  const buildFullValidateFiles = () => {
    const files = {}
    Object.entries(ASSET_FILE_FIELD_MAP).forEach(([uiId, apiKey]) => {
      if (uiId === "screenshots") return
      if (formData[uiId] instanceof File) files[apiKey] = formData[uiId]
    })
    const uploadedScreenshots = (formData.screenshots || []).filter(f => f instanceof File)
    if (uploadedScreenshots.length) files.screenshots = uploadedScreenshots
    return files
  }

  // Every currently-set asset image, in the same order they appear in the Assets tab — lets
  // the lightbox step through "the rest of the section" instead of only showing whichever one
  // was clicked.
  const buildAssetGallery = () => {
    const list = []
    const pushField = (fieldId, label) => {
      const val = formData[fieldId]
      if (val) list.push({ key: fieldId, src: assetSrc(val), label })
    }
    pushField("landscapeCover", "Landscape cover")
    pushField("squareCover", "Square cover")
    pushField("heroCover", "Hero cover")
    pushField("headerCapsule", "Header capsule")
    pushField("appIcon", "Icon")
    pushField("appLogo", "Logo")
    ;(formData.screenshots || []).forEach((f, i) => list.push({ key: `screenshots:${i}`, src: assetSrc(f), label: `Screenshot ${i + 1}` }))
    return list
  }

  const goToPreviewImage = delta => {
    const gallery = buildAssetGallery()
    if (!gallery.length) return
    const idx = gallery.findIndex(g => g.key === previewImageKey)
    const nextIdx = ((idx === -1 ? 0 : idx) + delta + gallery.length) % gallery.length
    setPreviewImageKey(gallery[nextIdx].key)
  }

  // Spreads only scores returned by Complete App Validate across the UI fields.
  const applyOverallSections = sections => {
    const perSection = Object.fromEntries(SECTIONS_CONFIG.map(s => [s.id, {}]))

    Object.entries(OVERALL_SECTION_FIELD_MAP).forEach(([apiKey, { section, field }]) => {
      const apiSection = sections[apiKey]
      if (apiSection) perSection[section][field] = normalizeValidateResponse(apiSection)
    })

    const assetsSection = sections.assets
    if (assetsSection) {
      const assetFields = SECTIONS_CONFIG.find(s => s.id === "assets").fields
      const seenAssetFields = new Set()
      if (Array.isArray(assetsSection.details)) {
        assetsSection.details.forEach(d => {
          const fieldId = ASSET_DETAIL_FIELD_MAP[d.asset]
          if (!fieldId) return
          const fieldLabel = assetFields.find(f => f.id === fieldId)?.label || fieldId
          const passed = d.passed ?? 0
          const total = d.total || 1
          const score = Math.round((passed / total) * 100)
          const status = passed >= total ? "pass" : passed === 0 ? "fail" : "warning"
          const failedChecks = (d.checks || []).filter(c => !c.ok).map(c => humanizeAssetCheck(c, fieldLabel))
          perSection.assets[fieldId] = { score, status, feedback: failedChecks.join(" ") || "Looks good." }
          seenAssetFields.add(fieldId)
        })
      }
      // Optional assets with no specific check in `details[]` (Square cover, Logo, Header
      // capsule, Trailer, ...) shouldn't inherit the umbrella's raw multi-clause "Failed
      // checks: ..." dump — that's not about them. Give each a plain message of its own.
      const umbrella = normalizeValidateResponse(assetsSection)
      assetFields
        .filter(f => f.type !== "heading" && !seenAssetFields.has(f.id))
        .forEach(f => {
          const hasValue = Array.isArray(formData[f.id]) ? formData[f.id].length > 0 : !!formData[f.id]
          perSection.assets[f.id] = {
            score: umbrella?.score ?? (hasValue ? 100 : 0),
            status: umbrella?.status ?? (hasValue ? "pass" : "fail"),
            feedback: hasValue ? "Looks good." : f.required ? `${f.label} hasn't been uploaded yet.` : "Optional — no issues found.",
          }
        })
    }

    return Object.fromEntries(Object.entries(perSection).map(([sectionId, fields]) => {
      const scores = Object.values(fields).map(r => r.score)
      const avg = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null
      return [sectionId, { score: avg, fields }]
    }).filter(([, result]) => Object.keys(result.fields).length > 0))
  }

  // Validates whichever section the user currently has open — the group-scoped endpoint
  // (POST /validate/group/<group>/), not the whole app. Every field in this section is sent
  // explicitly, with an empty one as `null`, so a field the user just cleared out is scored as
  // genuinely blank instead of silently falling back to whatever's still saved from before.
  const validate = async () => {
    if (!formData.appId) return
    const groupId = activeSection
    const groupCfg = SECTIONS_CONFIG.find(s => s.id === groupId)
    setValidating(true)
    setValidateError(null)
    try {
      const res = groupId === "assets"
        ? await storeValidatorApi.validateAssetsGroup(formData.appId, formData.platform, buildFullValidateFiles())
        : await storeValidatorApi.validateGroup(groupId, formData.appId, formData.platform, buildSectionValidatePayload(groupId))
      console.log(`Store Validator validate (${groupId}) response:`, res)
      trackEvent(ANALYTICS_EVENTS.FEATURE.STORE_VALIDATOR_RUN, {
        app_id: formData.appId, platform: formData.platform, app_name: formData.appName, section: groupId,
      })
      const sections = res?.overall?.sections || res?.sections || res || {}
      const freshResults = applyOverallSections(sections)
      if (freshResults[groupId]) { setValidationResults(p => ({ ...p, [groupId]: freshResults[groupId] })); markFieldsScored(freshResults) }
      // A section-scoped validate doesn't speak for the whole app's readiness — leave
      // overallApiResult alone, same as every other section-scoped call in this file.
      if (groupCfg) setDirtyFields(prev => { const next = new Set(prev); groupCfg.fields.forEach(f => next.delete(f.id)); return next })

      // Populate ready-to-apply suggestions for this section's fields with a real correct/ endpoint.
      const newSuggestions = {}
      await Promise.all((groupCfg?.fields || []).map(async field => {
        const apiKey = STORE_VALIDATOR_FIELD_ENDPOINTS[field.id]
        if (!apiKey) return
        const key = `${groupId}.${field.id}`
        if (fieldSuggestions[key] !== undefined) return
        const val = formData[field.id]
        const text = Array.isArray(val) ? val.join(", ") : (val || "")
        if (!text) return
        try {
          const correctData = await storeValidatorApi.correctField(apiKey, formData.platform, text, formData.appId)
          const suggestion = normalizeCorrectResponse(correctData, field)
          if (suggestion !== null) newSuggestions[key] = suggestion
        } catch (e) {
          console.error(`Correct failed for ${field.id}:`, e)
        }
      }))
      setFieldSuggestions(p => ({ ...p, ...newSuggestions }))
    } catch (e) {
      console.error(`Validate (${groupId}) failed:`, e)
      // A 413 here means the assets multipart request (every current local image bundled into
      // one call) was rejected outright by the server/proxy for being too large — previously
      // this just failed silently with nothing shown in the UI at all.
      setValidateError(
        e.status === 413
          ? "These images are too large to validate together — try using smaller image files, or validate fewer at a time."
          : `Validate failed: ${e.message || "something went wrong"}`
      )
    } finally {
      setValidating(false)
    }
  }

  // ── per-field validate ─────────────────────────────────────────────────────

  const validateField = async (sectionId, field) => {
    if (validatingFields.has(field.id)) return
    setValidatingFields(p => new Set([...p, field.id]))
    trackEvent(ANALYTICS_EVENTS.FEATURE.STORE_VALIDATOR_FIELD_REVALIDATED, { section: sectionId, field: field.id })

    const val = formData[field.id]
    try {
      const { result, suggestion } = await runFieldCheck(field, val)
      if (!result) return
      const key = `${sectionId}.${field.id}`
      if (suggestion !== null && suggestion !== undefined) setFieldSuggestions(p => ({ ...p, [key]: suggestion }))

      setValidationResults(p => {
        const existingFields = { ...(p[sectionId]?.fields || {}), [field.id]: result }
        const scores = Object.values(existingFields).map(r => r.score)
        const avg    = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)
        return { ...p, [sectionId]: { ...p[sectionId], fields: existingFields, score: avg } }
      })
      // Freshly scored against its current value — no longer "changed since last score".
      setDirtyFields(prev => { if (!prev.has(field.id)) return prev; const next = new Set(prev); next.delete(field.id); return next })
      setEverScoredFields(prev => (prev.has(field.id) ? prev : new Set(prev).add(field.id)))
    } catch (e) {
      console.error(`Validate failed for ${field.id}:`, e)
    } finally {
      setValidatingFields(p => { const n = new Set(p); n.delete(field.id); return n })
    }
  }

  // ── landing step ───────────────────────────────────────────────────────────

  const gridCols = "36px 44px minmax(0,2fr) 1fr 32px 70px 110px 100px 140px"

  if (step === "landing") return (
    <div style={{ minHeight: "100vh", paddingTop: "70px", background: "#171821" }}>
      <style>{RESPONSIVE_CSS}</style>
      <SEO title="Store Validator — KeyQuest" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={{ width: "100%", padding: "40px 32px 60px" }}>

        {/* ── Header ── */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "28px", gap: "16px", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <div style={{ width: "52px", height: "52px", borderRadius: "14px", background: "#F6255A18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Shield size={24} color="#F6255A" />
            </div>
            <div>
              <h1 style={{ color: "#fff", fontSize: "22px", fontWeight: 700, margin: "0 0 4px" }}>Store Validator</h1>
              <p style={{ color: "#6b7280", fontSize: "13px", margin: 0, lineHeight: 1.5, maxWidth: "520px" }}>
                Validate your Meta Quest store listing before publishing — get AI feedback on every metadata section.
              </p>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
            {platforms.length > 1 && (
              <div style={{ position: "relative" }}>
                <button
                  type="button"
                  onClick={() => setOpenDropdownId(openDropdownId === "platform" ? null : "platform")}
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", minWidth: "170px", background: "#21222D", border: `1px solid ${openDropdownId === "platform" ? "#F6255A55" : "#2E3244"}`, color: "#d1d5db", borderRadius: "8px", padding: "10px 12px", fontSize: "13px", cursor: "pointer" }}
                >
                  <span>{platformLabel(selectedPlatform)}</span>
                  <ChevronDown size={14} style={{ flexShrink: 0, color: "#6b7280", transition: "transform 0.2s", transform: openDropdownId === "platform" ? "rotate(180deg)" : "rotate(0deg)" }} />
                </button>
                <AnimatePresence>
                  {openDropdownId === "platform" && (
                    <React.Fragment>
                      <div onClick={() => setOpenDropdownId(null)} style={{ position: "fixed", inset: 0, zIndex: 30 }} />
                      <motion.div
                        initial={{ opacity: 0, y: -6, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -6, scale: 0.98 }}
                        transition={{ duration: 0.15, ease: "easeOut" }} style={dropdownPanelStyle}
                      >
                        {platforms.map(p => {
                          const selected = p === selectedPlatform
                          const comingSoon = p !== PLATFORM
                          return (
                            <button
                              type="button" key={p}
                              disabled={comingSoon}
                              onClick={() => { if (comingSoon) return; setSelectedPlatform(p); setOpenDropdownId(null); trackEvent(ANALYTICS_EVENTS.FEATURE.STORE_VALIDATOR_PLATFORM_SELECTED, { platform: p }) }}
                              style={{ ...dropdownOptionStyle(selected), ...(comingSoon ? { cursor: "not-allowed", opacity: 0.6 } : {}) }}
                              onMouseEnter={e => !comingSoon && hoverIn(e, selected)} onMouseLeave={e => !comingSoon && hoverOut(e, selected)}
                            >
                              <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                {platformLabel(p)}
                                {comingSoon && (
                                  <span style={{ fontSize: "9px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "#f59e0b", background: "#f59e0b1a", border: "1px solid #f59e0b40", borderRadius: "5px", padding: "2px 6px", flexShrink: 0 }}>
                                    Coming soon
                                  </span>
                                )}
                              </span>
                              {selected && <Check size={13} color="#F6255A" />}
                            </button>
                          )
                        })}
                      </motion.div>
                    </React.Fragment>
                  )}
                </AnimatePresence>
              </div>
            )}
            <button
              onClick={() => selectedPlatform === PLATFORM && createNewApp()}
              disabled={creatingApp || selectedPlatform !== PLATFORM}
              style={{ display: "flex", alignItems: "center", gap: "8px", background: creatingApp || selectedPlatform !== PLATFORM ? "#2E3244" : "#F6255A", border: "none", color: creatingApp || selectedPlatform !== PLATFORM ? "#6b7280" : "#fff", borderRadius: "10px", padding: "12px 20px", fontSize: "14px", fontWeight: 700, cursor: creatingApp || selectedPlatform !== PLATFORM ? "not-allowed" : "pointer" }}
            >
              {creatingApp ? <><RotateCw size={16} style={{ animation: "spin 1s linear infinite" }} /> Creating…</> : <><Plus size={16} /> Create New App</>}
            </button>
          </div>
        </div>

        {/* ── Bulk actions bar ── */}
        {selectedAppIds.size > 0 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#F6255A12", border: "1px solid #F6255A40", borderRadius: "10px", padding: "10px 16px", marginBottom: "14px" }}>
            <span style={{ color: "#fff", fontSize: "13px", fontWeight: 600 }}>{selectedAppIds.size} selected</span>
            <button
              onClick={() => requestDeleteApps([...selectedAppIds], `${selectedAppIds.size} selected app${selectedAppIds.size === 1 ? "" : "s"}`)}
              style={{ display: "flex", alignItems: "center", gap: "6px", background: "none", border: "1px solid #ef444455", color: "#ef4444", borderRadius: "7px", padding: "6px 14px", fontSize: "12px", fontWeight: 700, cursor: "pointer" }}
            >
              <Trash2 size={13} /> Delete
            </button>
          </div>
        )}

        {/* ── Apps list ── */}
        <div style={{ background: "#21222D", border: "1px solid #2E3244", borderRadius: "14px", overflow: "hidden" }}>
        <div className="sv-app-list-grid" style={{ overflowX: "auto" }}>

          {selectedPlatform !== PLATFORM ? (
            <div style={{ padding: "56px 20px", textAlign: "center" }}>
              <div style={{ fontSize: "28px", marginBottom: "10px" }}>🚧</div>
              <p style={{ color: "#9ca3af", fontSize: "14px", fontWeight: 600, margin: "0 0 4px" }}>
                {platformLabel(selectedPlatform)} support is coming soon
              </p>
              <p style={{ color: "#4b5563", fontSize: "13px", margin: 0 }}>
                We're still working on validation for this platform — switch back to Meta to see your apps.
              </p>
            </div>
          ) : (
            <>
              {/* Header row */}
              <div style={{ display: "grid", gridTemplateColumns: gridCols, alignItems: "center", gap: "12px", padding: "12px 18px", borderBottom: "1px solid #2E3244" }}>
                <input
                  type="checkbox"
                  checked={visibleApps.length > 0 && selectedAppIds.size === visibleApps.length}
                  onChange={toggleSelectAllApps}
                  style={{ width: "15px", height: "15px", accentColor: "#F6255A", cursor: "pointer" }}
                />
                <span />
                <span style={{ color: "#6b7280", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>Name</span>
                <span style={{ color: "#6b7280", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>Category</span>
                <span />
                <span style={{ color: "#6b7280", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>Score</span>
                <span style={{ color: "#6b7280", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>Status</span>
                <span style={{ color: "#6b7280", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>Updated</span>
                <span />
              </div>

              {appsLoading && [0, 1, 2, 3].map(i => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: gridCols, alignItems: "center", gap: "12px", padding: "14px 18px", borderBottom: i < 3 ? "1px solid #2E3244" : "none" }}>
                  <Skeleton width="15px" height="15px" radius="3px" />
                  <Skeleton width="34px" height="34px" radius="9px" />
                  <Skeleton width="60%" />
                  <Skeleton width="50%" />
                  <Skeleton width="16px" height="16px" radius="4px" />
                  <Skeleton width="40px" />
                  <Skeleton width="70%" />
                  <Skeleton width="50%" />
                  <Skeleton width="24px" height="24px" radius="6px" style={{ justifySelf: "end" }} />
                </div>
              ))}

              {!appsLoading && visibleApps.length === 0 && (
                <div style={{ padding: "40px 20px", textAlign: "center", color: "#4b5563", fontSize: "13px" }}>
                  No apps yet. Click <strong style={{ color: "#6b7280" }}>Create New App</strong> to get started.
                </div>
              )}

              {!appsLoading && visibleApps.map((app, i) => {
            const isSelected = selectedAppIds.has(app.id)
            return (
              <div
                key={app.id}
                onClick={() => openApp(app)}
                style={{
                  display: "grid", gridTemplateColumns: gridCols, alignItems: "center", gap: "12px",
                  padding: "14px 18px", cursor: "pointer",
                  background: isSelected ? "#F6255A0c" : "transparent",
                  borderBottom: i < apps.length - 1 ? "1px solid #2E3244" : "none",
                  transition: "background 0.15s",
                }}
                onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = "#ffffff05" }}
                onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = "transparent" }}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onClick={e => e.stopPropagation()}
                  onChange={() => toggleAppSelected(app.id)}
                  style={{ width: "15px", height: "15px", accentColor: "#F6255A", cursor: "pointer" }}
                />
                <div style={{ width: "34px", height: "34px", borderRadius: "9px", background: "#F6255A14", display: "flex", alignItems: "center", justifyContent: "center", color: "#F6255A", fontWeight: 700, fontSize: "13px", flexShrink: 0 }}>
                  {app.name.charAt(0)}
                </div>
                <span style={{ color: "#fff", fontSize: "14px", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{app.name}</span>
                <span style={{ color: "#9ca3af", fontSize: "13px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{app.category || "—"}</span>
                <button
                  type="button"
                  aria-label={`Open ${platformLabel(app.platform)} developer dashboard`}
                  title={`Open ${platformLabel(app.platform)} developer dashboard`}
                  onClick={e => { e.stopPropagation(); window.open(META_DASHBOARD_URL, "_blank", "noopener") }}
                  style={{ background: "none", border: "none", color: "#6b7280", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}
                  onMouseEnter={e => { e.currentTarget.style.opacity = "0.75" }}
                  onMouseLeave={e => { e.currentTarget.style.opacity = "1" }}
                >
                  <PlatformIcon platform={app.platform} size={16} />
                </button>
                <span style={{ fontSize: "12px", fontWeight: 700, color: app.score != null ? getScoreColor(app.score) : "#4b5563" }}>{app.score != null ? `${app.score}/100` : "—"}</span>
                <span style={{ fontSize: "12px", fontWeight: 600, color: appStatusColor(app.status) }}>{app.status}</span>
                <span style={{ fontSize: "12px", color: "#6b7280" }}>{app.updatedAt}</span>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "8px" }}>
                  {app.status === "Ready To Publish" && (
                    <button
                      type="button"
                      aria-label={`Preview ${app.name}`}
                      onClick={e => { e.stopPropagation(); trackEvent(ANALYTICS_EVENTS.FEATURE.STORE_VALIDATOR_PREVIEW_OPENED, { app_id: app.id }); window.open(previewUrl(app.id), "_blank", "noopener") }}
                      style={{ display: "flex", alignItems: "center", gap: "5px", background: "#21222D", border: "1px solid #2E3244", color: "#9ca3af", borderRadius: "6px", padding: "5px 10px", fontSize: "11px", fontWeight: 600, cursor: "pointer", flexShrink: 0 }}
                      onMouseEnter={e => { e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "#F6255A" }}
                      onMouseLeave={e => { e.currentTarget.style.color = "#9ca3af"; e.currentTarget.style.borderColor = "#2E3244" }}
                    >
                      <Eye size={12} /> Preview
                    </button>
                  )}
                  <button
                    type="button"
                    aria-label={`Delete ${app.name}`}
                    title="Delete app"
                    onClick={e => { e.stopPropagation(); requestDeleteApps([app.id], app.name) }}
                    style={{ background: "none", border: "none", color: "#6b7280", cursor: "pointer", padding: "3px", display: "flex", alignItems: "center" }}
                    onMouseEnter={e => { e.currentTarget.style.color = "#ef4444" }}
                    onMouseLeave={e => { e.currentTarget.style.color = "#6b7280" }}
                  >
                    <Trash2 size={14} />
                  </button>
                  <ChevronRight size={15} color="#4b5563" style={{ flexShrink: 0 }} />
                </div>
              </div>
            )
          })}
            </>
          )}
        </div>
        </div>

        <AnimatePresence>
          {deleteConfirmation && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => !deletingApps && setDeleteConfirmation(null)}
              style={{ position: "fixed", inset: 0, zIndex: 50, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}
            >
              <motion.div
                initial={{ opacity: 0, y: 12, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.98 }}
                onClick={e => e.stopPropagation()}
                style={{ width: "100%", maxWidth: "420px", background: "#21222D", border: "1px solid #3a3d50", borderRadius: "12px", padding: "24px", boxShadow: "0 20px 60px rgba(0,0,0,0.55)" }}
              >
                <div style={{ display: "flex", alignItems: "flex-start", gap: "12px", marginBottom: "16px" }}>
                  <div style={{ width: "36px", height: "36px", borderRadius: "9px", background: "#ef444418", color: "#ef4444", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Trash2 size={18} />
                  </div>
                  <div>
                    <h2 style={{ color: "#fff", fontSize: "16px", margin: "0 0 6px", fontWeight: 700 }}>Delete app?</h2>
                    <p style={{ color: "#9ca3af", fontSize: "13px", lineHeight: 1.5, margin: 0 }}>
                      This will permanently delete <strong style={{ color: "#e5e7eb" }}>{deleteConfirmation.label}</strong> and its saved listing data.
                    </p>
                  </div>
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                  <button
                    type="button"
                    disabled={deletingApps}
                    onClick={() => setDeleteConfirmation(null)}
                    style={{ background: "none", border: "1px solid #3a3d50", color: "#d1d5db", borderRadius: "8px", padding: "9px 16px", fontSize: "13px", fontWeight: 600, cursor: deletingApps ? "not-allowed" : "pointer" }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={deletingApps}
                    onClick={deleteConfirmedApps}
                    style={{ display: "flex", alignItems: "center", gap: "7px", background: "#ef4444", border: "none", color: "#fff", borderRadius: "8px", padding: "9px 16px", fontSize: "13px", fontWeight: 700, cursor: deletingApps ? "not-allowed" : "pointer" }}
                  >
                    {deletingApps && <RotateCw size={13} style={{ animation: "spin 1s linear infinite" }} />}
                    {deletingApps ? "Deleting…" : "Delete app"}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
      <style>{`@keyframes spin { from { transform:rotate(0deg) } to { transform:rotate(360deg) } }`}</style>
    </div>
  )

  // ── form step ──────────────────────────────────────────────────────────────

  const inputStyle = { width: "100%", background: "#21222D", border: "1px solid #2E3244", borderRadius: "8px", color: "#fff", fontSize: "14px", padding: "10px 14px", outline: "none", boxSizing: "border-box", transition: "border-color 0.15s" }

  const renderCustomSelect = field => {
    const { id, options, placeholder } = field
    const val = formData[id]
    const isOpen = openDropdownId === id
    // Primary/Additional genre 1/2 share one list — whatever's picked in one is disabled in the others.
    const disabledValues = GENRE_FIELD_IDS.includes(id)
      ? GENRE_FIELD_IDS.filter(fid => fid !== id).map(fid => formData[fid]).filter(Boolean)
      : []
    return (
      <div style={{ position: "relative" }}>
        <button
          type="button"
          onClick={() => setOpenDropdownId(isOpen ? null : id)}
          style={{ ...inputStyle, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", color: val ? "#fff" : "#6b7280", borderColor: isOpen ? "#F6255A55" : "#2E3244" }}
        >
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{val || placeholder || "Select…"}</span>
          <ChevronDown size={15} style={{ flexShrink: 0, color: "#6b7280", transition: "transform 0.2s", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }} />
        </button>
        <AnimatePresence>
          {isOpen && (
            <React.Fragment>
              <div onClick={() => setOpenDropdownId(null)} style={{ position: "fixed", inset: 0, zIndex: 30 }} />
              <motion.div
                initial={{ opacity: 0, y: -6, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -6, scale: 0.98 }}
                transition={{ duration: 0.15, ease: "easeOut" }} style={dropdownPanelStyle}
              >
                {options.map(o => {
                  const selected = val === o
                  const disabled = disabledValues.includes(o)
                  return (
                    <button
                      type="button" key={o}
                      disabled={disabled}
                      onClick={() => { if (disabled) return; setField(id, o); setOpenDropdownId(null) }}
                      style={{ ...dropdownOptionStyle(selected), opacity: disabled ? 0.35 : 1, cursor: disabled ? "not-allowed" : "pointer" }}
                      onMouseEnter={e => !disabled && hoverIn(e, selected)} onMouseLeave={e => !disabled && hoverOut(e, selected)}
                    >
                      {o}
                      {selected && <Check size={13} color="#F6255A" />}
                    </button>
                  )
                })}
              </motion.div>
            </React.Fragment>
          )}
        </AnimatePresence>
      </div>
    )
  }

  const renderCustomMultiSelect = field => {
    const { id, options, max } = field
    const val = formData[id] || []
    const isOpen = openDropdownId === id
    const summary = val.length === 0 ? "Select…" : val.length <= 2 ? val.join(", ") : `${val.length} selected`
    return (
      <div style={{ position: "relative" }}>
        <button
          type="button"
          onClick={() => setOpenDropdownId(isOpen ? null : id)}
          style={{ ...inputStyle, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", color: val.length ? "#fff" : "#6b7280", borderColor: isOpen ? "#F6255A55" : "#2E3244" }}
        >
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{summary}</span>
          <ChevronDown size={15} style={{ flexShrink: 0, color: "#6b7280", transition: "transform 0.2s", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }} />
        </button>
        {val.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "8px" }}>
            {val.map(tag => (
              <span key={tag} style={{ display: "inline-flex", alignItems: "center", gap: "5px", background: "#2E3244", border: "1px solid #353748", color: "#d1d5db", borderRadius: "20px", padding: "4px 12px", fontSize: "12px" }}>
                {tag}
                <button type="button" onClick={() => setField(id, val.filter(v => v !== tag))} style={{ background: "none", border: "none", color: "#6b7280", cursor: "pointer", padding: 0, display: "flex" }}><X size={11} /></button>
              </span>
            ))}
          </div>
        )}
        <AnimatePresence>
          {isOpen && (
            <React.Fragment>
              <div onClick={() => setOpenDropdownId(null)} style={{ position: "fixed", inset: 0, zIndex: 30 }} />
              <motion.div
                initial={{ opacity: 0, y: -6, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -6, scale: 0.98 }}
                transition={{ duration: 0.15, ease: "easeOut" }} style={dropdownPanelStyle}
              >
                {options.map(o => {
                  const on = val.includes(o)
                  const capped = !on && max && val.length >= max
                  return (
                    <button
                      type="button" key={o}
                      disabled={capped}
                      onClick={() => { if (capped) return; setField(id, on ? val.filter(v => v !== o) : [...val, o]) }}
                      style={{ ...dropdownOptionStyle(on), opacity: capped ? 0.35 : 1, cursor: capped ? "not-allowed" : "pointer" }}
                      onMouseEnter={e => !capped && hoverIn(e, on)} onMouseLeave={e => !capped && hoverOut(e, on)}
                    >
                      <span style={{ display: "flex", alignItems: "center", gap: "9px" }}>
                        <span style={{ width: "15px", height: "15px", borderRadius: "4px", border: `1.5px solid ${on ? "#F6255A" : "#4b5563"}`, background: on ? "#F6255A" : "none", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {on && <Check size={10} color="#fff" strokeWidth={3} />}
                        </span>
                        {o}
                      </span>
                    </button>
                  )
                })}
              </motion.div>
            </React.Fragment>
          )}
        </AnimatePresence>
      </div>
    )
  }

  const renderField = field => {
    const { id, type, maxLength, rows, options, max, placeholder, accept } = field
    const val = formData[id]

    if (type === "text" || type === "url")
      return <input type="text" value={val} placeholder={placeholder || ""} maxLength={maxLength} onChange={e => setField(id, e.target.value)} style={inputStyle} onFocus={e => e.target.style.borderColor = "#F6255A55"} onBlur={e => e.target.style.borderColor = "#2E3244"} />

    if (type === "textarea")
      return <textarea value={val} placeholder={placeholder || ""} maxLength={maxLength} rows={rows || 4} onChange={e => setField(id, e.target.value)} style={{ ...inputStyle, resize: "vertical" }} onFocus={e => e.target.style.borderColor = "#F6255A55"} onBlur={e => e.target.style.borderColor = "#2E3244"} />

    if (type === "markdown") {
      const toggleRow = (
        <button type="button" onClick={() => setMarkdownEnabled(p => !p)}
          style={{ display: "flex", alignItems: "center", gap: "7px", background: "none", border: "none", padding: 0, cursor: "pointer", marginBottom: "8px" }}
        >
          <div style={{ width: "30px", height: "17px", borderRadius: "9px", background: markdownEnabled ? "#F6255A" : "#2E3244", position: "relative", transition: "background 0.15s", flexShrink: 0 }}>
            <div style={{ position: "absolute", top: "2px", left: markdownEnabled ? "15px" : "2px", width: "13px", height: "13px", borderRadius: "50%", background: "#fff", transition: "left 0.15s" }} />
          </div>
          <span style={{ fontSize: "12px", color: markdownEnabled ? "#d1d5db" : "#6b7280", fontWeight: 600 }}>Markdown formatting</span>
        </button>
      )

      if (!markdownEnabled) {
        return (
          <div>
            {toggleRow}
            <textarea value={val} placeholder={placeholder || ""} maxLength={maxLength} rows={rows || 4} onChange={e => setField(id, e.target.value)} style={{ ...inputStyle, resize: "vertical" }} onFocus={e => e.target.style.borderColor = "#F6255A55"} onBlur={e => e.target.style.borderColor = "#2E3244"} />
          </div>
        )
      }

      const wrapSelection = (before, after = before) => {
        const el = markdownTextareaRef.current
        if (!el) return
        const { selectionStart: start, selectionEnd: end, value } = el
        const selected = value.slice(start, end) || "text"
        const nextVal = value.slice(0, start) + before + selected + after + value.slice(end)
        setField(id, nextVal)
        requestAnimationFrame(() => {
          el.focus()
          el.selectionStart = start + before.length
          el.selectionEnd = start + before.length + selected.length
        })
      }
      const insertAtLineStart = prefix => {
        const el = markdownTextareaRef.current
        if (!el) return
        const { selectionStart: start, value } = el
        const lineStart = value.lastIndexOf("\n", start - 1) + 1
        const nextVal = value.slice(0, lineStart) + prefix + value.slice(lineStart)
        setField(id, nextVal)
        requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = start + prefix.length })
      }
      const insertImageMarkdown = (src, alt = "image") => {
        const el = markdownTextareaRef.current
        const start = el ? el.selectionStart : val.length
        const md = `![${alt}](${src})`
        const nextVal = val.slice(0, start) + md + val.slice(start)
        setField(id, nextVal)
        setImagePromptOpen(false)
        requestAnimationFrame(() => { if (el) { el.focus(); el.selectionStart = el.selectionEnd = start + md.length } })
      }
      const insertLink = () => {
        const url = window.prompt("Link URL")
        if (!url) return
        wrapSelection("[", `](${url})`)
      }
      const toolbarBtn = (Icon, title, onClick) => (
        <button type="button" title={title} onClick={onClick}
          style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "26px", height: "26px", background: "none", border: "1px solid #2E3244", borderRadius: "5px", color: "#9ca3af", cursor: "pointer" }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = "#F6255A55"; e.currentTarget.style.color = "#fff" }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = "#2E3244"; e.currentTarget.style.color = "#9ca3af" }}
        >
          <Icon size={13} />
        </button>
      )
      return (
        <div>
          {toggleRow}
          <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px", position: "relative" }}>
            {toolbarBtn(Bold, "Bold", () => wrapSelection("**"))}
            {toolbarBtn(Italic, "Italic", () => wrapSelection("_"))}
            {toolbarBtn(Heading2, "Heading", () => insertAtLineStart("## "))}
            {toolbarBtn(List, "Bullet list", () => insertAtLineStart("- "))}
            {toolbarBtn(Link2, "Link", insertLink)}
            {toolbarBtn(Image, "Image", () => setImagePromptOpen(p => !p))}
            {imagePromptOpen && (
              <React.Fragment>
                <div onClick={() => setImagePromptOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
                <div style={{ position: "absolute", top: "32px", left: "128px", zIndex: 41, width: "260px", background: "#171821", border: "1px solid #2E3244", borderRadius: "9px", padding: "12px", boxShadow: "0 16px 40px rgba(0,0,0,0.5)", display: "flex", flexDirection: "column", gap: "10px" }}>
                  <label style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "7px", border: "1px dashed #2E3244", borderRadius: "7px", padding: "9px", cursor: "pointer", color: "#9ca3af", fontSize: "12px" }}>
                    <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => {
                      const file = e.target.files[0]
                      if (file) insertImageMarkdown(URL.createObjectURL(file), file.name)
                    }} />
                    <Plus size={13} /> Upload from device
                  </label>
                  <p style={{ color: "#f59e0b", fontSize: "10.5px", margin: 0, lineHeight: 1.5 }}>
                    Uploaded images only preview in this browser tab for now — they won't show once saved, reopened, or shared. Ask us once real hosting is wired up if this needs to work everywhere.
                  </p>
                </div>
              </React.Fragment>
            )}
            <button type="button" onClick={() => setMarkdownPreview(p => !p)}
              style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "5px", background: markdownPreview ? "#F6255A14" : "none", border: `1px solid ${markdownPreview ? "#F6255A55" : "#2E3244"}`, borderRadius: "5px", color: markdownPreview ? "#F6255A" : "#9ca3af", cursor: "pointer", fontSize: "11px", fontWeight: 600, padding: "4px 10px" }}
            >
              <Eye size={12} /> {markdownPreview ? "Write" : "Preview"}
            </button>
          </div>
          {markdownPreview ? (
            <div style={{ ...inputStyle, minHeight: `${(rows || 7) * 20}px`, lineHeight: 1.6, fontSize: "13px" }} className="sv-markdown-preview">
              {val ? <ReactMarkdown>{val}</ReactMarkdown> : <span style={{ color: "#4b5563" }}>Nothing to preview yet.</span>}
            </div>
          ) : (
            <textarea
              ref={markdownTextareaRef}
              value={val} placeholder={placeholder || ""} maxLength={maxLength} rows={rows || 4}
              onChange={e => setField(id, e.target.value)}
              style={{ ...inputStyle, resize: "vertical", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
              onFocus={e => e.target.style.borderColor = "#F6255A55"} onBlur={e => e.target.style.borderColor = "#2E3244"}
            />
          )}
        </div>
      )
    }

    if (type === "file") {
      const file = val
      if (!file)
        return (
          <label style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", border: "1px dashed #2E3244", borderRadius: "8px", padding: "14px", cursor: "pointer", color: "#6b7280", fontSize: "13px" }}>
            <input type="file" accept={accept} style={{ display: "none" }} onChange={e => e.target.files[0] && setField(id, e.target.files[0])} />
            <Plus size={14} /> Choose file…
          </label>
        )
      // An AI-generated asset from generate/complete arrives as a hosted URL string (already
      // validated + persisted server-side against this app_id) rather than a local File — same
      // preview treatment, just no createObjectURL() needed since it's already a real URL.
      const isUrl = typeof file === "string"
      const isImage = isUrl || file.type?.startsWith("image/")
      if (!isImage)
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "10px", border: "1px solid #2E3244", borderRadius: "8px", padding: "8px 12px" }}>
            <div style={{ width: "40px", height: "40px", borderRadius: "6px", background: "#171821", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><FileText size={16} color="#6b7280" /></div>
            <span style={{ color: "#d1d5db", fontSize: "13px", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</span>
            <button onClick={() => setField(id, null)} style={{ background: "none", border: "none", color: "#6b7280", cursor: "pointer", padding: 0, display: "flex" }}><X size={14} /></button>
          </div>
        )
      // Shown big (not a small thumbnail) so the user can actually judge an AI-generated
      // image before submitting — click it to open a full-resolution lightbox.
      const src = assetSrc(file)
      return (
        <div style={{ border: "1px solid #2E3244", borderRadius: "8px", overflow: "hidden", background: "#171821" }}>
          <div style={{ position: "relative" }}>
            <img src={src} alt="" onClick={() => setPreviewImageKey(id)} style={{ width: "100%", height: "180px", objectFit: "cover", display: "block", cursor: "zoom-in" }} />
            <button
              onClick={() => setField(id, null)}
              style={{ position: "absolute", top: "8px", right: "8px", width: "26px", height: "26px", borderRadius: "50%", background: "rgba(23,24,33,0.85)", border: "1px solid #2E3244", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}
            >
              <X size={13} />
            </button>
          </div>
          <div style={{ padding: "7px 12px", color: "#9ca3af", fontSize: "12px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {isUrl ? "AI-generated image" : file.name}
          </div>
        </div>
      )
    }

    if (type === "files") {
      const files = val || []
      return (
        <div>
          {files.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginBottom: "10px" }}>
              {files.map((f, i) => {
                const isUrl = typeof f === "string"
                const src = assetSrc(f)
                return (
                  <div key={isUrl ? f : `${f.name}-${i}`} style={{ position: "relative" }}>
                    <img src={src} alt="" onClick={() => setPreviewImageKey(`screenshots:${i}`)} style={{ width: "140px", height: "140px", objectFit: "cover", borderRadius: "8px", border: "1px solid #2E3244", cursor: "zoom-in", display: "block" }} />
                    <button
                      onClick={() => setField(id, files.filter((_, idx) => idx !== i))}
                      style={{ position: "absolute", top: "-6px", right: "-6px", width: "20px", height: "20px", borderRadius: "50%", background: "#ef4444", border: "1px solid #171821", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}
                    >
                      <X size={12} />
                    </button>
                  </div>
                )
              })}
            </div>
          )}
          {(!max || files.length < max) && (
            <label style={{ display: "flex", alignItems: "center", gap: "8px", border: "1px dashed #2E3244", borderRadius: "8px", padding: "14px", cursor: "pointer", color: "#6b7280", fontSize: "13px" }}>
              <input
                type="file"
                accept={accept}
                multiple
                style={{ display: "none" }}
                onChange={e => {
                  const picked = Array.from(e.target.files || [])
                  if (picked.length) setField(id, max ? [...files, ...picked].slice(0, max) : [...files, ...picked])
                  e.target.value = ""
                }}
              />
              <Plus size={14} /> Add screenshots…
            </label>
          )}
        </div>
      )
    }

    if (type === "select")
      return renderCustomSelect(field)

    if (type === "tags")
      return (
        <div>
          {(val || []).length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "8px" }}>
              {val.map(tag => (
                <span key={tag} style={{ display: "inline-flex", alignItems: "center", gap: "5px", background: "#2E3244", border: "1px solid #353748", color: "#d1d5db", borderRadius: "20px", padding: "4px 12px", fontSize: "13px" }}>
                  {tag}
                  <button onClick={() => removeTag(id, tag)} style={{ background: "none", border: "none", color: "#6b7280", cursor: "pointer", padding: 0, display: "flex", lineHeight: 1 }}><X size={12} /></button>
                </span>
              ))}
            </div>
          )}
          {(!max || (val || []).length < max) && (
            <div style={{ display: "flex", gap: "8px" }}>
              <input value={tagInputs[id] || ""} onChange={e => setTagInputs(p => ({ ...p, [id]: e.target.value }))} onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addTag(id, max))} placeholder={placeholder} style={{ ...inputStyle, flex: 1 }} onFocus={e => e.target.style.borderColor = "#F6255A55"} onBlur={e => e.target.style.borderColor = "#2E3244"} />
              <button onClick={() => addTag(id, max)} style={{ background: "#2E3244", border: "1px solid #2E3244", color: "#9ca3af", borderRadius: "8px", padding: "0 14px", cursor: "pointer" }}><Plus size={15} /></button>
            </div>
          )}
        </div>
      )

    if (type === "checkboxes") {
      // Long option lists (art style, game theme, gameplay mechanics, content descriptors)
      // get the compact dropdown treatment instead of a big wall of pill buttons.
      if (options.length > 6) return renderCustomMultiSelect(field)
      return (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {options.map(opt => {
            const on = (val || []).includes(opt)
            const capped = !on && max && (val || []).length >= max
            return (
              <button key={opt} disabled={capped} onClick={() => toggleCheck(id, opt, max)} style={{ display: "flex", alignItems: "center", gap: "8px", background: on ? "#F6255A14" : "#21222D", border: `1px solid ${on ? "#F6255A55" : "#2E3244"}`, borderRadius: "8px", padding: "8px 14px", fontSize: "13px", color: on ? "#fff" : capped ? "#4b5563" : "#9ca3af", cursor: capped ? "not-allowed" : "pointer", opacity: capped ? 0.5 : 1, transition: "all 0.15s" }}>
                <div style={{ width: "14px", height: "14px", borderRadius: "3px", border: `1.5px solid ${on ? "#F6255A" : "#4b5563"}`, background: on ? "#F6255A" : "none", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {on && <Check size={9} color="#fff" strokeWidth={3} />}
                </div>
                {opt}
              </button>
            )
          })}
        </div>
      )
    }

    if (type === "radio")
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {options.map(opt => {
            const on = val === opt
            return (
              <button key={opt} onClick={() => setField(id, opt)} style={{ display: "flex", alignItems: "center", gap: "10px", background: on ? "#F6255A14" : "none", border: `1px solid ${on ? "#F6255A55" : "#2E3244"}`, borderRadius: "8px", padding: "10px 14px", fontSize: "13px", color: on ? "#e5e7eb" : "#9ca3af", cursor: "pointer", textAlign: "left", transition: "all 0.15s" }}>
                <div style={{ width: "15px", height: "15px", borderRadius: "50%", border: `1.5px solid ${on ? "#F6255A" : "#4b5563"}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {on && <div style={{ width: "7px", height: "7px", borderRadius: "50%", background: "#F6255A" }} />}
                </div>
                {opt}
              </button>
            )
          })}
        </div>
      )
  }

  // Renders a field's card, then recursively renders any field nested under it (e.g. a
  // genre's sub-type picker), indented with a connector arrow to show the relationship.
  const renderFieldCard = (field, depth = 0) => {
    const { id, label, maxLength, optional, hint } = field
    const val = formData[id]
    const charCount = typeof val === "string" ? val.length : null
    const fieldRes = sectionResult?.fields?.[id]
    const borderCol = fieldRes
      ? fieldRes.status === "pass" ? "#22c55e30" : fieldRes.status === "warning" ? "#f59e0b30" : "#ef444430"
      : "#2E3244"
    const children = activeCfg.fields.filter(f => f.nestedUnder && f.nestedUnder(formData) === id)
    // The genre trio's score always lives under "primaryGenre" (see setField/regenerateField),
    // so that's what "has this ever been scored" needs to check regardless of which of the 3
    // dropdowns is being looked at here.
    const everScoredLookupId = (id === "primaryGenre" || id === "additionalGenre1" || id === "additionalGenre2") ? "primaryGenre" : id
    const isAssetField = !!ASSET_FILE_FIELD_MAP[id]
    // trailer has no generate capability (the API 400s /generate/trailer/) — same exclusion the
    // "Generate with AI" button below already applies.
    const hasRegenerateApi = isAssetField ? ASSET_FILE_FIELD_MAP[id] !== "trailer" : !!FIELD_TO_API_KEY[id]
    // Text/format fields only offer this once validated at least once and then edited again
    // (see the dirty/everScored gating). An image has no "typed it manually" precursor step —
    // there's always just an existing asset you might want a different AI take on — so show it
    // right alongside "Generate with AI" whenever there's already something there, regardless
    // of dirty/everScored state.
    const hasAssetValue = Array.isArray(val) ? val.length > 0 : !!val
    const canRegenerateField = isAssetField
      ? hasRegenerateApi && !!formData.appId && hasAssetValue
      : hasRegenerateApi && !!formData.appId && dirtyFields.has(id) && everScoredFields.has(everScoredLookupId)
    const fieldRegenerating = isAssetField ? generatingAssetKey === id : regeneratingField === id
    const canCopyField = !isAssetField && !!fieldValueAsText(id)
    const justCopied = copiedFieldId === id
    const canDownloadAsset = isAssetField && (Array.isArray(val) ? val.length > 0 : !!val)
    const isDownloading = downloadingFieldId === id

    return (
      <React.Fragment key={id}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginLeft: `${depth * 26}px` }}>
          {depth > 0 && <CornerDownRight size={15} color="#4b5563" style={{ flexShrink: 0, marginTop: "17px" }} />}
          <div style={{ flex: 1, minWidth: 0, background: "#21222D", border: `1px solid ${borderCol}`, borderRadius: "10px", padding: "15px 18px", transition: "border-color 0.3s" }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: "8px", marginBottom: hint ? "3px" : "10px" }}>
              <label style={{ color: "#e5e7eb", fontSize: "13px", fontWeight: 600 }}>{label}</label>
              {optional && <span style={{ fontSize: "11px", color: "#4b5563" }}>Optional</span>}
              <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "9px" }}>
                {canDownloadAsset && (
                  <button type="button" title="Download to your device" disabled={isDownloading} onClick={() => downloadFieldAsset(id)}
                    style={{
                      display: "flex", alignItems: "center", gap: "4px",
                      background: "none", border: `1px solid ${isDownloading ? "#2E3244" : "#2E3244"}`,
                      borderRadius: "5px", padding: "3px 8px", fontSize: "10.5px", fontWeight: 600,
                      cursor: isDownloading ? "not-allowed" : "pointer", color: isDownloading ? "#4b5563" : "#9ca3af",
                    }}
                    onMouseEnter={e => { if (!isDownloading) { e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "#4b5563" } }}
                    onMouseLeave={e => { if (!isDownloading) { e.currentTarget.style.color = "#9ca3af"; e.currentTarget.style.borderColor = "#2E3244" } }}
                  >
                    {isDownloading
                      ? <><RotateCw size={11} style={{ animation: "spin 1s linear infinite" }} /> Downloading…</>
                      : <><Download size={11} /> Download</>}
                  </button>
                )}
                {canCopyField && (
                  <button type="button" title={justCopied ? "Copied!" : "Copy this field's value"} onClick={() => copyFieldValue(id)}
                    style={{
                      display: "flex", alignItems: "center", gap: "4px",
                      background: justCopied ? "#22c55e14" : "none",
                      border: `1px solid ${justCopied ? "#22c55e55" : "#2E3244"}`,
                      borderRadius: "5px", padding: "3px 8px", fontSize: "10.5px", fontWeight: 600,
                      cursor: "pointer", color: justCopied ? "#22c55e" : "#9ca3af",
                    }}
                    onMouseEnter={e => { if (!justCopied) { e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "#4b5563" } }}
                    onMouseLeave={e => { if (!justCopied) { e.currentTarget.style.color = "#9ca3af"; e.currentTarget.style.borderColor = "#2E3244" } }}
                  >
                    {justCopied ? <><Check size={11} /> Copied!</> : <><Copy size={11} /> Copy</>}
                  </button>
                )}
                {fieldRes && (
                  <span style={{ fontSize: "11px", fontWeight: 700, color: getScoreColor(fieldRes.score) }}>{fieldRes.score}/100</span>
                )}
              </div>
            </div>
            {hint && <p style={{ color: "#4b5563", fontSize: "12px", margin: "0 0 10px", lineHeight: 1.5 }}>{hint}</p>}
            {id === "iarcCertId" ? (
              Object.keys(formData.iarcResponses || {}).length > 0 ? (
                <IarcAnswersSummary
                  value={formData.iarcResponses}
                  onChange={setIarcResponses}
                  onSave={saveApp}
                  saving={savingApp}
                  insights={iarcInsights}
                  onDownloadCertificate={downloadIarcCertificate}
                  downloadingCertificate={downloadingCertificate}
                  certificateError={certificateError}
                />
              ) : (
                <button
                  type="button" onClick={() => setShowIarcQuestionnaire(true)}
                  style={{ display: "flex", alignItems: "center", gap: "7px", background: "#F6255A14", border: "1px solid #F6255A40", color: "#F6255A", borderRadius: "8px", padding: "10px 14px", fontSize: "13px", fontWeight: 700, cursor: "pointer" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#F6255A22"}
                  onMouseLeave={e => e.currentTarget.style.background = "#F6255A14"}
                >
                  <Shield size={14} /> Add Certificate
                </button>
              )
            ) : renderField(field)}
            {maxLength && charCount !== null && (
              <div style={{ textAlign: "right", marginTop: "4px" }}>
                <span style={{ fontSize: "11px", color: charCount > maxLength * 0.9 ? "#f97316" : "#4b5563" }}>{charCount} / {maxLength}</span>
              </div>
            )}
            {canRegenerateField && (
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "8px" }}>
                <span style={{ color: "#6b7280", fontSize: "11.5px" }}>Want to regenerate this field?</span>
                <button
                  type="button" onClick={() => isAssetField ? generateAssetField(field) : regenerateField(id)} disabled={anyGenerationInFlight}
                  style={{
                    display: "flex", alignItems: "center", gap: "5px",
                    background: anyGenerationInFlight ? "#2E3244" : "#F6255A", border: "none",
                    color: anyGenerationInFlight ? "#6b7280" : "#fff",
                    borderRadius: "6px", padding: "4px 10px", fontSize: "10.5px", fontWeight: 700,
                    cursor: anyGenerationInFlight ? "not-allowed" : "pointer",
                  }}
                >
                  {fieldRegenerating
                    ? <><RotateCw size={10} style={{ animation: "spin 1s linear infinite" }} /> Regenerating…</>
                    : <><Sparkles size={10} /> Regenerate this field</>}
                </button>
              </div>
            )}
            {/* trailer is excluded — the API 400s /generate/trailer/, no video-generation capability */}
            {ASSET_FILE_FIELD_MAP[id] && ASSET_FILE_FIELD_MAP[id] !== "trailer" && (
              <div style={{ marginTop: "8px" }}>
                {assetPromptOpenFor === id ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    <textarea
                      value={assetPromptDraft}
                      onChange={e => setAssetPromptDraft(e.target.value)}
                      placeholder="Optional — describe what this image should look like. Leave blank to use the app's saved details."
                      rows={2}
                      style={{ ...inputStyle, resize: "vertical", fontSize: "12px" }}
                      onFocus={e => e.target.style.borderColor = "#F6255A55"}
                      onBlur={e => e.target.style.borderColor = "#2E3244"}
                    />
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        onClick={() => generateAssetField(field)}
                        disabled={anyGenerationInFlight}
                        style={{ display: "flex", alignItems: "center", gap: "6px", background: anyGenerationInFlight ? "#2E3244" : "#F6255A", border: "none", color: anyGenerationInFlight ? "#6b7280" : "#fff", borderRadius: "7px", padding: "6px 12px", fontSize: "12px", fontWeight: 700, cursor: anyGenerationInFlight ? "not-allowed" : "pointer" }}
                      >
                        {generatingAssetKey === id ? <><RotateCw size={12} style={{ animation: "spin 1s linear infinite" }} /> Generating…</> : "Generate"}
                      </button>
                      <button
                        onClick={() => { setAssetPromptOpenFor(null); setAssetPromptDraft("") }}
                        disabled={generatingAssetKey === id}
                        style={{ background: "none", border: "none", color: "#6b7280", fontSize: "12px", cursor: "pointer" }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => { setAssetPromptOpenFor(id); setAssetPromptDraft("") }}
                    disabled={anyGenerationInFlight}
                    style={{ display: "flex", alignItems: "center", gap: "6px", background: "none", border: "none", color: anyGenerationInFlight ? "#4b5563" : "#F6255A", fontSize: "12px", fontWeight: 600, cursor: anyGenerationInFlight ? "not-allowed" : "pointer", padding: 0 }}
                  >
                    <Sparkles size={12} /> Generate with AI
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        {children.map(child => renderFieldCard(child, depth + 1))}
      </React.Fragment>
    )
  }

  // ── suggestion widget (mirrors field types, reads/writes fieldSuggestions) ──

  const suggBase = { width: "100%", background: "#171821", border: "1px solid #2E3244", borderRadius: "7px", color: "#d1d5db", fontSize: "12px", padding: "8px 10px", outline: "none", boxSizing: "border-box", transition: "border-color 0.15s" }
  const focusR = e => { e.target.style.borderColor = "#F6255A55" }
  const blurR  = e => { e.target.style.borderColor = "#2E3244" }

  const renderSuggestionField = (field, key) => {
    const { type, options, id, max } = field
    const val = fieldSuggestions[key]

    if (type === "text" || type === "url")
      return <input type="text" value={val || ""} onChange={e => setSuggestion(key, e.target.value)} style={suggBase} onFocus={focusR} onBlur={blurR} />

    if (type === "textarea" || type === "markdown")
      return <textarea value={val || ""} rows={3} onChange={e => setSuggestion(key, e.target.value)} style={{ ...suggBase, resize: "vertical", lineHeight: 1.5 }} onFocus={focusR} onBlur={blurR} />

    if (type === "select")
      return (
        <select value={val || ""} onChange={e => setSuggestion(key, e.target.value)} style={{ ...suggBase, cursor: "pointer" }}>
          <option value="">Select…</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      )

    if (type === "radio")
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
          {options.map(opt => {
            const on = val === opt
            return (
              <button key={opt} onClick={() => setSuggestion(key, opt)} style={{ display: "flex", alignItems: "center", gap: "8px", background: on ? "#F6255A14" : "none", border: `1px solid ${on ? "#F6255A55" : "#2E3244"}`, borderRadius: "6px", padding: "8px 10px", fontSize: "12px", color: on ? "#e5e7eb" : "#9ca3af", cursor: "pointer", textAlign: "left" }}>
                <div style={{ width: "13px", height: "13px", borderRadius: "50%", border: `1.5px solid ${on ? "#F6255A" : "#4b5563"}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {on && <div style={{ width: "5px", height: "5px", borderRadius: "50%", background: "#F6255A" }} />}
                </div>
                {opt}
              </button>
            )
          })}
        </div>
      )

    if (type === "checkboxes") {
      const checked = Array.isArray(val) ? val : []
      return (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
          {options.map(opt => {
            const on = checked.includes(opt)
            return (
              <button key={opt} onClick={() => setSuggestion(key, on ? checked.filter(o => o !== opt) : [...checked, opt])}
                style={{ display: "flex", alignItems: "center", gap: "6px", background: on ? "#F6255A14" : "none", border: `1px solid ${on ? "#F6255A55" : "#2E3244"}`, borderRadius: "6px", padding: "5px 10px", fontSize: "11px", color: on ? "#e5e7eb" : "#9ca3af", cursor: "pointer" }}>
                <div style={{ width: "11px", height: "11px", borderRadius: "2px", border: `1.5px solid ${on ? "#F6255A" : "#4b5563"}`, background: on ? "#F6255A" : "none", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {on && <Check size={7} color="#fff" strokeWidth={3} />}
                </div>
                {opt}
              </button>
            )
          })}
        </div>
      )
    }

    if (type === "tags") {
      const suggested = Array.isArray(val) ? val : []
      const existing = formData[id] || []
      if (!suggested.length) return <p style={{ color: "#4b5563", fontSize: "11px", margin: 0 }}>No new tag suggestions.</p>
      return (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
          {suggested.map(t => {
            const added = existing.includes(t)
            const atCap = !added && max && existing.length >= max
            return (
              <button
                type="button" key={t} disabled={atCap}
                onClick={() => { if (added) removeTag(id, t); else if (!atCap) setField(id, [...existing, t]) }}
                style={{
                  display: "inline-flex", alignItems: "center", gap: "4px",
                  background: added ? "#22c55e14" : atCap ? "#ffffff05" : "#F6255A12",
                  border: `1px solid ${added ? "#22c55e40" : atCap ? "#2E3244" : "#F6255A40"}`,
                  color: added ? "#22c55e" : atCap ? "#4b5563" : "#F6255A",
                  borderRadius: "20px", padding: "3px 10px", fontSize: "11px",
                  cursor: atCap ? "not-allowed" : "pointer",
                }}
              >
                {added ? <Check size={10} /> : "+"} {t}
              </button>
            )
          })}
        </div>
      )
    }
    return null
  }

  return (
    <div style={{ minHeight: "100vh", paddingTop: "80px", background: "#171821", display: "flex", flexDirection: "column" }}>
      <style>{RESPONSIVE_CSS}</style>
      <SEO title={`Store Validator — ${formData.appName || "New App"}`} />

      {/* ── Full-size asset preview lightbox — steps through every other image currently
          set in the Assets section via the arrows, not just the one that was clicked ── */}
      {previewImageKey && (() => {
        const gallery = buildAssetGallery()
        const activeImg = gallery.find(g => g.key === previewImageKey)
        if (!activeImg) return null
        return (
          <div
            onClick={() => setPreviewImageKey(null)}
            style={{
              // The site's own sidebar/topbar chrome uses z-index up to 9999 (see
              // src/assets/scss/custom/structure/_vertical.scss and _right-sidebar.scss) —
              // this needs to sit above all of it, not just above this page's own content.
              position: "fixed", inset: 0, zIndex: 100000, display: "flex", alignItems: "center", justifyContent: "center",
              padding: "40px", cursor: "zoom-out", background: "rgba(10,10,15,0.75)",
              backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)",
            }}
          >
            <img
              src={activeImg.src}
              alt=""
              onClick={e => e.stopPropagation()}
              style={{ maxWidth: "min(80vw, 900px)", maxHeight: "80vh", borderRadius: "10px", boxShadow: "0 20px 60px rgba(0,0,0,0.6)", cursor: "default" }}
            />
            {gallery.length > 1 && (
              <div
                onClick={e => e.stopPropagation()}
                style={{ position: "fixed", bottom: "24px", left: "50%", transform: "translateX(-50%)", display: "flex", alignItems: "center", gap: "14px", background: "rgba(23,24,33,0.85)", padding: "6px 8px", borderRadius: "24px" }}
              >
                <button
                  onClick={() => goToPreviewImage(-1)}
                  style={{ width: "32px", height: "32px", borderRadius: "50%", background: "#fff", border: "none", color: "#171821", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                >
                  <ChevronLeft size={16} strokeWidth={2.5} />
                </button>
                <span style={{ color: "#fff", fontSize: "12px", whiteSpace: "nowrap" }}>
                  {activeImg.label} · {gallery.findIndex(g => g.key === previewImageKey) + 1} / {gallery.length}
                </span>
                <button
                  onClick={() => goToPreviewImage(1)}
                  style={{ width: "32px", height: "32px", borderRadius: "50%", background: "#fff", border: "none", color: "#171821", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                >
                  <ChevronRight size={16} strokeWidth={2.5} />
                </button>
              </div>
            )}
            <button
              onClick={() => setPreviewImageKey(null)}
              style={{ position: "fixed", top: "24px", right: "24px", width: "42px", height: "42px", borderRadius: "50%", background: "#fff", border: "none", color: "#171821", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 16px rgba(0,0,0,0.35)" }}
            >
              <X size={20} strokeWidth={2.5} />
            </button>
          </div>
        )
      })()}

      {/* ── Top bar ── */}
      <div className="sv-top-bar" style={{ borderBottom: "1px solid #2E3244", padding: "12px 28px", display: "flex", alignItems: "center", gap: "12px", flexShrink: 0 }}>
        <button onClick={async () => { await saveApp(); setStep("landing"); setFormData(INITIAL_FORM); setValidationResults({}); setOverallApiResult(null); setIarcInsights(null); syncUrl(null) }} style={{ background: "none", border: "none", color: "#6b7280", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", fontSize: "13px", padding: 0 }}>
          <ChevronLeft size={15} /> Back
        </button>
        <span style={{ color: "#2E3244" }}>·</span>
        <Shield size={15} color="#F6255A" />
        <span style={{ color: "#fff", fontWeight: 700, fontSize: "14px" }}>{formData.appName || "New App"}</span>

        <div style={{ marginLeft: "auto", display: "flex", gap: "8px" }}>
          <button onClick={saveApp} disabled={savingApp} style={{ background: "#21222D", border: "1px solid #2E3244", color: savingApp ? "#4b5563" : "#9ca3af", borderRadius: "8px", padding: "8px 16px", fontSize: "13px", fontWeight: 600, cursor: savingApp ? "not-allowed" : "pointer" }}>
            {savingApp ? "Saving…" : "Save draft"}
          </button>
          {appStatus !== "draft" && (
            <button onClick={exportApp} disabled={exportingApp} style={{ display: "flex", alignItems: "center", gap: "6px", background: "#21222D", border: "1px solid #2E3244", color: exportingApp ? "#4b5563" : "#9ca3af", borderRadius: "8px", padding: "8px 16px", fontSize: "13px", fontWeight: 600, cursor: exportingApp ? "not-allowed" : "pointer" }}>
              {exportingApp
                ? <><RotateCw size={13} style={{ animation: "spin 1s linear infinite" }} /> Exporting…</>
                : <><Download size={13} /> Export</>}
            </button>
          )}
          {appStatus === "draft" ? (
            <button
              onClick={() => canSubmit && submitAppForReview()}
              disabled={!canSubmit || submitting}
              title={!canSubmit ? "Validate all sections and score 60+/100 to submit" : ""}
              style={{ background: canSubmit ? "#F6255A" : "#2E3244", border: "none", color: canSubmit ? "#fff" : "#4b5563", borderRadius: "8px", padding: "8px 18px", fontSize: "13px", fontWeight: 700, cursor: canSubmit && !submitting ? "pointer" : "not-allowed", display: "flex", alignItems: "center", gap: "7px" }}
            >
              {submitting ? <><RotateCw size={13} style={{ animation: "spin 1s linear infinite" }} /> Submitting…</> : <><Send size={13} /> Submit for Review</>}
            </button>
          ) : (
            <button onClick={() => { trackEvent(ANALYTICS_EVENTS.FEATURE.STORE_VALIDATOR_PREVIEW_OPENED, { app_id: formData.appId }); window.open(previewUrl(formData.appId), "_blank", "noopener") }} style={{ background: "#21222D", border: "1px solid #2E3244", color: "#9ca3af", borderRadius: "8px", padding: "8px 16px", fontSize: "13px", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: "6px" }}>
              <Eye size={14} /> Preview
            </button>
          )}
        </div>
      </div>

      {/* ── AI generate from brief ── */}
      <div style={{ borderBottom: "1px solid #2E3244", padding: "14px 28px", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "7px", marginBottom: "8px" }}>
          <Sparkles size={13} color="#F6255A" />
          <span style={{ color: "#d1d5db", fontSize: "13px", fontWeight: 600 }}>Describe your app — AI fills in fields across every section</span>
        </div>
        <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
          <textarea
            value={aiBrief}
            onChange={e => setAiBrief(e.target.value)}
            placeholder="e.g. A cozy farming sim where you inherit a rundown farm, restore it, befriend the townsfolk, and uncover a mystery about the previous owner."
            rows={2}
            disabled={conversation.pending.length > 0 || hasGeneratedOnce}
            style={{ ...inputStyle, resize: "vertical", flex: 1, opacity: conversation.pending.length > 0 || hasGeneratedOnce ? 0.5 : 1 }}
            onFocus={e => e.target.style.borderColor = "#F6255A55"}
            onBlur={e => e.target.style.borderColor = "#2E3244"}
          />
          <button
            onClick={generateFromBrief}
            disabled={anyGenerationInFlight || !aiBrief.trim() || conversation.pending.length > 0 || hasGeneratedOnce}
            title={hasGeneratedOnce ? "Already generated once — use each section's own Regenerate button for further AI changes." : ""}
            style={{
              display: "flex", alignItems: "center", gap: "7px", whiteSpace: "nowrap",
              background: anyGenerationInFlight || !aiBrief.trim() || conversation.pending.length > 0 || hasGeneratedOnce ? "#2E3244" : "#F6255A",
              border: "none", color: anyGenerationInFlight || !aiBrief.trim() || conversation.pending.length > 0 || hasGeneratedOnce ? "#6b7280" : "#fff",
              borderRadius: "8px", padding: "0 18px", height: "40px", fontSize: "13px", fontWeight: 700,
              cursor: anyGenerationInFlight || !aiBrief.trim() || conversation.pending.length > 0 || hasGeneratedOnce ? "not-allowed" : "pointer", flexShrink: 0,
            }}
          >
            {generating ? <><RotateCw size={13} style={{ animation: "spin 1s linear infinite" }} /> Generating…</> : assetsGenerating ? <><RotateCw size={13} style={{ animation: "spin 1s linear infinite" }} /> Finishing assets…</> : <><Sparkles size={13} /> Generate</>}
          </button>
        </div>

        {/* A full run (~2-3 min, mostly billed image calls) has no real progress % from the
            backend, so this is an indeterminate bar — it just signals "still working", not
            how far along. Only for the initial text/spec generation (generating) — once that
            phase lands, the rest of the form is already usable and assetsGenerating finishing
            images in the background is already surfaced via the button's own "Finishing
            assets…" label, so a full-width bar here would overstate it as still blocking. */}
        {generating && (
          <div style={{ marginTop: "10px" }}>
            <div style={{ height: "4px", borderRadius: "2px", background: "#2E3244", overflow: "hidden" }}>
              <div style={{ height: "100%", width: "40%", borderRadius: "2px", background: "#F6255A", animation: "sv-progress-indeterminate 1.3s ease-in-out infinite" }} />
            </div>
            <p style={{ color: "#6b7280", fontSize: "11px", margin: "6px 0 0" }}>
              Generating your app with AI — this usually takes 2–3 minutes…
            </p>
          </div>
        )}

        {conversation.pending.length > 0 && (
          <div style={{ marginTop: "12px", display: "flex", flexDirection: "column", gap: "10px", background: "#21222D", border: "1px solid #2E3244", borderRadius: "8px", padding: "12px" }}>
            <span style={{ color: "#9ca3af", fontSize: "12px" }}>A couple quick questions before generating everything:</span>
            {conversation.pending.map(q => {
              const selected = conversation.answersDraft?.[q.id] ?? q.recommended ?? ""
              return (
                <div key={q.id}>
                  <label style={{ color: "#d1d5db", fontSize: "12px", display: "block", marginBottom: "6px" }}>{q.text}</label>
                  {q.options.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                      {q.options.map((opt, i) => (
                        <label
                          key={i}
                          style={{
                            display: "flex", gap: "9px", alignItems: "flex-start", cursor: "pointer",
                            border: `1px solid ${selected === opt ? "#F6255A55" : "#2E3244"}`,
                            background: selected === opt ? "#F6255A14" : "#171821",
                            borderRadius: "8px", padding: "9px 11px",
                          }}
                        >
                          <input
                            type="radio"
                            name={q.id}
                            checked={selected === opt}
                            onChange={() => setConversation({ answersDraft: { ...conversation.answersDraft, [q.id]: opt } })}
                            style={{ marginTop: "3px", flexShrink: 0, accentColor: "#F6255A" }}
                          />
                          <span style={{ color: "#d1d5db", fontSize: "12.5px", lineHeight: 1.5 }}>
                            {opt}{opt === q.recommended && <span style={{ color: "#F6255A", fontWeight: 600 }}> · Recommended</span>}
                          </span>
                        </label>
                      ))}
                      <input
                        type="text"
                        value={q.options.includes(selected) ? "" : selected}
                        onChange={e => setConversation({ answersDraft: { ...conversation.answersDraft, [q.id]: e.target.value } })}
                        placeholder="Or type your own answer…"
                        style={{ ...inputStyle, fontSize: "12.5px", padding: "9px 11px" }}
                        onFocus={e => e.target.style.borderColor = "#F6255A55"}
                        onBlur={e => e.target.style.borderColor = "#2E3244"}
                      />
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={selected}
                      onChange={e => setConversation({ answersDraft: { ...conversation.answersDraft, [q.id]: e.target.value } })}
                      style={inputStyle}
                      onFocus={e => e.target.style.borderColor = "#F6255A55"}
                      onBlur={e => e.target.style.borderColor = "#2E3244"}
                    />
                  )}
                </div>
              )
            })}
            <button
              onClick={answerFollowUps}
              disabled={anyGenerationInFlight}
              style={{
                alignSelf: "flex-start", display: "flex", alignItems: "center", gap: "7px",
                background: anyGenerationInFlight ? "#2E3244" : "#F6255A", border: "none", color: anyGenerationInFlight ? "#6b7280" : "#fff",
                borderRadius: "8px", padding: "8px 16px", fontSize: "13px", fontWeight: 700,
                cursor: anyGenerationInFlight ? "not-allowed" : "pointer",
              }}
            >
              {generating ? <><RotateCw size={13} style={{ animation: "spin 1s linear infinite" }} /> Generating…</> : "Continue"}
            </button>
          </div>
        )}
      </div>

      {/* ── Overall score tracker ── */}
      {(() => {
        // Editing a field already drops just that field's own score and recomputes its
        // section's average (setField) — correct, but averaging across every section dilutes
        // one dropped field twice over, so the Overall Score often rounds right back to the
        // same number and visibly looks unchanged. Make the staleness itself visible instead of
        // relying on the number always moving enough to notice.
        const anyFieldDirty = dirtyFields.size > 0
        const liveScore = overallScore()
        const displayColor = anyFieldDirty || liveScore === null ? "#4b5563" : getScoreColor(liveScore)
        return (
          <div style={{ borderBottom: "1px solid #2E3244", padding: "14px 28px", flexShrink: 0 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px", gap: "12px", flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "9px" }}>
                <span style={{ color: "#6b7280", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Overall Score
                </span>
                {anyFieldDirty ? (
                  <span style={{ fontSize: "11px", fontWeight: 700, color: "#6b7280", background: "#4b556318", padding: "2px 9px", borderRadius: "10px" }}>
                    Needs re-validation
                  </span>
                ) : liveScore !== null && (
                  <span style={{ fontSize: "11px", fontWeight: 700, color: displayColor, background: displayColor + "18", padding: "2px 9px", borderRadius: "10px" }}>
                    {getScoreLabel(liveScore)}
                  </span>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: "10px" }}>
                <span style={{ fontSize: "12px", color: "#4b5563" }}>
                  {Object.keys(validationResults).length}/{SECTIONS_CONFIG.length} sections validated
                </span>
                <span style={{ display: "flex", alignItems: "baseline", gap: "3px" }}>
                  <span style={{ fontSize: "19px", fontWeight: 800, lineHeight: 1, color: displayColor }}>
                    {liveScore != null ? `${liveScore}/100` : "—"}
                  </span>
                </span>
              </div>
            </div>
            <div style={{ height: "7px", background: "#21222D", borderRadius: "4px", overflow: "hidden" }}>
              <motion.div
                initial={false}
                animate={{ width: `${liveScore ?? 0}%` }}
                transition={{ duration: 0.45, ease: "easeOut" }}
                style={{ height: "100%", background: anyFieldDirty || liveScore === null ? "#2E3244" : displayColor, borderRadius: "4px" }}
              />
            </div>
          </div>
        )
      })()}

      {/* ── Section tabs ── */}
      <div style={{ borderBottom: "1px solid #2E3244", padding: "0 12px", display: "flex", alignItems: "center", gap: "4px", flexShrink: 0 }}>
        <button
          onClick={() => activeIdx > 0 && switchSection(SECTIONS_CONFIG[activeIdx - 1].id)}
          disabled={activeIdx === 0}
          title="Previous section"
          style={{ flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", width: "30px", height: "30px", borderRadius: "7px", background: activeIdx === 0 ? "#171821" : "#21222D", border: `1px solid ${activeIdx === 0 ? "#1e2030" : "#2E3244"}`, color: activeIdx === 0 ? "#2E3244" : "#e5e7eb", cursor: activeIdx === 0 ? "default" : "pointer" }}
        >
          <ChevronLeft size={16} />
        </button>
        <div style={{ display: "flex", gap: "0", overflowX: "auto", flex: 1, minWidth: 0 }}>
          {SECTIONS_CONFIG.map((s, i) => {
          const isActive   = activeSection === s.id
          const isComplete = isSectionComplete(s.id)
          const hasScore   = !!validationResults[s.id]
          const score      = hasScore ? validationResults[s.id].score : null
          return (
            <button
              key={s.id}
              onClick={() => switchSection(s.id)}
              style={{
                display: "flex", alignItems: "center", gap: "7px",
                padding: "13px 16px",
                background: "none", border: "none",
                borderBottom: `2px solid ${isActive ? "#F6255A" : "transparent"}`,
                color: isActive ? "#fff" : "#6b7280",
                fontSize: "13px", fontWeight: isActive ? 600 : 400,
                cursor: "pointer", whiteSpace: "nowrap",
                transition: "color 0.15s",
              }}
            >
              {s.id === "assets" && assetsGenerating
                ? <RotateCw size={14} color="#F6255A" style={{ animation: "spin 1s linear infinite" }} />
                : isComplete
                  ? <CheckCircle2 size={14} color={isActive ? "#22c55e" : "#22c55e88"} />
                  : <div style={{ width: "14px", height: "14px", borderRadius: "50%", border: `1.5px solid ${isActive ? "#F6255A" : "#4b5563"}`, flexShrink: 0 }} />}
              {s.label}
              {s.id === "assets" && assetsGenerating && (
                <span style={{ fontSize: "11px", fontWeight: 700, color: "#F6255A", background: "#F6255A18", padding: "1px 7px", borderRadius: "10px" }}>
                  Generating…
                </span>
              )}
              {!(s.id === "assets" && assetsGenerating) && score !== null && (
                <span style={{ fontSize: "11px", fontWeight: 700, color: getScoreColor(score), background: getScoreColor(score) + "18", padding: "1px 7px", borderRadius: "10px" }}>
                  {score}/100
                </span>
              )}
            </button>
          )
          })}
        </div>
        <button
          onClick={() => activeIdx < SECTIONS_CONFIG.length - 1 && switchSection(SECTIONS_CONFIG[activeIdx + 1].id)}
          disabled={activeIdx === SECTIONS_CONFIG.length - 1}
          title="Next section"
          style={{ flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", width: "30px", height: "30px", borderRadius: "7px", background: activeIdx === SECTIONS_CONFIG.length - 1 ? "#171821" : "#21222D", border: `1px solid ${activeIdx === SECTIONS_CONFIG.length - 1 ? "#1e2030" : "#2E3244"}`, color: activeIdx === SECTIONS_CONFIG.length - 1 ? "#2E3244" : "#e5e7eb", cursor: activeIdx === SECTIONS_CONFIG.length - 1 ? "default" : "pointer" }}
        >
          <ChevronRight size={16} />
        </button>
      </div>

      {/* ── Main content ── */}
      {openingApp ? (
        <div className="sv-main-columns" style={{ flex: 1, display: "flex" }}>
          <div className="sv-form-col" style={{ flex: 3, minWidth: 0, padding: "22px 24px 0", borderRight: "1px solid #2E3244" }}>
            {[0, 1, 2, 3].map(i => (
              <div key={i} style={{ background: "#21222D", border: "1px solid #2E3244", borderRadius: "10px", padding: "15px 18px", marginBottom: "14px" }}>
                <Skeleton width="130px" height="12px" style={{ marginBottom: "12px" }} />
                <Skeleton height="38px" radius="8px" />
              </div>
            ))}
          </div>
          <div className="sv-panel-col" style={{ flex: 2, minWidth: 0, padding: "20px" }}>
            <Skeleton width="100px" height="13px" style={{ marginBottom: "18px" }} />
            {[0, 1, 2].map(i => <Skeleton key={i} height="64px" radius="10px" style={{ marginBottom: "12px" }} />)}
          </div>
        </div>
      ) : showIarcQuestionnaire ? (
        <IarcCertificateTab
          value={formData.iarcResponses}
          onChange={setIarcResponses}
          onBack={() => setShowIarcQuestionnaire(false)}
          onGenerate={generateIarcQuestionnaire}
          generating={generatingIarc}
          generateDisabled={anyGenerationInFlight && !generatingIarc}
          onValidate={validateIarcQuestionnaire}
          validating={validatingIarc}
          // Only blocked by the IARC questionnaire's own Generate with AI (avoids racing a
          // fresh set of AI answers landing mid-validate) — not by unrelated generation
          // elsewhere in the app (e.g. assets still finishing in the background), matching
          // validateIarcQuestionnaire()'s own guard above.
          validateDisabled={generatingIarc}
          validateResult={validationResults.content_rating?.fields?.iarcQuestionnaire || null}
          onSave={saveApp}
          saving={savingApp}
          onAutoSave={saveIarcAnswers}
          insights={iarcInsights}
          onDownloadCertificate={downloadIarcCertificate}
          downloadingCertificate={downloadingCertificate}
          certificateError={certificateError}
        />
      ) : (
      <div className="sv-main-columns" style={{ flex: 1, display: "flex" }}>

        {/* ── Left: Form column ── */}
        <div className="sv-form-col" style={{ flex: 3, minWidth: 0, padding: "22px 24px 0", borderRight: "1px solid #2E3244" }}>

          {/* Section heading — Prev/Next now live up on the tab bar itself */}
          <div style={{ marginBottom: "18px" }}>
            <h2 style={{ color: "#fff", fontSize: "17px", fontWeight: 700, margin: "0 0 3px", display: "flex", alignItems: "center", gap: "8px" }}>
              <activeCfg.icon size={15} color="#F6255A" />
              {activeCfg.label}
            </h2>
            {activeCfg.description
              ? <p style={{ color: "#6b7280", fontSize: "12px", margin: 0, lineHeight: 1.5 }}>{activeCfg.description}</p>
              : <p style={{ color: "#4b5563", fontSize: "12px", margin: 0 }}>{activeIdx + 1} of {SECTIONS_CONFIG.length} sections</p>}
          </div>

          {activeSection === "assets" && assetsGenerating && (
            <div style={{ display: "flex", alignItems: "center", gap: "10px", background: "#F6255A0f", border: "1px solid #F6255A33", borderRadius: "8px", padding: "10px 14px", marginBottom: "18px" }}>
              <RotateCw size={14} color="#F6255A" style={{ animation: "spin 1s linear infinite", flexShrink: 0 }} />
              <span style={{ color: "#e5e7eb", fontSize: "12px" }}>
                Generating your assets — the rest of your listing is ready to review while this finishes (usually a couple of minutes).
              </span>
            </div>
          )}

          {/* Fields */}
          <div style={{ display: "flex", flexDirection: "column", gap: "10px", paddingBottom: "28px" }}>
            {activeCfg.fields.map(field => {
              if (field.type === "heading") return (
                <div key={field.id} style={{ marginTop: "4px", paddingBottom: "5px", borderBottom: "1px solid #2E3244" }}>
                  <h3 style={{ color: "#d1d5db", fontSize: "11px", fontWeight: 700, margin: "0 0 3px", textTransform: "uppercase", letterSpacing: "0.07em" }}>{field.title}</h3>
                  {field.description && <p style={{ color: "#4b5563", fontSize: "12px", margin: 0, lineHeight: 1.5 }}>{field.description}</p>}
                </div>
              )
              if (field.type === "note") return (
                <div key={field.id} style={{ display: "flex", gap: "9px", alignItems: "flex-start", background: "#f59e0b14", border: "1px solid #f59e0b40", borderRadius: "8px", padding: "10px 12px" }}>
                  <AlertTriangle size={15} color="#f59e0b" style={{ flexShrink: 0, marginTop: "1px" }} />
                  <p style={{ color: "#e5c07b", fontSize: "12px", margin: 0, lineHeight: 1.5 }}>{field.text}</p>
                </div>
              )
              // Fields nested under another one are rendered by their parent's renderFieldCard
              // call instead, right below it — not here at their position in the flat array.
              if (field.nestedUnder) return null
              return renderFieldCard(field, 0)
            })}
            {(GENERATE_GROUPS.includes(activeSection) || activeSection === "assets") && !!sectionResult && (
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "4px" }}>
                <span style={{ color: "#6b7280", fontSize: "12px" }}>Didn't like this version?</span>
                <button
                  onClick={() => regenerateSection(activeSection)}
                  disabled={anyGenerationInFlight}
                  style={{
                    display: "flex", alignItems: "center", gap: "5px",
                    background: anyGenerationInFlight ? "#2E3244" : "#F6255A", border: "none",
                    color: anyGenerationInFlight ? "#6b7280" : "#fff",
                    borderRadius: "7px", padding: "6px 12px", fontSize: "11px", fontWeight: 700,
                    cursor: anyGenerationInFlight ? "not-allowed" : "pointer",
                  }}
                >
                  {regeneratingSection === activeSection
                    ? <><RotateCw size={11} style={{ animation: "spin 1s linear infinite" }} /> Regenerating…</>
                    : <><Sparkles size={11} /> Regenerate this section</>}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── Right: AI Validation panel ── */}
        <div className="sv-panel-col" style={{ flex: 2, minWidth: 0, display: "flex", flexDirection: "column" }}>

          {/* Panel header */}
          <div style={{ padding: "14px 20px", borderBottom: "1px solid #2E3244", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
              <Sparkles size={13} color="#F6255A" />
              <span style={{ color: "#d1d5db", fontSize: "13px", fontWeight: 600 }}>AI Validation</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              {sectionResult && (
                <>
                  <div style={{ height: "5px", width: "80px", background: "#2E3244", borderRadius: "3px" }}>
                    <motion.div initial={{ width: 0 }} animate={{ width: `${sectionResult.score}%` }} transition={{ duration: 0.8, ease: "easeOut" }}
                      style={{ height: "100%", background: getScoreColor(sectionResult.score), borderRadius: "3px" }} />
                  </div>
                  <span style={{ fontSize: "16px", fontWeight: 800, color: getScoreColor(sectionResult.score), lineHeight: 1 }}>{sectionResult.score}/100</span>
                </>
              )}
              {(() => {
                // This button now validates only the ACTIVE section (POST /validate/group/
                // <activeSection>/), not the whole app — so its own "does this need a check"
                // state is derived from just this section: never validated yet, or a field in
                // it is dirty. Once it already has a baseline AND a specific field is dirty,
                // defer to that field's own (finer-grained) Re-validate button below instead.
                const sectionHasDirtyField = activeCfg.fields.some(f => dirtyFields.has(f.id))
                const preferScopedRevalidate = sectionHasDirtyField && !!sectionResult
                const sectionNeedsValidation = !sectionResult || sectionHasDirtyField
                // isSectionComplete() already exists (used for the sidebar's own checkmarks) —
                // was never checked here, so Validate stayed enabled/highlighted even with a
                // required field (e.g. App Name) still empty, in every section.
                const sectionIncomplete = !isSectionComplete(activeSection)
                const validateDisabled = validating || !sectionNeedsValidation || preferScopedRevalidate || sectionIncomplete
                return (
                  <button
                    onClick={validate}
                    disabled={validateDisabled}
                    title={
                      sectionIncomplete ? "Fill in the required fields in this section first"
                        : preferScopedRevalidate ? "Re-validate just the field(s) you changed below instead"
                        : !validating && !sectionNeedsValidation ? "Already validated — edit a field to re-enable"
                        : ""
                    }
                    style={{ display: "flex", alignItems: "center", gap: "6px", background: validateDisabled ? "#2E3244" : "#F6255A", border: "none", color: validateDisabled ? "#6b7280" : "#fff", borderRadius: "7px", padding: "7px 14px", fontSize: "12px", fontWeight: 700, cursor: validateDisabled ? "not-allowed" : "pointer" }}
                  >
                    {validating
                      ? <><RotateCw size={12} style={{ animation: "spin 1s linear infinite" }} /> Analyzing…</>
                      : !sectionNeedsValidation
                        ? <><CheckCircle2 size={12} /> Up to date</>
                        : <><Sparkles size={12} /> Validate</>}
                  </button>
                )
              })()}
            </div>
          </div>

          {validateError && (
            <div style={{ margin: "12px 20px 0", padding: "10px 12px", background: "#f9731618", border: "1px solid #f9731640", borderRadius: "8px", color: "#f97316", fontSize: "12px", lineHeight: 1.6 }}>
              {validateError}
            </div>
          )}

          {/* Empty state */}
          {!sectionResult && !validating && (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "24px", textAlign: "center" }}>
              <div style={{ width: "46px", height: "46px", borderRadius: "14px", background: "#21222D", border: "1px solid #2E3244", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "10px" }}>
                <Sparkles size={19} color="#4b5563" />
              </div>
              <p style={{ color: "#4b5563", fontSize: "12px", margin: 0, lineHeight: 1.6 }}>
                Fill in the fields and click <strong style={{ color: "#6b7280" }}>Validate</strong> to get per-field AI feedback and improvement suggestions.
              </p>
            </div>
          )}

          {/* Loading */}
          {validating && (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
              <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.2, repeat: Infinity, ease: "linear" }} style={{ marginBottom: "12px" }}>
                <Sparkles size={24} color="#F6255A" />
              </motion.div>
              <p style={{ color: "#6b7280", fontSize: "13px", margin: 0 }}>Analyzing {activeCfg.label.toLowerCase()}…</p>
            </div>
          )}

          {/* Per-field results */}
          {sectionResult && !validating && (
            <AnimatePresence>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ padding: "12px 14px 20px" }}>
                {activeCfg.fields.filter(f => f.type !== "heading" && f.type !== "note").map(field => {
                  const fieldRes      = sectionResult.fields?.[field.id]
                  const key           = `${activeSection}.${field.id}`
                  const hasSuggestion = fieldSuggestions[key] !== undefined
                  const isFullScore   = fieldRes?.score === 100
                  const suggestionRevealed = !isFullScore || revealedSuggestions.has(key)
                  const isRevalidating= validatingFields.has(field.id)
                  // Once a field has a real score, only let it be re-validated again after the
                  // user actually changes it — protects every other still-accurate field's
                  // result from being casually re-run (and re-billed) for no reason.
                  const canRevalidateField = !fieldRes || dirtyFields.has(field.id)
                  const accentCol     = !fieldRes ? "#4b5563"
                    : fieldRes.status === "pass" ? "#22c55e"
                    : fieldRes.status === "warning" ? "#f59e0b"
                    : "#ef4444"
                  const StatusIcon    = !fieldRes || isRevalidating ? RotateCw
                    : fieldRes.status === "pass" ? CheckCircle2
                    : fieldRes.status === "warning" ? AlertTriangle
                    : XCircle
                  // "markdown" (Long Description) was missing here, so it fell through to the
                  // generic "Apply ↑" while every other free-text field says "Update ↑" — both
                  // mean the same thing (replace the field's current text with the suggestion).
                  const applyLabel    = field.type === "tags" ? "Add to field ↑"
                    : field.type === "text" || field.type === "textarea" || field.type === "markdown" || field.type === "url" ? "Update ↑"
                    : "Apply ↑"

                  return (
                    <div key={field.id} style={{ marginBottom: "10px", background: "#21222D", border: `1px solid ${accentCol}28`, borderRadius: "10px", padding: "13px 14px" }}>

                      {/* Header: label + score */}
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "7px" }}>
                        <span style={{ color: "#e5e7eb", fontSize: "12px", fontWeight: 600 }}>{field.label}</span>
                        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                          <StatusIcon size={12} color={isRevalidating ? "#6b7280" : accentCol}
                            style={isRevalidating ? { animation: "spin 1s linear infinite" } : undefined} />
                          {fieldRes && <span style={{ fontSize: "12px", fontWeight: 700, color: isRevalidating ? "#6b7280" : accentCol }}>{fieldRes.score}/100</span>}
                        </div>
                      </div>

                      {/* Feedback */}
                      {fieldRes && (
                        <p style={{ color: "#6b7280", fontSize: "11px", lineHeight: 1.6, margin: "0 0 10px", whiteSpace: "pre-line" }}>{fieldRes.feedback}</p>
                      )}
                      {!fieldRes && !isRevalidating && (
                        <p style={{ color: "#4b5563", fontSize: "11px", margin: "0 0 10px" }}>Click Re-validate to get AI feedback for this field.</p>
                      )}

                      {/* Suggestion widget — hidden behind a "good to go" message at a full score,
                          until the user explicitly asks to see it anyway */}
                      {hasSuggestion && isFullScore && !suggestionRevealed && (
                        <div style={{ borderTop: "1px solid #2E3244", paddingTop: "10px", marginBottom: "10px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
                          <span style={{ display: "flex", alignItems: "center", gap: "6px", color: "#22c55e", fontSize: "12px", fontWeight: 600 }}>
                            <CheckCircle2 size={13} /> Full score — you're good to go!
                          </span>
                          <button
                            type="button"
                            onClick={() => setRevealedSuggestions(p => new Set([...p, key]))}
                            style={{ background: "none", border: "none", color: "#6b7280", fontSize: "11px", cursor: "pointer", padding: 0, textDecoration: "underline", flexShrink: 0 }}
                          >
                            Show suggestion anyway
                          </button>
                        </div>
                      )}
                      {hasSuggestion && suggestionRevealed && (
                        <div style={{ borderTop: "1px solid #2E3244", paddingTop: "10px", marginBottom: "10px" }}>
                          <p style={{ color: "#4b5563", fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", margin: "0 0 7px" }}>
                            {field.type === "tags" ? "Suggested keywords — tap to add or remove" : "Suggested"}
                          </p>
                          {renderSuggestionField(field, key)}
                        </div>
                      )}

                      {/* Action buttons */}
                      <div style={{ display: "flex", gap: "6px" }}>
                        <button
                          onClick={() => validateField(activeSection, field)}
                          disabled={isRevalidating || !canRevalidateField}
                          title={!canRevalidateField ? "Already validated — edit this field to re-enable" : ""}
                          style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "5px", background: "none", border: "1px solid #2E3244", color: isRevalidating || !canRevalidateField ? "#4b5563" : "#6b7280", borderRadius: "6px", padding: "6px 0", fontSize: "11px", fontWeight: 600, cursor: isRevalidating || !canRevalidateField ? "not-allowed" : "pointer" }}
                          onMouseEnter={e => { if (!isRevalidating && canRevalidateField) e.currentTarget.style.borderColor = "#4b5563" }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = "#2E3244" }}
                        >
                          <RotateCw size={11} style={isRevalidating ? { animation: "spin 1s linear infinite" } : undefined} />
                          {isRevalidating ? "Analyzing…" : "Re-validate"}
                        </button>
                        {hasSuggestion && suggestionRevealed && field.type !== "tags" && (
                          <button
                            onClick={() => applyFieldSuggestion(field, key)}
                            style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "5px", background: "#F6255A12", border: "1px solid #F6255A44", color: "#F6255A", borderRadius: "6px", padding: "6px 0", fontSize: "11px", fontWeight: 700, cursor: "pointer" }}
                            onMouseEnter={e => e.currentTarget.style.background = "#F6255A22"}
                            onMouseLeave={e => e.currentTarget.style.background = "#F6255A12"}
                          >
                            {applyLabel}
                          </button>
                        )}
                      </div>
                    </div>
                  )
                })}
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </div>
      )}

      <style>{`
        @keyframes spin { from { transform:rotate(0deg) } to { transform:rotate(360deg) } }
        input::placeholder, textarea::placeholder { color: #6b7280; opacity: 1; }
      `}</style>
    </div>
  )
}

export default StoreValidator
